import {
  ref,
  get,
  set,
  update,
  remove,
  query,
  orderByChild,
  equalTo,
  onValue,
  off,
} from 'firebase/database';
import { database } from '../config/firebase';
import {
  FirebaseUserData,
  UserProfile,
  SiteData,
  SiteMember,
  SiteSettings,
  SitePage,
  PageSection,
  MediaAsset,
  BlogPost,
  YachtItem,
  YachtReservation,
  YachtRoute,
  YachtMarina,
  SiteCommunication,
  CommunicationStatus,
  CommunicationSource,
  SiteDomain,
  SiteSubscription,
  SiteAnalytics,
  TemplateDefinition,
  IndustryDefinition,
  IndustryType,
  SubscriptionPlanId,
  ShopierTransaction,
  BillingOrder,
  OrderStatus,
  PublishedSiteSnapshot,
  SiteVersion,
  PaymentRecord,
  AuditLog,
  PlanQuotaConfig,
  SiteSEO,
  DomainMapping,
  BackgroundJob,
  AISitePlan,
  OfficialPortfolioProject,
  YachtAvailability,
  SectorQuotaInfo,
  SectorQuotas,
  UserSubscriptionRecord,
  UserTrialInfo,
  GoogleAdsManagementSubscription,
  GoogleAdsManagementRequest,
  GoogleAdsConfig,
  GoogleAdsAccount,
  GoogleAdsConnection,
  GoogleAdsManagementStatus,
  GoogleAdsAccessStatus,
  GoogleAdsConnectionStatus,
  AIVisibilityProfile,
  AIVisibilitySubscription,
  AIVisibilityTask,
  AIVisibilityHealthReport,
  AILocalVisibilityEntity,
  AIVisibilityAnalysisRun,
  ExternalLicense,
  ExternalLicenseStatus,
} from '../types';
import { QuotaService, EntitlementDebugReport } from './quotaService';
import { normalizeQuota } from './quotaNormalization';
import { SiteStructureService } from './siteStructureService';
import { INITIAL_OFFICIAL_PORTFOLIO } from '../data/officialPortfolioData';
import { TEMPLATES } from '../config/templates';
import {
  SUBSCRIPTION_PLANS,
} from '../config/constants';
import {
  normalizeDomain,
  sanitizeSlug,
  sanitizeUrl,
  escapeHtml,
  checkSubmissionRateLimit,
} from '../utils/security';
import { addMonthsSafely } from '../utils/dateUtils';

// Local storage prefix for offline simulation and fallback
const LOCAL_STORAGE_PREFIX = 'adiosoft_rtdb_v3_';

export class RealtimeDatabaseService {
  public isRtdbAvailable(): boolean {
    return Boolean(database);
  }

  private memoryStore: Record<string, string> = {};

  private getLocal<T>(key: string, defaultValue: T): T {
    try {
      if (typeof localStorage !== 'undefined') {
        const raw = localStorage.getItem(LOCAL_STORAGE_PREFIX + key);
        return raw ? JSON.parse(raw) : defaultValue;
      }
      const raw = this.memoryStore[LOCAL_STORAGE_PREFIX + key];
      return raw ? JSON.parse(raw) : defaultValue;
    } catch {
      return defaultValue;
    }
  }

  private setLocal<T>(key: string, value: T): void {
    try {
      const serialized = JSON.stringify(value);
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(LOCAL_STORAGE_PREFIX + key, serialized);
      } else {
        this.memoryStore[LOCAL_STORAGE_PREFIX + key] = serialized;
      }
    } catch (err) {
      console.warn('Storage write warning:', err);
    }
  }

  /**
   * Sanitizes payload for Firebase Realtime Database.
   * Firebase RTDB strictly rejects any object containing properties with `undefined` values.
   */
  public cleanForRtdb<T>(data: T): T {
    if (data === null || data === undefined) return data;
    return JSON.parse(JSON.stringify(data));
  }

  // Mutex lock to prevent duplicate creation race conditions
  private creationLocks: Set<string> = new Set();

  // Helper for generating unique keys
  public generateId(prefix: string): string {
    return `${prefix}_${Math.random().toString(36).substring(2, 9)}_${Date.now().toString(36)}`;
  }

  // UTC Timestamp generator
  public getUtcTimestamp(): number {
    return Date.now();
  }

  // ==========================================
  // User Subscriptions & Sector Quotas
  // ==========================================
  public async getUserSubscriptions(userId: string): Promise<UserSubscriptionRecord[]> {
    if (this.isRtdbAvailable() && database) {
      try {
        const snap = await get(ref(database, `userSubscriptions/${userId}`));
        if (snap.exists()) {
          const val = snap.val();
          return Object.values(val);
        }
      } catch (err) {
        console.warn('RTDB getUserSubscriptions fallback:', err);
      }
    }
    const allUserSubs = this.getLocal<Record<string, Record<string, UserSubscriptionRecord>>>('userSubscriptions', {});
    return Object.values(allUserSubs[userId] || {});
  }

  public async saveUserSubscription(sub: UserSubscriptionRecord): Promise<void> {
    const payload: UserSubscriptionRecord = {
      ...sub,
      updatedAt: this.getUtcTimestamp(),
    };
    if (this.isRtdbAvailable() && database) {
      try {
        await set(ref(database, `userSubscriptions/${sub.userId}/${sub.id}`), this.cleanForRtdb(payload));
      } catch (err) {
        console.warn('RTDB saveUserSubscription fallback:', err);
      }
    }
    const allUserSubs = this.getLocal<Record<string, Record<string, UserSubscriptionRecord>>>('userSubscriptions', {});
    if (!allUserSubs[sub.userId]) allUserSubs[sub.userId] = {};
    allUserSubs[sub.userId][sub.id] = payload;
    this.setLocal('userSubscriptions', allUserSubs);
  }

  /**
   * Helper to determine if a product or sector belongs to website subscriptions
   * (Corporate or Yacht Rental, excluding Google Ads and AI Visibility)
   */
  public isWebsiteSubscriptionProduct(planIdOrSector?: string): boolean {
    if (!planIdOrSector) return false;
    const p = String(planIdOrSector).toUpperCase();
    if (p.includes('GOOGLE_ADS') || p.includes('AI_') || p.includes('VISIBILITY')) {
      return false;
    }
    return (
      p.includes('CORP') ||
      p.includes('YACHT') ||
      p === 'CORPORATE_WEBSITE' ||
      p === 'YACHT_RENTAL_WEBSITE' ||
      p === 'CORPORATE' ||
      p === 'YACHT_RENTAL'
    );
  }

  /**
   * Finds the currently active website subscription for a user/tenant.
   * Single License Architecture Rule: Account level MAX 1 ACTIVE WEBSITE SUBSCRIPTION.
   * Returns the active UserSubscriptionRecord if one exists and is not expired, or null.
   */
  public async findActiveWebsiteSubscription(
    userIdOrTenantId: string,
    now: number = this.getUtcTimestamp()
  ): Promise<UserSubscriptionRecord | null> {
    if (!userIdOrTenantId || userIdOrTenantId === 'guest_user') return null;

    const userSubs = await this.getUserSubscriptions(userIdOrTenantId);

    // Filter active website subscriptions
    const activeWebsiteSubs = userSubs.filter((sub) => {
      if (sub.status !== 'ACTIVE') return false;
      const expire = sub.expireDate || sub.endDate;
      if (expire && expire <= now) return false;
      return (
        this.isWebsiteSubscriptionProduct(sub.planId) ||
        this.isWebsiteSubscriptionProduct(sub.sector)
      );
    });

    if (activeWebsiteSubs.length > 0) {
      return activeWebsiteSubs[0];
    }

    // Also inspect active sites for site.subscription if user has no userSubscriptions record
    const sites = await this.getSitesByUser(userIdOrTenantId);
    for (const site of sites) {
      if (!site.isDeleted && site.status !== 'DELETED' && site.subscription) {
        const sub = site.subscription;
        if (sub.status === 'ACTIVE' && (!sub.expireDate || sub.expireDate > now)) {
          if (
            this.isWebsiteSubscriptionProduct(sub.planId) ||
            this.isWebsiteSubscriptionProduct(site.industry)
          ) {
            return {
              id: sub.lastPaymentId || `site_sub_${site.id}`,
              userId: site.ownerId || userIdOrTenantId,
              planId: sub.planId,
              planName: sub.planName,
              sector: site.industry === 'YACHT_RENTAL' ? 'YACHT_RENTAL' : 'CORPORATE',
              websiteLimit: 1,
              status: 'ACTIVE',
              startDate: sub.startDate || now,
              expireDate: sub.expireDate,
              endDate: sub.expireDate,
              isWebsiteSubscription: true,
              createdAt: sub.startDate || now,
              updatedAt: now,
            };
          }
        }
      }
    }

    return null;
  }

  // ==========================================
  // Trial Entitlement Management (/userTrials/{userId})
  // ==========================================
  public async getUserTrial(userId: string): Promise<UserTrialInfo | null> {
    if (!userId || userId === 'guest_user') return null;
    if (this.isRtdbAvailable() && database) {
      try {
        const trialRef = ref(database, `userTrials/${userId}`);
        const snapshot = await get(trialRef);
        if (snapshot.exists()) {
          return snapshot.val() as UserTrialInfo;
        }
      } catch (err) {
        console.warn('RTDB getUserTrial fallback:', err);
      }
    }
    const allTrials = this.getLocal<Record<string, UserTrialInfo>>('userTrials', {});
    if (allTrials[userId]) {
      return allTrials[userId];
    }

    // Check user record for trial fields
    const user = await this.getUser(userId);
    if (user?.trialStartDate && user?.trialEndDate) {
      const trialInfo: UserTrialInfo = {
        trialStartDate: user.trialStartDate,
        trialEndDate: user.trialEndDate,
        trialStatus: user.trialStatus || (this.getUtcTimestamp() >= user.trialEndDate ? 'EXPIRED' : 'ACTIVE'),
        websiteLimit: 1,
        sector: user.trialSector || null,
        used: user.trialSiteId ? 1 : 0,
        siteId: user.trialSiteId,
        createdAt: user.createdAt || this.getUtcTimestamp(),
        updatedAt: user.updatedAt || this.getUtcTimestamp(),
      };
      await this.saveUserTrial(userId, trialInfo);
      return trialInfo;
    }

    // If new user and not expired demo, initialize 72-hour trial record
    if (userId && !userId.startsWith('usr_demo_expired')) {
      const now = this.getUtcTimestamp();
      const trialInfo: UserTrialInfo = {
        trialStartDate: now,
        trialEndDate: now + 3 * 24 * 60 * 60 * 1000,
        trialStatus: 'ACTIVE',
        websiteLimit: 1,
        sector: null,
        used: 0,
        createdAt: now,
        updatedAt: now,
      };
      await this.saveUserTrial(userId, trialInfo);
      return trialInfo;
    }

    return null;
  }

  public async saveUserTrial(userId: string, trial: UserTrialInfo): Promise<void> {
    if (!userId || userId === 'guest_user') return;
    const payload: UserTrialInfo = {
      ...trial,
      updatedAt: this.getUtcTimestamp(),
    };
    if (this.isRtdbAvailable() && database) {
      try {
        await set(ref(database, `userTrials/${userId}`), this.cleanForRtdb(payload));
      } catch (err) {
        console.warn('RTDB saveUserTrial fallback:', err);
      }
    }
    const allTrials = this.getLocal<Record<string, UserTrialInfo>>('userTrials', {});
    allTrials[userId] = payload;
    this.setLocal('userTrials', allTrials);
  }

  public async updateUserTrial(userId: string, updates: Partial<UserTrialInfo>): Promise<UserTrialInfo | null> {
    const existing = await this.getUserTrial(userId);
    const updated: UserTrialInfo = {
      ...(existing || {
        userId,
        trialStatus: 'ACTIVE',
        websiteLimit: 1,
        used: 0,
        trialStartDate: this.getUtcTimestamp(),
        trialEndDate: this.getUtcTimestamp() + 14 * 86400000,
        createdAt: this.getUtcTimestamp(),
      }),
      ...updates,
      updatedAt: this.getUtcTimestamp(),
    };
    await this.saveUserTrial(userId, updated);

    if (this.isRtdbAvailable() && database) {
      try {
        await update(ref(database, `users/${userId}/trial`), {
          ...updates,
          updatedAt: this.getUtcTimestamp(),
        });
      } catch (err) {
        console.warn('RTDB updateUserTrial warning:', err);
      }
    }
    const allUsers = this.getLocal<Record<string, UserProfile>>('users', {});
    if (allUsers[userId]) {
      allUsers[userId].trial = {
        ...(allUsers[userId].trial as any),
        ...updates,
        updatedAt: this.getUtcTimestamp(),
      };
      this.setLocal('users', allUsers);
    }

    return updated;
  }

  /**
   * Calculates isolated sector quotas based on active database user subscriptions/entitlements, trials and real sites.
   * Uses central QuotaService with strict normalization (999 is 999, 2 is 2, 1 is 1, 0 is 0).
   * Hardcoded limit overrides are completely removed.
   */
  public async getSectorQuotas(userId: string, isSuperAdmin: boolean = false): Promise<SectorQuotas> {
    const existingSites = await this.getSitesByUser(userId);
    const userSubs = await this.getUserSubscriptions(userId);
    const userTrial = await this.getUserTrial(userId);

    const { quotas } = QuotaService.calculateSectorQuotas({
      userId,
      isSuperAdmin: false, // Quotas are strictly for customer websites, never for admin app
      userSubscriptions: userSubs,
      existingSites,
      currentTime: this.getUtcTimestamp(),
      userTrial,
    });

    return quotas;
  }

  /**
   * Retrieves both sector quotas and detailed entitlement debug report for admin inspection.
   */
  public async getSectorQuotasWithDebug(
    userId: string,
    isSuperAdmin: boolean = false
  ): Promise<{ quotas: SectorQuotas; debugReport: EntitlementDebugReport }> {
    const existingSites = await this.getSitesByUser(userId);
    const userSubs = await this.getUserSubscriptions(userId);
    const userTrial = await this.getUserTrial(userId);
    return QuotaService.calculateSectorQuotas({
      userId,
      isSuperAdmin,
      userSubscriptions: userSubs,
      existingSites,
      currentTime: this.getUtcTimestamp(),
      userTrial,
    });
  }

  // ==========================================
  // 1. /users/{uid}
  // ==========================================
  public async getUser(uid: string): Promise<UserProfile | null> {
    if (this.isRtdbAvailable() && database) {
      try {
        const userRef = ref(database, `users/${uid}`);
        const snapshot = await get(userRef);
        if (snapshot.exists()) {
          return snapshot.val() as UserProfile;
        }
      } catch (err) {
        console.warn('RTDB getUser fallback:', err);
      }
    }
    const allUsers = this.getLocal<Record<string, UserProfile>>('users', {});
    return allUsers[uid] || null;
  }

  public async saveUser(user: UserProfile): Promise<UserProfile> {
    const now = this.getUtcTimestamp();
    const payload: UserProfile = {
      ...user,
      updatedAt: now,
      createdAt: user.createdAt || now,
    };

    if (this.isRtdbAvailable() && database) {
      try {
        const userRef = ref(database, `users/${user.uid}`);
        await set(userRef, payload);
      } catch (err) {
        console.warn('RTDB saveUser fallback:', err);
      }
    }
    const allUsers = this.getLocal<Record<string, UserProfile>>('users', {});
    allUsers[user.uid] = payload;
    this.setLocal('users', allUsers);
    return payload;
  }

  public async getAllUsers(): Promise<UserProfile[]> {
    if (this.isRtdbAvailable() && database) {
      try {
        const usersRef = ref(database, 'users');
        const snapshot = await get(usersRef);
        if (snapshot.exists()) {
          return Object.values(snapshot.val());
        }
      } catch (err) {
        console.warn('RTDB getAllUsers fallback:', err);
      }
    }
    const allUsers = this.getLocal<Record<string, UserProfile>>('users', {});
    return Object.values(allUsers);
  }

  // ==========================================
  // 2. /sites/{siteId}
  // ==========================================
  public async getSite(siteId: string): Promise<SiteData | null> {
    if (this.isRtdbAvailable() && database) {
      try {
        const siteRef = ref(database, `sites/${siteId}`);
        const snapshot = await get(siteRef);
        if (snapshot.exists()) {
          const raw = snapshot.val() as SiteData;
          if (raw.isDeleted) return null;

          // Fetch nested sub-branches
          const [settings, pages, subscription, domains] = await Promise.all([
            this.getSiteSettings(siteId),
            this.getSitePages(siteId),
            this.getSiteSubscription(siteId),
            this.getSiteDomains(siteId),
          ]);

          const localSites = this.getLocal<Record<string, SiteData>>('sites', {});
          const localSite = localSites[siteId];

          let resolvedPages = pages && pages.length > 0
            ? pages
            : (raw.pages && raw.pages.length > 0
              ? raw.pages
              : (localSite?.pages && localSite.pages.length > 0
                ? localSite.pages
                : []));

          if (resolvedPages.length === 0) {
            const structure = SiteStructureService.buildStructure({
              siteId,
              siteName: raw.name || 'Adiosoft',
              slug: raw.slug || 'site',
              industry: raw.industry || 'CORPORATE',
              colorPalette: {
                primaryColor: settings?.primaryColor || raw.settings?.primaryColor,
                secondaryColor: settings?.secondaryColor || raw.settings?.secondaryColor,
                accentColor: settings?.accentColor || raw.settings?.accentColor,
              },
              typography: {
                fontFamily: settings?.fontFamily || raw.settings?.fontFamily,
                headingFont: settings?.headingFont || raw.settings?.headingFont,
              },
              contactInfo: {
                phone: settings?.phone || raw.settings?.phone,
                email: settings?.email || raw.settings?.email,
                address: settings?.address || raw.settings?.address,
              },
            });
            resolvedPages = structure.pages;
            this.saveSitePages(siteId, resolvedPages).catch(console.warn);
            if (settings && (!settings.headerMenu || settings.headerMenu.length === 0)) {
              settings.headerMenu = structure.headerMenu;
              this.saveSiteSettings(siteId, settings).catch(console.warn);
            }
          }

          return {
            ...raw,
            id: raw.id || siteId,
            settings: settings || raw.settings || localSite?.settings,
            pages: resolvedPages,
            subscription: subscription || raw.subscription || localSite?.subscription,
            domains: domains.length > 0 ? domains : (raw.domains || localSite?.domains || []),
          };
        }
      } catch (err) {
        console.warn('RTDB getSite fallback:', err);
      }
    }
    const allSites = this.getLocal<Record<string, SiteData>>('sites', {});
    const s = allSites[siteId];
    if (!s || s.isDeleted) return null;
    if (!s.pages || s.pages.length === 0) {
      const structure = SiteStructureService.buildStructure({
        siteId,
        siteName: s.name || 'Adiosoft',
        slug: s.slug || 'site',
        industry: s.industry || 'CORPORATE',
        colorPalette: {
          primaryColor: s.settings?.primaryColor,
          secondaryColor: s.settings?.secondaryColor,
          accentColor: s.settings?.accentColor,
        },
        typography: {
          fontFamily: s.settings?.fontFamily,
          headingFont: s.settings?.headingFont,
        },
        contactInfo: {
          phone: s.settings?.phone,
          email: s.settings?.email,
          address: s.settings?.address,
        },
      });
      s.pages = structure.pages;
      if (!s.settings?.headerMenu || s.settings.headerMenu.length === 0) {
        s.settings = { ...(s.settings || {}), headerMenu: structure.headerMenu } as any;
      }
      allSites[siteId] = s;
      this.setLocal('sites', allSites);
    }
    return s;
  }

  public async getSiteById(siteId: string): Promise<SiteData | null> {
    return this.getSite(siteId);
  }

  public async getAllSites(): Promise<SiteData[]> {
    if (this.isRtdbAvailable() && database) {
      try {
        const allSitesRef = ref(database, 'sites');
        const snapshot = await get(allSitesRef);
        if (snapshot.exists()) {
          const list: SiteData[] = [];
          const rawVal = snapshot.val();
          for (const sId of Object.keys(rawVal)) {
            const item = rawVal[sId] as SiteData;
            if (!item || item.isDeleted) continue;
            list.push({
              ...item,
              id: item.id || sId,
            });
          }
          return list;
        }
      } catch (err) {
        console.warn('RTDB getAllSites fallback:', err);
      }
    }
    const allSites = this.getLocal<Record<string, SiteData>>('sites', {});
    return Object.values(allSites).filter((s) => !s.isDeleted);
  }

  public async getSitesByUser(userId: string): Promise<SiteData[]> {
    if (!userId || typeof userId !== 'string' || !userId.trim()) {
      return [];
    }
    const cleanUserId = userId.trim();

    if (this.isRtdbAvailable() && database) {
      try {
        let snapshot;
        try {
          const userSitesQuery = query(ref(database, 'sites'), orderByChild('ownerId'), equalTo(cleanUserId));
          snapshot = await get(userSitesQuery);
        } catch {
          // If .indexOn is missing in RTDB rules, safely fetch sites and filter strictly by ownerId in memory
          const allSitesRef = ref(database, 'sites');
          snapshot = await get(allSitesRef);
        }

        if (snapshot.exists()) {
          const list: SiteData[] = [];
          const rawVal = snapshot.val();
          const localSites = this.getLocal<Record<string, SiteData>>('sites', {});
          for (const sId of Object.keys(rawVal)) {
            const item = rawVal[sId] as SiteData;
            if (!item || item.isDeleted) continue;
            // STRICT TENANT ISOLATION: Must match target userId exactly
            if (item.ownerId !== cleanUserId) continue;
            const [settings, pages, subscription, domains] = await Promise.all([
                this.getSiteSettings(sId),
                this.getSitePages(sId),
                this.getSiteSubscription(sId),
                this.getSiteDomains(sId),
              ]);
              const localSite = localSites[sId];
              let resolvedPages = pages && pages.length > 0
                ? pages
                : (item.pages && item.pages.length > 0
                  ? item.pages
                  : (localSite?.pages && localSite.pages.length > 0
                    ? localSite.pages
                    : []));

              if (resolvedPages.length === 0) {
                const tmpl = TEMPLATES.find((t) => t.id === item.templateId) || TEMPLATES[0];
                const now = this.getUtcTimestamp();
                resolvedPages = tmpl.defaultPages.map((dp, idx) => ({
                  id: this.generateId(`page_${idx + 1}`),
                  siteId: sId,
                  title: dp.title,
                  slug: dp.slug,
                  seoTitle: dp.seoTitle,
                  seoDescription: dp.seoDescription,
                  isHomePage: dp.isHomePage,
                  isPublished: true,
                  isDeleted: false,
                  order: idx,
                  createdAt: now,
                  updatedAt: now,
                  sections: dp.sections.map((sec, secIdx) => ({
                    id: this.generateId(`sec_${idx}_${secIdx}`),
                    pageId: '',
                    siteId: sId,
                    type: sec.type,
                    name: sec.name,
                    isVisible: sec.isVisible,
                    isDeleted: false,
                    order: secIdx,
                    content: JSON.parse(JSON.stringify(sec.content)),
                    style: JSON.parse(JSON.stringify(sec.style)),
                    createdAt: now,
                    updatedAt: now,
                  })),
                }));
              }

              list.push({
                ...item,
                id: item.id || sId,
                settings: settings || item.settings || localSite?.settings,
                pages: resolvedPages,
                subscription: subscription || item.subscription || localSite?.subscription,
                domains: domains.length > 0 ? domains : (item.domains || localSite?.domains || []),
              });
          }
          return list;
        }
      } catch (err) {
        console.warn('RTDB getSitesByUser fallback:', err);
      }
    }

    const allSites = this.getLocal<Record<string, SiteData>>('sites', {});
    return Object.values(allSites).filter((s) => {
      if (s.isDeleted) return false;
      return s.ownerId === cleanUserId;
    }).map((s) => {
      if (!s.pages || s.pages.length === 0) {
        const tmpl = TEMPLATES.find((t) => t.id === s.templateId) || TEMPLATES[0];
        const now = this.getUtcTimestamp();
        s.pages = tmpl.defaultPages.map((dp, idx) => ({
          id: this.generateId(`page_${idx + 1}`),
          siteId: s.id,
          title: dp.title,
          slug: dp.slug,
          seoTitle: dp.seoTitle,
          seoDescription: dp.seoDescription,
          isHomePage: dp.isHomePage,
          isPublished: true,
          isDeleted: false,
          order: idx,
          createdAt: now,
          updatedAt: now,
          sections: dp.sections.map((sec, secIdx) => ({
            id: this.generateId(`sec_${idx}_${secIdx}`),
            pageId: '',
            siteId: s.id,
            type: sec.type,
            name: sec.name,
            isVisible: sec.isVisible,
            isDeleted: false,
            order: secIdx,
            content: JSON.parse(JSON.stringify(sec.content)),
            style: JSON.parse(JSON.stringify(sec.style)),
            createdAt: now,
            updatedAt: now,
          })),
        }));
      }
      return s;
    });
  }

  public async getSiteBySlugOrDomain(slugOrDomain: string): Promise<SiteData | null> {
    if (!slugOrDomain) return null;
    const clean = String(slugOrDomain).toLowerCase().replace(/^https?:\/\//, '').replace(/\/.*$/, '').trim();

    if (this.isRtdbAvailable() && database) {
      try {
        // Query by slug
        const slugQuery = query(ref(database, 'sites'), orderByChild('slug'), equalTo(clean));
        const snap = await get(slugQuery);
        if (snap.exists()) {
          const val = snap.val();
          const firstKey = Object.keys(val)[0];
          return this.getSite(firstKey);
        }
      } catch (err) {
        console.warn('RTDB getSiteBySlugOrDomain fallback:', err);
      }
    }

    const allSites = this.getLocal<Record<string, SiteData>>('sites', {});
    const match = Object.values(allSites).find(
      (s) => !s.isDeleted && (s.slug === clean || s.subdomain === clean || s.customDomain === clean)
    );
    return match || null;
  }

  public async createSite(
    ownerId: string,
    name: string,
    slugOrSubdomain: string,
    industry: IndustryType,
    templateId: string,
    planId: SubscriptionPlanId = 'PLAN_CORP_ANNUAL'
  ): Promise<SiteData> {
    const targetSector: IndustryType = industry === 'YACHT_RENTAL' ? 'YACHT_RENTAL' : 'CORPORATE';
    const lockKey = `${ownerId}_${targetSector}`;

    // Database Sector Quota Validation: Ensure user has active sector package and remaining quota
    if (ownerId && ownerId !== 'guest_user') {
      if (this.creationLocks.has(lockKey)) {
        throw new Error('Şu anda bu sektör için bir web sitesi oluşturuluyor. Lütfen işlemin tamamlanmasını bekleyin.');
      }
      this.creationLocks.add(lockKey);

      try {
        // Enforce Rule 1 & 2: 1 Account = 1 Active Website. An account cannot have 2 active websites across any sector.
        const existingUserSites = await this.getSitesByUser(ownerId);
        const activeWebsites = (existingUserSites || []).filter((s) => !s.isDeleted && s.status !== 'DELETED');
        if (activeWebsites.length >= 1) {
          throw new Error(
            'Bu hesapta zaten aktif bir website bulunmaktadır. Adiosoft sisteminde 1 hesapta aynı anda en fazla 1 aktif website barındırılabilir. Yeni bir site kurmak için lütfen önce mevcut sitenizi tamamen silin veya yeni bir Adiosoft hesabı oluşturun.'
          );
        }

        // Enforce Rule 10 & 11: Sector is locked to active subscription
        const userSubs = await this.getUserSubscriptions(ownerId);
        const activeWebSub = userSubs.find(
          (s) => s.status === 'ACTIVE' && (s.sector === 'CORPORATE' || s.sector === 'YACHT_RENTAL' || s.isWebsiteSubscription)
        );
        if (activeWebSub) {
          const allowedSector = activeWebSub.sector;
          if (allowedSector && allowedSector !== targetSector) {
            throw new Error(
              `Mevcut aktif aboneliğiniz ${allowedSector === 'YACHT_RENTAL' ? 'Yat Kiralama' : 'Kurumsal Web Sitesi'} paketidir. Bu hesapta yalnızca ${allowedSector === 'YACHT_RENTAL' ? 'Yat Kiralama' : 'Kurumsal Web Sitesi'} oluşturabilirsiniz.`
            );
          }
        }

        const quotas = await this.getSectorQuotas(ownerId, false);
        const sectorQuota = quotas[targetSector];
        if (!sectorQuota.allowed || sectorQuota.total === 0) {
          throw new Error(
            `Bu sektör (${targetSector === 'YACHT_RENTAL' ? 'Lüks Yat Kiralama' : 'Kurumsal Web Sitesi'}) için aktif bir website paketiniz bulunmuyor. Lütfen önce ilgili paketi satın alın.`
          );
        }
        if (sectorQuota.remaining <= 0 || sectorQuota.used >= sectorQuota.total) {
          throw new Error(
            `${sectorQuota.title} paketinizdeki website oluşturma hakkınız doldu (${sectorQuota.used} / ${sectorQuota.total}). Yeni bir ${sectorQuota.title} oluşturmak için lütfen mevcut sitelerinizden birini silin veya ek paket edinin.`
          );
        }
      } catch (err) {
        this.creationLocks.delete(lockKey);
        throw err;
      }
    }

    const now = this.getUtcTimestamp();
    const siteId = this.generateId('site');
    const slug = (slugOrSubdomain || name || 'site').toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-');

    const selectedTemplate = TEMPLATES.find((t) => t.id === templateId) || TEMPLATES[0];

    // Build complete default pages & sections using SiteStructureService (no empty/incomplete structure)
    const siteStructure = SiteStructureService.buildStructure({
      siteId,
      siteName: name,
      slug,
      industry,
      colorPalette: {
        primaryColor: selectedTemplate.primaryColor,
        secondaryColor: selectedTemplate.secondaryColor,
        accentColor: selectedTemplate.accentColor,
      },
      typography: {
        fontFamily: selectedTemplate.fontFamily,
        headingFont: selectedTemplate.headingFont,
      },
    });

    const pages: SitePage[] = siteStructure.pages;

    // Site Settings payload
    const settings: SiteSettings = {
      siteId,
      siteName: name,
      slug,
      industry,
      templateId: selectedTemplate.id,
      primaryColor: selectedTemplate.primaryColor,
      secondaryColor: selectedTemplate.secondaryColor,
      accentColor: selectedTemplate.accentColor,
      fontFamily: selectedTemplate.fontFamily,
      headingFont: selectedTemplate.headingFont,
      logoUrl: '',
      logoText: name,
      phone: '',
      email: '',
      address: '',
      headerMenu: siteStructure.headerMenu,
      headerButtonText: industry === 'YACHT_RENTAL' ? 'Rezervasyon Yap' : 'İletişime Geçin',
      headerButtonAction: {
        type: 'page',
        targetPage: industry === 'YACHT_RENTAL' ? '/rezervasyon' : '/iletisim',
      },
      socialLinks: {},
      seo: {
        title: `${name} — Resmi Web Sitesi`,
        description: `${name} resmi web platformu.`,
        keywords: [(name || '').toLowerCase(), (industry || '').toLowerCase()].filter(Boolean),
      },
      storageUsedBytes: 0,
      storageLimitBytes: 10 * 1024 * 1024 * 1024,
      updatedAt: now,
    };

    // Subscription payload matching target sector and database entitlements
    const planConfig =
      targetSector === 'YACHT_RENTAL'
        ? SUBSCRIPTION_PLANS.PLAN_YACHT_ANNUAL
        : SUBSCRIPTION_PLANS[planId] || SUBSCRIPTION_PLANS.PLAN_CORP_ANNUAL;

    // Check user active subscription for actual database limit (e.g. 999, "999", 2, "2")
    const userSubsForCreation = await this.getUserSubscriptions(ownerId);
    const activeSectorSub = userSubsForCreation.find(
      (s) => s.sector === targetSector && s.status === 'ACTIVE'
    );
    const rawLimitForSite = activeSectorSub?.websiteLimit ?? planConfig.websiteLimit;
    const normLimitForSite = normalizeQuota(rawLimitForSite);
    const effectiveLimitForSite = normLimitForSite.valid ? normLimitForSite.value : 0;

    const subscription: SiteSubscription = {
      siteId,
      planId: activeSectorSub?.planId || planConfig.id,
      planName: activeSectorSub?.planName || planConfig.name,
      priceTRY: planConfig.priceTRY,
      amount: planConfig.priceTRY,
      currency: 'TRY',
      websiteLimit: effectiveLimitForSite,
      status: 'ACTIVE',
      startDate: now,
      expireDate: now + 86400000 * 365,
      autoRenew: true,
      shopierOrderId: this.generateId('shp_ord'),
      updatedAt: now,
    };

    // Domain payload
    const domains: SiteDomain[] = [
      {
        id: this.generateId('dom'),
        siteId,
        domain: `adiosoft.com/${slug}`,
        subdomain: slug,
        isPrimary: true,
        status: 'ACTIVE',
        sslStatus: 'ACTIVE',
        createdAt: now,
        verifiedAt: now,
        updatedAt: now,
      },
    ];

    // Primary Site Record
    const newSite: SiteData = {
      siteId,
      id: siteId,
      ownerId,
      name,
      slug,
      subdomain: slug,
      industry,
      templateId: selectedTemplate.id,
      status: 'PUBLISHED',
      isDeleted: false,
      deletedAt: null,
      publishedVersion: 1,
      draftVersion: 1,
      createdAt: now,
      updatedAt: now,
      publishedAt: now,
      settings,
      subscription,
      domains,
      pages,
      publishedPages: pages,
      storageUsedMB: 50,
      storageLimitMB: 10000,
    };

    // Starter data policy: Zero fake/seed data in production. All modules start clean.

    // Save to RTDB sub-branches
    if (this.isRtdbAvailable() && database) {
      try {
        await Promise.all([
          set(ref(database, `sites/${siteId}`), {
            siteId,
            id: siteId,
            ownerId,
            name,
            slug,
            subdomain: slug,
            industry,
            templateId: selectedTemplate.id,
            status: 'PUBLISHED',
            isDeleted: false,
            createdAt: now,
            updatedAt: now,
            publishedAt: now,
          }),
          this.saveSiteMember(siteId, ownerId, 'owner'),
          this.saveSiteSettings(siteId, settings),
          this.saveSitePages(siteId, pages),
          this.saveSiteSubscription(siteId, subscription),
          this.saveSiteDomains(siteId, domains),
          this.initializeAnalytics(siteId),
        ]);
      } catch (err) {
        console.warn('RTDB createSite save fallback:', err);
      }
    }

    // Local Storage Save
    const allSites = this.getLocal<Record<string, SiteData>>('sites', {});
    allSites[siteId] = newSite;
    this.setLocal('sites', allSites);

    if (ownerId && ownerId !== 'guest_user') {
      this.creationLocks.delete(lockKey);
    }

    return newSite;
  }

  public async createSiteFromAISitePlan(
    ownerId: string,
    sitePlan: AISitePlan,
    planId: SubscriptionPlanId = 'PLAN_CORP_ANNUAL'
  ): Promise<SiteData> {
    const targetSector: IndustryType = sitePlan.industry === 'YACHT_RENTAL' ? 'YACHT_RENTAL' : 'CORPORATE';
    const lockKey = `${ownerId}_${targetSector}`;

    // Database Sector Quota Validation: Ensure user has active sector package and remaining quota
    if (ownerId && ownerId !== 'guest_user') {
      if (this.creationLocks.has(lockKey)) {
        throw new Error('Şu anda bu sektör için bir web sitesi oluşturuluyor. Lütfen işlemin tamamlanmasını bekleyin.');
      }
      this.creationLocks.add(lockKey);

      try {
        // Enforce Rule 1 & 2: 1 Account = 1 Active Website. An account cannot have 2 active websites across any sector.
        const existingUserSites = await this.getSitesByUser(ownerId);
        const activeWebsites = (existingUserSites || []).filter((s) => !s.isDeleted && s.status !== 'DELETED');
        if (activeWebsites.length >= 1) {
          throw new Error(
            'Bu hesapta zaten aktif bir website bulunmaktadır. Adiosoft sisteminde 1 hesapta aynı anda en fazla 1 aktif website barındırılabilir. Yeni bir site kurmak için lütfen önce mevcut sitenizi tamamen silin veya yeni bir Adiosoft hesabı oluşturun.'
          );
        }

        // Enforce Rule 10 & 11: Sector is locked to active subscription
        const userSubs = await this.getUserSubscriptions(ownerId);
        const activeWebSub = userSubs.find(
          (s) => s.status === 'ACTIVE' && (s.sector === 'CORPORATE' || s.sector === 'YACHT_RENTAL' || s.isWebsiteSubscription)
        );
        if (activeWebSub) {
          const allowedSector = activeWebSub.sector;
          if (allowedSector && allowedSector !== targetSector) {
            throw new Error(
              `Mevcut aktif aboneliğiniz ${allowedSector === 'YACHT_RENTAL' ? 'Yat Kiralama' : 'Kurumsal Web Sitesi'} paketidir. Bu hesapta yalnızca ${allowedSector === 'YACHT_RENTAL' ? 'Yat Kiralama' : 'Kurumsal Web Sitesi'} oluşturabilirsiniz.`
            );
          }
        }

        const quotas = await this.getSectorQuotas(ownerId, false);
        const sectorQuota = quotas[targetSector];
        if (!sectorQuota.allowed || sectorQuota.total === 0) {
          throw new Error(
            `Bu sektör (${targetSector === 'YACHT_RENTAL' ? 'Lüks Yat Kiralama' : 'Kurumsal Web Sitesi'}) için aktif bir website paketiniz bulunmuyor. Yeni bir site oluşturmak için lütfen ilgili paketi satın alın.`
          );
        }
        if (sectorQuota.remaining <= 0 || sectorQuota.used >= sectorQuota.total) {
          throw new Error(
            `${sectorQuota.title} paketinizdeki website oluşturma hakkınız doldu (${sectorQuota.used} / ${sectorQuota.total}). Yeni bir ${sectorQuota.title} oluşturmak için lütfen mevcut sitelerinizden birini silin veya ek paket edinin.`
          );
        }
      } catch (err) {
        this.creationLocks.delete(lockKey);
        throw err;
      }
    }

    const siteId = this.generateId('site');
    const now = this.getUtcTimestamp();
    const cleanSlug = sanitizeSlug(sitePlan.slug || sitePlan.name) || `site-${Date.now().toString(36)}`;
    const industry = sitePlan.industry || 'CORPORATE';

    const selectedPlan =
      industry === 'YACHT_RENTAL'
        ? SUBSCRIPTION_PLANS.PLAN_YACHT_ANNUAL
        : SUBSCRIPTION_PLANS[planId] || SUBSCRIPTION_PLANS.PLAN_CORP_ANNUAL;

    // Build full, robust 6 or 7 page structure with headerMenu and sections using SiteStructureService
    const siteStructure = SiteStructureService.buildStructure({
      siteId,
      siteName: sitePlan.settings?.siteName || sitePlan.name,
      slug: cleanSlug,
      industry,
      colorPalette: sitePlan.colorPalette,
      typography: sitePlan.typography,
      contactInfo: {
        phone: sitePlan.settings?.phone,
        email: sitePlan.settings?.email,
        address: sitePlan.settings?.address,
        whatsapp: sitePlan.settings?.socialLinks?.whatsapp,
      },
      userPrompt: sitePlan.settings?.seo?.description || sitePlan.name,
    });

    const settings: SiteSettings = {
      siteId,
      siteName: sitePlan.settings?.siteName || sitePlan.name,
      slug: cleanSlug,
      industry,
      logoText: sitePlan.settings?.logoText || sitePlan.name,
      primaryColor: sitePlan.colorPalette?.primaryColor || (industry === 'YACHT_RENTAL' ? '#0369A1' : '#1E40AF'),
      secondaryColor: sitePlan.colorPalette?.secondaryColor || (industry === 'YACHT_RENTAL' ? '#0C4A6E' : '#0F172A'),
      accentColor: sitePlan.colorPalette?.accentColor || (industry === 'YACHT_RENTAL' ? '#F59E0B' : '#10B981'),
      fontFamily: sitePlan.typography?.fontFamily || 'Inter',
      headingFont: sitePlan.typography?.headingFont || 'Plus Jakarta Sans',
      phone: sitePlan.settings?.phone || '',
      email: sitePlan.settings?.email || '',
      address: sitePlan.settings?.address || '',
      headerMenu: siteStructure.headerMenu,
      headerButtonText: industry === 'YACHT_RENTAL' ? 'Rezervasyon Yap' : 'Teklif Al',
      headerButtonAction: {
        type: 'page',
        targetPage: industry === 'YACHT_RENTAL' ? '/rezervasyon' : '/iletisim',
      },
      socialLinks: {
        whatsapp: sitePlan.settings?.socialLinks?.whatsapp || '',
        facebook: sitePlan.settings?.socialLinks?.facebook || '',
        instagram: sitePlan.settings?.socialLinks?.instagram || '',
        linkedin: sitePlan.settings?.socialLinks?.linkedin || '',
      },
      seo: {
        title: sitePlan.seo?.title || sitePlan.name,
        description: sitePlan.seo?.description || `${sitePlan.name} resmi web sitesi.`,
        keywords: sitePlan.seo?.keywords || [],
      },
      updatedAt: now,
    };

    // Use full 6 or 7 pages generated by SiteStructureService
    const pages: SitePage[] = siteStructure.pages;

    // Check user active subscription for actual database limit (e.g. 999, "999", 2, "2")
    const userSubsForAi = await this.getUserSubscriptions(ownerId);
    const activeSectorSubAi = userSubsForAi.find(
      (s) => s.sector === targetSector && s.status === 'ACTIVE'
    );
    const rawLimitForAi = activeSectorSubAi?.websiteLimit ?? selectedPlan.websiteLimit;
    const normLimitForAi = normalizeQuota(rawLimitForAi);
    const effectiveLimitForAi = normLimitForAi.valid ? normLimitForAi.value : 0;

    const subscription: SiteSubscription = {
      siteId,
      planId: activeSectorSubAi?.planId || selectedPlan.id,
      planName: activeSectorSubAi?.planName || selectedPlan.name,
      priceTRY: selectedPlan.priceTRY,
      amount: selectedPlan.priceTRY,
      currency: 'TRY',
      websiteLimit: effectiveLimitForAi,
      status: 'ACTIVE',
      startDate: now,
      expireDate: now + 365 * 24 * 60 * 60 * 1000, // 1 year
      autoRenew: true,
      shopierOrderId: 'shp_ai_trial_01',
      updatedAt: now,
    };

    const domains: SiteDomain[] = [
      {
        id: this.generateId('dom'),
        siteId,
        domain: `adiosoft.com/${cleanSlug}`,
        subdomain: cleanSlug,
        isPrimary: true,
        status: 'ACTIVE',
        sslStatus: 'ACTIVE',
        createdAt: now,
        verifiedAt: now,
        updatedAt: now,
      },
    ];

    const newSite: SiteData = {
      id: siteId,
      siteId,
      ownerId,
      name: sitePlan.name,
      slug: cleanSlug,
      subdomain: cleanSlug,
      industry,
      templateId: 'ai-generated-custom',
      status: 'DRAFT', // Always create AI sites as DRAFT initially
      pages,
      publishedPages: pages,
      settings,
      subscription,
      domains,
      createdAt: now,
      updatedAt: now,
      storageUsedMB: 0,
      storageLimitMB: selectedPlan.storageGb * 1024,
      isDeleted: false,
    };

    // Zero fake content policy: Real sites start clean with 0 fake blog posts or fleet items.

    if (this.isRtdbAvailable() && database) {
      try {
        await Promise.all([
          set(ref(database, `sites/${siteId}`), {
            siteId,
            id: siteId,
            ownerId,
            name: sitePlan.name,
            slug: cleanSlug,
            subdomain: cleanSlug,
            industry,
            templateId: 'ai-generated-custom',
            status: 'DRAFT',
            isDeleted: false,
            createdAt: now,
            updatedAt: now,
          }),
          this.saveSiteMember(siteId, ownerId, 'owner'),
          this.saveSiteSettings(siteId, settings),
          this.saveSitePages(siteId, pages),
          this.saveSiteSubscription(siteId, subscription),
          this.saveSiteDomains(siteId, domains),
          this.initializeAnalytics(siteId),
        ]);
      } catch (err) {
        console.warn('RTDB createSiteFromAISitePlan fallback:', err);
      }
    }

    const allSites = this.getLocal<Record<string, SiteData>>('sites', {});
    allSites[siteId] = newSite;
    this.setLocal('sites', allSites);

    if (ownerId && ownerId !== 'guest_user') {
      this.creationLocks.delete(lockKey);
    }

    return newSite;
  }


  public async updateSite(site: SiteData): Promise<SiteData> {
    const now = this.getUtcTimestamp();
    const siteId = site.id || site.siteId!;
    const updated: SiteData = {
      ...site,
      id: siteId,
      siteId,
      updatedAt: now,
    };

    if (this.isRtdbAvailable() && database) {
      try {
        await Promise.all([
          update(ref(database, `sites/${siteId}`), {
            name: updated.name,
            slug: updated.slug,
            subdomain: updated.subdomain || updated.slug,
            customDomain: updated.customDomain || null,
            domainStatus: updated.domainStatus || 'PENDING_DNS',
            sslStatus: updated.sslStatus || 'PENDING',
            industry: updated.industry,
            templateId: updated.templateId,
            status: updated.status,
            updatedAt: now,
          }),
          this.saveSiteSettings(siteId, updated.settings),
          this.saveSitePages(siteId, updated.pages),
          updated.subscription ? this.saveSiteSubscription(siteId, updated.subscription) : Promise.resolve(),
          updated.domains ? this.saveSiteDomains(siteId, updated.domains) : Promise.resolve(),
        ]);
      } catch (err) {
        console.warn('RTDB updateSite fallback:', err);
      }
    }

    const allSites = this.getLocal<Record<string, SiteData>>('sites', {});
    allSites[siteId] = updated;
    this.setLocal('sites', allSites);
    return updated;
  }

  public async publishSite(siteId: string, publishedBy = 'system'): Promise<SiteData> {
    const current = await this.getSite(siteId);
    if (!current) throw new Error('Site bulunamadı');

    // 1. Step 1: Validate Draft State
    if (!current.pages || current.pages.length === 0) {
      throw new Error('Yayınlanacak sayfa bulunamadı. Lütfen en az bir sayfa ekleyin.');
    }

    // 2. Step 2: Validate Pages (must have at least one home page)
    const homePage = current.pages.find((p) => p.isHomePage && !p.isDeleted);
    if (!homePage) {
      // Auto-designate first page as home page if not set
      current.pages[0].isHomePage = true;
    }

    // 3. Step 3: Validate Sections
    let totalSections = 0;
    const validatedPages = (current.pages || [])
      .filter((p) => p && !p.isDeleted && p.isPublished !== false)
      .map((p) => {
        const activeSections = (p?.sections || []).filter((s) => !s.isDeleted && s.isVisible !== false);
        totalSections += activeSections.length;
        return {
          ...p,
          sections: activeSections,
        };
      });

    if (totalSections === 0) {
      throw new Error('Sitede yayınlanabilir hiçbir bölüm (section) bulunamadı.');
    }

    // 4. Step 4: Validate Navigation
    const navigation = validatedPages.map((p, idx) => ({
      id: p.id,
      title: p.title,
      slug: p.slug,
      isHomePage: Boolean(p.isHomePage),
      order: p.order ?? idx,
    }));

    // 5. Step 5: Validate SEO
    const seo: SiteSEO = {
      title: current.settings?.seo?.title || `${current.name} — Resmi Web Sitesi`,
      description: current.settings?.seo?.description || `${current.name} kurumsal web platformu.`,
      keywords: current.settings?.seo?.keywords || [
        (current.name || '').toLowerCase(),
        (current.industry || '').toLowerCase(),
      ].filter(Boolean),
      ogImage: current.settings?.seo?.ogImage || current.settings?.logoUrl || '',
      canonicalUrl: current.customDomain
        ? `https://${current.customDomain}`
        : `https://www.adiosoft.com/${current.slug || current.subdomain || 'site'}`,
    };

    const now = this.getUtcTimestamp();
    const newVersionNumber = (current.publishedVersion || 0) + 1;
    const versionId = `v${newVersionNumber}_${now.toString(36)}`;

    // 6. Step 6: Generate Published Snapshot
    const snapshot: PublishedSiteSnapshot = {
      versionId,
      siteId,
      publishedAt: now,
      publishedBy,
      name: current.name,
      industry: current.industry,
      templateId: current.templateId,
      templateVersion: 1,
      theme: {
        primaryColor: current.settings?.primaryColor || '#0284C7',
        secondaryColor: current.settings?.secondaryColor || '#0F172A',
        accentColor: current.settings?.accentColor || '#38BDF8',
        fontFamily: current.settings?.fontFamily || 'Inter, sans-serif',
        headingFont: current.settings?.headingFont || 'Plus Jakarta Sans, sans-serif',
        logoUrl: current.settings?.logoUrl || '',
        logoText: current.settings?.logoText || current.name,
      },
      settings: current.settings,
      navigation,
      pages: validatedPages,
      seo,
      status: 'PUBLISHED',
    };

    // 7. Step 7 & 8: Multi-location Atomic Write (/sitePublished/{siteId}, /siteVersions/{siteId}/{versionId})
    const versionRecord: SiteVersion = {
      versionId,
      siteId,
      publishedAt: now,
      publishedBy,
      summary: `Versiyon ${newVersionNumber} yayını (${validatedPages.length} sayfa, ${totalSections} bölüm)`,
      pageCount: validatedPages.length,
      sectionCount: totalSections,
      snapshot,
    };

    if (this.isRtdbAvailable() && database) {
      try {
        await Promise.all([
          // Root published snapshot for public renderer
          set(ref(database, `sitePublished/${siteId}`), snapshot),
          // Version history record
          set(ref(database, `siteVersions/${siteId}/${versionId}`), versionRecord),
          // Update site status
          update(ref(database, `sites/${siteId}`), {
            status: 'PUBLISHED',
            publishedVersion: newVersionNumber,
            publishedAt: now,
            updatedAt: now,
          }),
          // Audit Log
          this.createAuditLog(
            siteId,
            publishedBy,
            'SITE_PUBLISHED',
            `Site başarıyla yayınlandı (Versiyon: ${newVersionNumber})`
          ),
        ]);
      } catch (err) {
        console.warn('RTDB publishSite atomic transaction fallback:', err);
      }
    }

    // Local Storage Multi-location update
    const allPublished = this.getLocal<Record<string, PublishedSiteSnapshot>>('publishedSnapshots', {});
    allPublished[siteId] = snapshot;
    this.setLocal('publishedSnapshots', allPublished);

    const allVersions = this.getLocal<Record<string, Record<string, SiteVersion>>>('siteVersions', {});
    if (!allVersions[siteId]) allVersions[siteId] = {};
    allVersions[siteId][versionId] = versionRecord;
    this.setLocal('siteVersions', allVersions);

    const updatedSite: SiteData = {
      ...current,
      status: 'PUBLISHED',
      publishedVersion: newVersionNumber,
      publishedAt: now,
      updatedAt: now,
      publishedPages: validatedPages,
    };

    const allSites = this.getLocal<Record<string, SiteData>>('sites', {});
    allSites[siteId] = updatedSite;
    this.setLocal('sites', allSites);

    return updatedSite;
  }

  /**
   * Unpublish Website
   * Transitions site status to UNPUBLISHED, removes published snapshot from public renderer,
   * invalidates resolver cache, but preserves all user draft content and data completely intact.
   */
  public async unpublishSite(siteId: string, unpublishedBy = 'system'): Promise<SiteData> {
    const current = await this.getSite(siteId);
    if (!current) throw new Error('Site bulunamadı');

    const now = Date.now();

    // 1. Update RTDB if available
    if (this.isRtdbAvailable() && database) {
      try {
        await Promise.all([
          // Remove or clear published snapshot
          set(ref(database, `sitePublished/${siteId}`), null),
          // Update site status to UNPUBLISHED
          update(ref(database, `sites/${siteId}`), {
            status: 'UNPUBLISHED',
            updatedAt: now,
          }),
          // Audit Log
          this.createAuditLog(
            siteId,
            unpublishedBy,
            'SITE_UNPUBLISHED',
            `Site yayından kaldırıldı (Unpublished). Veriler ve taslak korundu.`
          ),
        ]);
      } catch (err) {
        console.warn('RTDB unpublishSite fallback:', err);
      }
    }

    // 2. Local storage sync
    const allPublished = this.getLocal<Record<string, PublishedSiteSnapshot>>('publishedSnapshots', {});
    delete allPublished[siteId];
    this.setLocal('publishedSnapshots', allPublished);

    const updatedSite: SiteData = {
      ...current,
      status: 'UNPUBLISHED',
      updatedAt: now,
    };

    const allSites = this.getLocal<Record<string, SiteData>>('sites', {});
    allSites[siteId] = updatedSite;
    this.setLocal('sites', allSites);

    // 3. Invalidate domain and resolver cache
    try {
      const { SiteResolverService } = await import('./siteResolverService');
      if (current.subdomain) SiteResolverService.invalidateCache(current.subdomain);
      if (current.customDomain) SiteResolverService.invalidateCache(current.customDomain);
      if (current.domains) {
        current.domains.forEach((d) => SiteResolverService.invalidateCache(d.domain));
      }
    } catch (cacheErr) {
      console.warn('Cache invalidation notice:', cacheErr);
    }

    return updatedSite;
  }

  /**
   * Deletes a site (soft-delete):
   * 1. Sets isDeleted = true and deletedAt timestamp in database.
   * 2. Sets status = 'UNPUBLISHED'.
   * 3. Removes public published snapshot from /sitePublished/{siteId}.
   * 4. Disconnects custom domain and removes domain mapping to avoid conflicts.
   * 5. Invalidates CDN and cache.
   * 6. Reclaims sector and total website quota automatically in QuotaService.
   */
  public async deleteSite(siteId: string, deletedBy: string = 'user'): Promise<void> {
    const current = await this.getSite(siteId);
    if (!current) return;

    const now = this.getUtcTimestamp();

    // 1. Disconnect any custom domains attached to this site
    // Disconnect DNS mapping and mark domain records as DISCONNECTED
    if (current.customDomain || (current.domains && current.domains.length > 0)) {
      try {
        const domainsToClear = [
          ...(current.customDomain ? [current.customDomain] : []),
          ...(current.domains ? current.domains.map((d) => d.domain) : []),
        ];
        for (const dom of domainsToClear) {
          await this.removeDomainMapping(dom);
        }
      } catch (domErr) {
        console.warn('Domain removal during site delete notice:', domErr);
      }
    }

    // 2. Storage cleanup: physically remove all website-specific assets from Firebase Storage
    try {
      const { storageService } = await import('./storageService');
      await storageService.deleteSiteFolder(siteId);
    } catch (storageErr) {
      console.warn('Storage cleanup during site delete notice:', storageErr);
    }

    // 3. Database update: Permanent removal of all website content, records, and metadata
    // CRITICAL: Subscriptions, payments, orders, invoices, and audit logs are NEVER touched!
    if (this.isRtdbAvailable() && database) {
      try {
        await Promise.all([
          // Remove public published snapshot immediately
          set(ref(database, `sitePublished/${siteId}`), null),
          // Remove version snapshots
          set(ref(database, `siteVersions/${siteId}`), null),
          // Remove website-specific settings
          set(ref(database, `siteSettings/${siteId}`), null),
          // Remove pages structure & design
          set(ref(database, `sitePages/${siteId}`), null),
          // Remove sections and components
          set(ref(database, `siteSections/${siteId}`), null),
          // Remove website media records
          set(ref(database, `siteMedia/${siteId}`), null),
          // Remove blog posts
          set(ref(database, `siteBlogs/${siteId}`), null),
          // Remove yacht records, fleet, routes, marinas, reservations, availability
          set(ref(database, `siteYachts/${siteId}`), null),
          set(ref(database, `siteYachtAvailability/${siteId}`), null),
          set(ref(database, `siteRoutes/${siteId}`), null),
          set(ref(database, `siteMarinas/${siteId}`), null),
          set(ref(database, `siteReservations/${siteId}`), null),
          // Remove communications / contact messages
          set(ref(database, `siteCommunications/${siteId}`), null),
          // Remove site domain mappings
          set(ref(database, `siteDomains/${siteId}`), null),
          // Remove site members & analytics
          set(ref(database, `siteMembers/${siteId}`), null),
          set(ref(database, `siteAnalytics/${siteId}`), null),
          // Permanently remove the site record from sites collection
          set(ref(database, `sites/${siteId}`), null),
          // System Audit Log (Kept in auditLogs/ for accounting & integrity compliance)
          this.createAuditLog(
            siteId,
            deletedBy,
            'SITE_DELETED',
            `Website "${current.name}" ve tüm içerikleri kalıcı olarak silindi. Website hakkı serbest bırakıldı, abonelik korundu.`
          ),
        ]);
      } catch (err) {
        console.warn('RTDB deleteSite fallback:', err);
      }
    }

    // 4. Local storage purge: Remove all cached site data and sub-branches
    const allPublished = this.getLocal<Record<string, PublishedSiteSnapshot>>('publishedSnapshots', {});
    delete allPublished[siteId];
    this.setLocal('publishedSnapshots', allPublished);

    const allSites = this.getLocal<Record<string, SiteData>>('sites', {});
    delete allSites[siteId];
    this.setLocal('sites', allSites);

    const allSiteDomains = this.getLocal<Record<string, SiteDomain[]>>('siteDomains', {});
    delete allSiteDomains[siteId];
    this.setLocal('siteDomains', allSiteDomains);

    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const keysToRemove: string[] = [];
        for (let i = 0; i < window.localStorage.length; i++) {
          const k = window.localStorage.key(i);
          if (k && k.includes(siteId)) {
            keysToRemove.push(k);
          }
        }
        keysToRemove.forEach((k) => window.localStorage.removeItem(k));
      }
    } catch (lsErr) {
      console.warn('Local storage purge notice:', lsErr);
    }

    // 5. Invalidate caches
    try {
      const { SiteResolverService } = await import('./siteResolverService');
      if (current.subdomain) SiteResolverService.invalidateCache(current.subdomain);
      if (current.customDomain) SiteResolverService.invalidateCache(current.customDomain);
      if (current.domains) {
        current.domains.forEach((d) => SiteResolverService.invalidateCache(d.domain));
      }
    } catch (cacheErr) {
      console.warn('Cache invalidation notice:', cacheErr);
    }
  }

  /**
   * Retrieves the optimized, isolated PUBLISHED SNAPSHOT for public rendering.
   * Public renderer NEVER reads raw draft /siteSections or editor states.
   */
  public async getPublishedSnapshot(siteId: string): Promise<PublishedSiteSnapshot | null> {
    if (this.isRtdbAvailable() && database) {
      try {
        const snap = await get(ref(database, `sitePublished/${siteId}`));
        if (snap.exists()) {
          return snap.val() as PublishedSiteSnapshot;
        }
      } catch (err) {
        console.warn('RTDB getPublishedSnapshot fallback:', err);
      }
    }

    const allPublished = this.getLocal<Record<string, PublishedSiteSnapshot>>('publishedSnapshots', {});
    if (allPublished[siteId]) {
      return allPublished[siteId];
    }

    // Fallback: If site was published before snapshot node, construct it safely
    const site = await this.getSite(siteId);
    if (site && site.status === 'PUBLISHED') {
      return {
        versionId: 'v1_legacy',
        siteId,
        publishedAt: site.publishedAt || site.createdAt,
        publishedBy: site.ownerId,
        name: site.name,
        industry: site.industry,
        templateId: site.templateId,
        templateVersion: 1,
        theme: {
          primaryColor: site.settings?.primaryColor || '#0284C7',
          secondaryColor: site.settings?.secondaryColor || '#0F172A',
          accentColor: site.settings?.accentColor || '#38BDF8',
          fontFamily: site.settings?.fontFamily || 'Inter, sans-serif',
          headingFont: site.settings?.headingFont || 'Plus Jakarta Sans, sans-serif',
          logoUrl: site.settings?.logoUrl || '',
          logoText: site.settings?.logoText || site.name,
        },
        settings: site.settings,
        navigation: site.pages.map((p, i) => ({ id: p.id, title: p.title, slug: p.slug, isHomePage: p.isHomePage, order: i })),
        pages: site.publishedPages && site.publishedPages.length > 0 ? site.publishedPages : site.pages,
        seo: site.settings?.seo || { title: site.name },
        status: 'PUBLISHED',
      };
    }

    return null;
  }

  /**
   * Version Rollback: Safely restores a previously published version snapshot as the active published state.
   */
  public async rollbackToVersion(siteId: string, versionId: string, userId: string): Promise<PublishedSiteSnapshot> {
    let targetVersion: SiteVersion | null = null;

    if (this.isRtdbAvailable() && database) {
      try {
        const snap = await get(ref(database, `siteVersions/${siteId}/${versionId}`));
        if (snap.exists()) {
          targetVersion = snap.val() as SiteVersion;
        }
      } catch (err) {
        console.warn('RTDB rollback target fetch fallback:', err);
      }
    }

    if (!targetVersion) {
      const allVersions = this.getLocal<Record<string, Record<string, SiteVersion>>>('siteVersions', {});
      targetVersion = allVersions[siteId]?.[versionId] || null;
    }

    if (!targetVersion || !targetVersion.snapshot) {
      throw new Error('Geri yüklenecek versiyon snapshot kaydı bulunamadı.');
    }

    const now = this.getUtcTimestamp();
    const restoredSnapshot: PublishedSiteSnapshot = {
      ...targetVersion.snapshot,
      versionId: `rollback_${Date.now().toString(36)}`,
      publishedAt: now,
      publishedBy: userId,
    };

    if (this.isRtdbAvailable() && database) {
      try {
        await Promise.all([
          set(ref(database, `sitePublished/${siteId}`), restoredSnapshot),
          update(ref(database, `sites/${siteId}`), {
            status: 'PUBLISHED',
            publishedAt: now,
            updatedAt: now,
          }),
          this.createAuditLog(
            siteId,
            userId,
            'VERSION_ROLLBACK',
            `Versiyon ${versionId} başarıyla geri yüklendi ve yayına alındı.`
          ),
        ]);
      } catch (err) {
        console.warn('RTDB rollback write fallback:', err);
      }
    }

    const allPublished = this.getLocal<Record<string, PublishedSiteSnapshot>>('publishedSnapshots', {});
    allPublished[siteId] = restoredSnapshot;
    this.setLocal('publishedSnapshots', allPublished);

    return restoredSnapshot;
  }

  /**
   * Fetches version history for a given site.
   */
  public async getSiteVersions(siteId: string): Promise<SiteVersion[]> {
    if (this.isRtdbAvailable() && database) {
      try {
        const snap = await get(ref(database, `siteVersions/${siteId}`));
        if (snap.exists()) {
          return Object.values(snap.val() as Record<string, SiteVersion>).sort(
            (a, b) => b.publishedAt - a.publishedAt
          );
        }
      } catch (err) {
        console.warn('RTDB getSiteVersions fallback:', err);
      }
    }

    const allVersions = this.getLocal<Record<string, Record<string, SiteVersion>>>('siteVersions', {});
    return Object.values(allVersions[siteId] || {}).sort((a, b) => b.publishedAt - a.publishedAt);
  }

  public async getSiteVersion(siteId: string, versionId: string): Promise<SiteVersion | null> {
    if (this.isRtdbAvailable() && database) {
      try {
        const snap = await get(ref(database, `siteVersions/${siteId}/${versionId}`));
        if (snap.exists()) {
          return snap.val() as SiteVersion;
        }
      } catch (err) {
        console.warn('RTDB getSiteVersion fallback:', err);
      }
    }
    const allVersions = this.getLocal<Record<string, Record<string, SiteVersion>>>('siteVersions', {});
    return allVersions[siteId]?.[versionId] || null;
  }

  public async savePublishedSnapshot(siteId: string, snapshot: PublishedSiteSnapshot): Promise<void> {
    if (this.isRtdbAvailable() && database) {
      try {
        await set(ref(database, `sitePublished/${siteId}`), snapshot);
      } catch (err) {
        console.warn('RTDB savePublishedSnapshot fallback:', err);
      }
    }
    const allPublished = this.getLocal<Record<string, PublishedSiteSnapshot>>('publishedSnapshots', {});
    allPublished[siteId] = snapshot;
    this.setLocal('publishedSnapshots', allPublished);
  }

  public async saveSiteVersion(siteId: string, versionId: string, version: SiteVersion): Promise<void> {
    if (this.isRtdbAvailable() && database) {
      try {
        await set(ref(database, `siteVersions/${siteId}/${versionId}`), version);
      } catch (err) {
        console.warn('RTDB saveSiteVersion fallback:', err);
      }
    }
    const allVersions = this.getLocal<Record<string, Record<string, SiteVersion>>>('siteVersions', {});
    if (!allVersions[siteId]) allVersions[siteId] = {};
    allVersions[siteId][versionId] = version;
    this.setLocal('siteVersions', allVersions);
  }

  public async getSiteBySubdomain(subdomain: string): Promise<SiteData | null> {
    if (!subdomain) return null;
    const cleanSub = String(subdomain).toLowerCase().trim();
    if (this.isRtdbAvailable() && database) {
      try {
        const q = query(ref(database, 'sites'), orderByChild('subdomain'), equalTo(cleanSub));
        const snap = await get(q);
        if (snap.exists()) {
          const val = snap.val();
          const firstKey = Object.keys(val)[0];
          return this.getSite(firstKey);
        }
      } catch (err) {
        console.warn('RTDB getSiteBySubdomain fallback:', err);
      }
    }
    const allSites = this.getLocal<Record<string, SiteData>>('sites', {});
    const match = Object.values(allSites).find(
      (s) => !s.isDeleted && (s.subdomain?.toLowerCase() === cleanSub || s.slug?.toLowerCase() === cleanSub)
    );
    return match || null;
  }

  /**
   * Audit Logging: Records security, publishing, and ownership actions.
   */
  public async createAuditLog(
    siteId: string | undefined,
    userId: string,
    action: AuditLog['action'],
    details: string
  ): Promise<AuditLog> {
    const logId = this.generateId('log');
    const log: AuditLog = {
      logId,
      userId,
      action,
      details,
      timestamp: this.getUtcTimestamp(),
      ...(siteId ? { siteId } : {}),
    };

    if (this.isRtdbAvailable() && database) {
      try {
        await set(ref(database, `auditLogs/${logId}`), this.cleanForRtdb(log));
      } catch (err) {
        console.warn('RTDB createAuditLog fallback:', err);
      }
    }

    const allLogs = this.getLocal<AuditLog[]>('auditLogs', []);
    allLogs.unshift(log);
    this.setLocal('auditLogs', allLogs.slice(0, 200));
    return log;
  }

  public async getAuditLogs(siteId?: string): Promise<AuditLog[]> {
    if (this.isRtdbAvailable() && database) {
      try {
        const snap = await get(ref(database, 'auditLogs'));
        if (snap.exists()) {
          const list = Object.values(snap.val() as Record<string, AuditLog>);
          return siteId ? list.filter((l) => l.siteId === siteId) : list;
        }
      } catch (err) {
        console.warn('RTDB getAuditLogs fallback:', err);
      }
    }
    const allLogs = this.getLocal<AuditLog[]>('auditLogs', []);
    return siteId ? allLogs.filter((l) => l.siteId === siteId) : allLogs;
  }

  /**
   * Idempotent Payment Record: Records Shopier / Stripe transactions and prevents replay updates.
   */
  public async processShopierWebhook(payment: {
    idempotencyKey: string;
    siteId: string;
    userId: string;
    amount: number;
    currency?: 'TRY' | 'USD' | 'EUR';
    planId: SubscriptionPlanId;
    planName: string;
    rawPayload?: Record<string, any>;
  }): Promise<{ success: boolean; isDuplicate: boolean; record: PaymentRecord }> {
    const paymentId = this.generateId('pay');
    
    // Check if idempotency key was already processed
    const existingPayments = this.getLocal<Record<string, PaymentRecord>>('paymentRecords', {});
    const duplicate = Object.values(existingPayments).find(
      (p) => p.idempotencyKey === payment.idempotencyKey || (p.siteId === payment.siteId && p.planId === payment.planId && Date.now() - p.createdAt < 300000)
    );

    if (duplicate) {
      return { success: true, isDuplicate: true, record: duplicate };
    }

    const now = this.getUtcTimestamp();
    const record: PaymentRecord = {
      paymentId,
      siteId: payment.siteId,
      userId: payment.userId,
      provider: 'SHOPIER',
      amount: payment.amount,
      currency: payment.currency || 'TRY',
      status: 'SUCCESS',
      planId: payment.planId,
      planName: payment.planName,
      idempotencyKey: payment.idempotencyKey,
      createdAt: now,
      processedAt: now,
      rawPayload: payment.rawPayload,
    };

    if (this.isRtdbAvailable() && database) {
      try {
        const existingSub = await this.getSiteSubscription(payment.siteId);
        let subStartDate = now;
        let subExpireDate = now + 86400000 * 365;

        // Early Renewal Rule: If subscription is active and renewalDate is in the future,
        // extend the new period from the current renewalDate rather than from today!
        if (existingSub && existingSub.expireDate && existingSub.expireDate > now) {
          subExpireDate = existingSub.expireDate + (86400000 * 365);
          subStartDate = existingSub.startDate || now;
        }

        await Promise.all([
          set(ref(database, `payments/${paymentId}`), record),
          this.saveSiteSubscription(payment.siteId, {
            siteId: payment.siteId,
            planId: payment.planId,
            planName: payment.planName,
            priceTRY: payment.amount,
            amount: payment.amount,
            currency: payment.currency || 'TRY',
            status: 'ACTIVE',
            startDate: subStartDate,
            expireDate: subExpireDate,
            lastPaymentId: paymentId,
            paymentId,
            updatedAt: now,
          }),
          this.createAuditLog(
            payment.siteId,
            payment.userId,
            'PAYMENT_RECEIVED',
            `Ödeme alındı: ${payment.amount} ${payment.currency || 'TRY'} (Plan: ${payment.planName})`
          ),
        ]);
      } catch (err) {
        console.warn('RTDB processShopierWebhook fallback:', err);
      }
    }

    existingPayments[paymentId] = record;
    this.setLocal('paymentRecords', existingPayments);

    return { success: true, isDuplicate: false, record };
  }

  /**
   * Billing Orders lookup helper
   */
  public async getOrder(orderId: string): Promise<BillingOrder | null> {
    return this.getOrderById(orderId);
  }

  /**
   * User lookup by email
   */
  public async getUserByEmail(email: string): Promise<UserProfile | null> {
    const cleanEmail = email.trim().toLowerCase();
    if (this.isRtdbAvailable() && database) {
      try {
        const snap = await get(ref(database, 'users'));
        if (snap.exists()) {
          const users = snap.val() as Record<string, UserProfile>;
          for (const u of Object.values(users)) {
            if (u?.email?.toLowerCase() === cleanEmail) {
              return u;
            }
          }
        }
      } catch (err) {
        console.warn('RTDB getUserByEmail fallback:', err);
      }
    }
    const localUsers = this.getLocal<Record<string, UserProfile>>('users', {});
    for (const u of Object.values(localUsers)) {
      if (u?.email?.toLowerCase() === cleanEmail) {
        return u;
      }
    }
    return null;
  }

  // ==========================================
  // 3. /siteMembers/{siteId}/{uid}
  // ==========================================
  public async getSiteMembers(siteId: string): Promise<SiteMember[]> {
    if (this.isRtdbAvailable() && database) {
      try {
        const membersRef = ref(database, `siteMembers/${siteId}`);
        const snapshot = await get(membersRef);
        if (snapshot.exists()) {
          return Object.values(snapshot.val());
        }
      } catch (err) {
        console.warn('RTDB getSiteMembers fallback:', err);
      }
    }
    const allMembers = this.getLocal<Record<string, Record<string, SiteMember>>>('siteMembers', {});
    return Object.values(allMembers[siteId] || {});
  }

  public async saveSiteMember(
    siteId: string,
    uid: string,
    role: 'owner' | 'admin' | 'editor' | 'viewer',
    addedBy?: string
  ): Promise<SiteMember> {
    const now = this.getUtcTimestamp();
    const permissions = {
      canEditContent: role !== 'viewer',
      canPublish: role === 'owner' || role === 'admin',
      canManageBilling: role === 'owner',
      canManageMembers: role === 'owner' || role === 'admin',
      canManageDomains: role === 'owner' || role === 'admin',
      canManageYachts: role !== 'viewer',
      canManageReservations: role !== 'viewer',
    };

    const member: SiteMember = {
      siteId,
      uid,
      role,
      permissions,
      addedAt: now,
      updatedAt: now,
      addedBy: addedBy || uid,
    };

    if (this.isRtdbAvailable() && database) {
      try {
        await set(ref(database, `siteMembers/${siteId}/${uid}`), member);
      } catch (err) {
        console.warn('RTDB saveSiteMember fallback:', err);
      }
    }

    const allMembers = this.getLocal<Record<string, Record<string, SiteMember>>>('siteMembers', {});
    if (!allMembers[siteId]) allMembers[siteId] = {};
    allMembers[siteId][uid] = member;
    this.setLocal('siteMembers', allMembers);
    return member;
  }

  public async removeSiteMember(siteId: string, uid: string): Promise<void> {
    if (this.isRtdbAvailable() && database) {
      try {
        await remove(ref(database, `siteMembers/${siteId}/${uid}`));
      } catch (err) {
        console.warn('RTDB removeSiteMember fallback:', err);
      }
    }
    const allMembers = this.getLocal<Record<string, Record<string, SiteMember>>>('siteMembers', {});
    if (allMembers[siteId] && allMembers[siteId][uid]) {
      delete allMembers[siteId][uid];
      this.setLocal('siteMembers', allMembers);
    }
  }

  // ==========================================
  // 4. /siteSettings/{siteId}
  // ==========================================
  public async getSiteSettings(siteId: string): Promise<SiteSettings | null> {
    if (this.isRtdbAvailable() && database) {
      try {
        const snap = await get(ref(database, `siteSettings/${siteId}`));
        if (snap.exists()) return snap.val() as SiteSettings;
      } catch (err) {
        console.warn('RTDB getSiteSettings fallback:', err);
      }
    }
    const allSettings = this.getLocal<Record<string, SiteSettings>>('siteSettings', {});
    return allSettings[siteId] || null;
  }

  public async saveSiteSettings(siteId: string, settings: SiteSettings): Promise<void> {
    const payload = {
      ...settings,
      siteId,
      updatedAt: this.getUtcTimestamp(),
    };
    if (this.isRtdbAvailable() && database) {
      try {
        await set(ref(database, `siteSettings/${siteId}`), payload);
      } catch (err) {
        console.warn('RTDB saveSiteSettings fallback:', err);
      }
    }
    const allSettings = this.getLocal<Record<string, SiteSettings>>('siteSettings', {});
    allSettings[siteId] = payload;
    this.setLocal('siteSettings', allSettings);
  }

  // ==========================================
  // 5. /sitePages/{siteId}/{pageId} & /siteSections
  // ==========================================
  public async getSitePages(siteId: string): Promise<SitePage[]> {
    if (this.isRtdbAvailable() && database) {
      try {
        let snap;
        try {
          // Attempt query by order
          const pagesQuery = query(ref(database, `sitePages/${siteId}`), orderByChild('order'));
          snap = await get(pagesQuery);
        } catch {
          // Fallback to direct ref fetch if .indexOn is missing in live RTDB rules
          snap = await get(ref(database, `sitePages/${siteId}`));
        }

        if (snap.exists()) {
          const rawPages = snap.val();
          const pageList: SitePage[] = [];
          for (const pId of Object.keys(rawPages)) {
            const pageItem = rawPages[pId] as SitePage;
            if (!pageItem.isDeleted) {
              const sections = await this.getPageSections(siteId, pId);
              pageList.push({
                ...pageItem,
                id: pageItem.id || pId,
                sections: sections.length > 0 ? sections : pageItem.sections || [],
              });
            }
          }
          return pageList.sort((a, b) => (a.order || 0) - (b.order || 0));
        }
      } catch (err) {
        console.warn('RTDB getSitePages fallback:', err);
      }
    }
    const allPages = this.getLocal<Record<string, SitePage[]>>('sitePages', {});
    const localPages = (allPages[siteId] || []).filter((p) => !p.isDeleted);
    if (localPages.length > 0) {
      return localPages.sort((a, b) => (a.order || 0) - (b.order || 0));
    }

    const allSites = this.getLocal<Record<string, SiteData>>('sites', {});
    const localSite = allSites[siteId];
    if (localSite?.pages && localSite.pages.length > 0) {
      return localSite.pages.filter((p) => !p.isDeleted).sort((a, b) => (a.order || 0) - (b.order || 0));
    }

    return [];
  }

  public async saveSitePages(siteId: string, pages: SitePage[]): Promise<void> {
    const now = this.getUtcTimestamp();
    const formattedPages = pages.map((p, idx) => ({
      ...p,
      siteId,
      order: idx,
      updatedAt: now,
      createdAt: p.createdAt || now,
    }));

    if (this.isRtdbAvailable() && database) {
      try {
        const pagesMap: Record<string, any> = {};
        for (const p of formattedPages) {
          pagesMap[p.id] = {
            id: p.id,
            siteId,
            title: p.title,
            slug: p.slug,
            seoTitle: p.seoTitle || null,
            seoDescription: p.seoDescription || null,
            isHomePage: p.isHomePage,
            isPublished: p.isPublished !== false,
            isDeleted: Boolean(p.isDeleted),
            order: p.order,
            createdAt: p.createdAt,
            updatedAt: p.updatedAt,
          };
          // Save sections under /siteSections/{siteId}/{pageId}
          await this.savePageSections(siteId, p.id, p.sections || []);
        }
        await set(ref(database, `sitePages/${siteId}`), pagesMap);
      } catch (err) {
        console.warn('RTDB saveSitePages fallback:', err);
      }
    }

    const allPages = this.getLocal<Record<string, SitePage[]>>('sitePages', {});
    allPages[siteId] = formattedPages;
    this.setLocal('sitePages', allPages);
  }

  public async getPageSections(siteId: string, pageId: string): Promise<PageSection[]> {
    if (this.isRtdbAvailable() && database) {
      try {
        let snap;
        try {
          const sectionsQuery = query(ref(database, `siteSections/${siteId}/${pageId}`), orderByChild('order'));
          snap = await get(sectionsQuery);
        } catch {
          snap = await get(ref(database, `siteSections/${siteId}/${pageId}`));
        }
        if (snap.exists()) {
          const raw = snap.val();
          return Object.values(raw as Record<string, PageSection>)
            .filter((sec) => !sec.isDeleted)
            .sort((a, b) => (a.order || 0) - (b.order || 0));
        }
      } catch (err) {
        console.warn('RTDB getPageSections fallback:', err);
      }
    }
    const allSections = this.getLocal<PageSection[]>(`sections_${siteId}_${pageId}`, []);
    const validSections = allSections.filter((s) => !s.isDeleted);
    if (validSections.length > 0) {
      return validSections.sort((a, b) => (a.order || 0) - (b.order || 0));
    }

    // Check if sections exist inside local sitePages or sites
    const allPages = this.getLocal<Record<string, SitePage[]>>('sitePages', {});
    const sitePages = allPages[siteId] || [];
    const foundPage = sitePages.find((p) => p.id === pageId);
    if (foundPage?.sections && foundPage.sections.length > 0) {
      return foundPage.sections.filter((s) => !s.isDeleted).sort((a, b) => (a.order || 0) - (b.order || 0));
    }

    const allSites = this.getLocal<Record<string, SiteData>>('sites', {});
    const s = allSites[siteId];
    const sPage = s?.pages?.find((p) => p.id === pageId);
    if (sPage?.sections && sPage.sections.length > 0) {
      return sPage.sections.filter((sec) => !sec.isDeleted).sort((a, b) => (a.order || 0) - (b.order || 0));
    }

    return [];
  }

  public async savePageSections(siteId: string, pageId: string, sections: PageSection[]): Promise<void> {
    const now = this.getUtcTimestamp();
    const formatted = sections.map((s, idx) => ({
      ...s,
      siteId,
      pageId,
      order: idx,
      updatedAt: now,
      createdAt: s.createdAt || now,
    }));

    if (this.isRtdbAvailable() && database) {
      try {
        const map: Record<string, any> = {};
        for (const s of formatted) {
          map[s.id] = s;
        }
        await set(ref(database, `siteSections/${siteId}/${pageId}`), map);
      } catch (err) {
        console.warn('RTDB savePageSections fallback:', err);
      }
    }
    this.setLocal(`sections_${siteId}_${pageId}`, formatted);
  }

  // ==========================================
  // 6. /siteMedia/{siteId}/{mediaId}
  // ==========================================
  public async getSiteMedia(siteId: string): Promise<MediaAsset[]> {
    if (this.isRtdbAvailable() && database) {
      try {
        let snap;
        try {
          const mediaQuery = query(ref(database, `siteMedia/${siteId}`), orderByChild('uploadedAt'));
          snap = await get(mediaQuery);
        } catch {
          snap = await get(ref(database, `siteMedia/${siteId}`));
        }
        if (snap.exists()) {
          return Object.values(snap.val() as Record<string, MediaAsset>).filter((m) => !m.isDeleted);
        }
      } catch (err) {
        console.warn('RTDB getSiteMedia fallback:', err);
      }
    }
    const allMedia = this.getLocal<Record<string, MediaAsset[]>>('siteMedia', {});
    return (allMedia[siteId] || []).filter((m) => !m.isDeleted);
  }

  public async addMediaAsset(siteId: string, asset: Omit<MediaAsset, 'id' | 'uploadedAt'>): Promise<MediaAsset> {
    const id = this.generateId('media');
    const fullAsset: MediaAsset = {
      ...asset,
      id,
      siteId,
      uploadedAt: this.getUtcTimestamp(),
      isDeleted: false,
    };

    if (this.isRtdbAvailable() && database) {
      try {
        await set(ref(database, `siteMedia/${siteId}/${id}`), fullAsset);
      } catch (err) {
        console.warn('RTDB addMediaAsset fallback:', err);
      }
    }

    const allMedia = this.getLocal<Record<string, MediaAsset[]>>('siteMedia', {});
    if (!allMedia[siteId]) allMedia[siteId] = [];
    allMedia[siteId].push(fullAsset);
    this.setLocal('siteMedia', allMedia);
    return fullAsset;
  }

  public async deleteMediaAsset(siteId: string, mediaId: string): Promise<void> {
    if (this.isRtdbAvailable() && database) {
      try {
        await update(ref(database, `siteMedia/${siteId}/${mediaId}`), {
          isDeleted: true,
        });
      } catch (err) {
        console.warn('RTDB deleteMediaAsset fallback:', err);
      }
    }
    const allMedia = this.getLocal<Record<string, MediaAsset[]>>('siteMedia', {});
    if (allMedia[siteId]) {
      allMedia[siteId] = allMedia[siteId].filter((m) => m.id !== mediaId);
      this.setLocal('siteMedia', allMedia);
    }
  }

  // ==========================================
  // 7. /siteBlogs/{siteId}/{postId}
  // ==========================================
  public async getSiteBlogs(siteId: string, onlyPublished: boolean = false): Promise<BlogPost[]> {
    if (this.isRtdbAvailable() && database) {
      try {
        let snap;
        try {
          const blogQuery = query(ref(database, `siteBlogs/${siteId}`), orderByChild('createdAt'));
          snap = await get(blogQuery);
        } catch {
          snap = await get(ref(database, `siteBlogs/${siteId}`));
        }
        if (snap.exists()) {
          const list = Object.values(snap.val() as Record<string, BlogPost>).filter((b) => !b.isDeleted);
          return onlyPublished ? list.filter((b) => b.status === 'PUBLISHED') : list;
        }
      } catch (err) {
        console.warn('RTDB getSiteBlogs fallback:', err);
      }
    }
    const allBlogs = this.getLocal<Record<string, BlogPost[]>>('siteBlogs', {});
    const list = (allBlogs[siteId] || []).filter((b) => !b.isDeleted);
    return onlyPublished ? list.filter((b) => b.status === 'PUBLISHED') : list;
  }

  public async saveBlogPost(siteId: string, post: Partial<BlogPost>): Promise<BlogPost> {
    const now = this.getUtcTimestamp();
    const id = post.id || this.generateId('post');
    const fullPost: BlogPost = {
      id,
      siteId,
      title: post.title || 'Yeni Blog Yazısı',
      slug: post.slug || id,
      content: post.content || '',
      summary: post.summary || '',
      coverImage: post.coverImage || 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1200&q=80',
      category: post.category || 'Genel',
      tags: post.tags || ['Kurumsal'],
      status: post.status || 'PUBLISHED',
      isDeleted: false,
      publishedAt: post.publishedAt || now,
      createdAt: post.createdAt || now,
      updatedAt: now,
      readTimeMinutes: post.readTimeMinutes || 4,
      authorName: post.authorName || 'Adiosoft Editör',
    };

    if (this.isRtdbAvailable() && database) {
      try {
        await set(ref(database, `siteBlogs/${siteId}/${id}`), fullPost);
      } catch (err) {
        console.warn('RTDB saveBlogPost fallback:', err);
      }
    }

    const allBlogs = this.getLocal<Record<string, BlogPost[]>>('siteBlogs', {});
    if (!allBlogs[siteId]) allBlogs[siteId] = [];
    const index = allBlogs[siteId].findIndex((b) => b.id === id);
    if (index >= 0) allBlogs[siteId][index] = fullPost;
    else allBlogs[siteId].push(fullPost);
    this.setLocal('siteBlogs', allBlogs);
    return fullPost;
  }

  public async deleteBlogPost(siteId: string, postId: string): Promise<void> {
    const now = this.getUtcTimestamp();
    if (this.isRtdbAvailable() && database) {
      try {
        await update(ref(database, `siteBlogs/${siteId}/${postId}`), {
          isDeleted: true,
          updatedAt: now,
        });
      } catch (err) {
        console.warn('RTDB deleteBlogPost fallback:', err);
      }
    }
    const allBlogs = this.getLocal<Record<string, BlogPost[]>>('siteBlogs', {});
    if (allBlogs[siteId]) {
      allBlogs[siteId] = allBlogs[siteId].filter((b) => b.id !== postId);
      this.setLocal('siteBlogs', allBlogs);
    }
  }

  // ==========================================
  // 8. /siteYachts/{siteId}/{yachtId}
  // ==========================================
  public async getSiteYachts(siteId: string): Promise<YachtItem[]> {
    if (this.isRtdbAvailable() && database) {
      try {
        let snap;
        try {
          const yachtQuery = query(ref(database, `siteYachts/${siteId}`), orderByChild('createdAt'));
          snap = await get(yachtQuery);
        } catch {
          snap = await get(ref(database, `siteYachts/${siteId}`));
        }
        if (snap.exists()) {
          return Object.values(snap.val() as Record<string, YachtItem>).filter((y) => !y.isDeleted);
        }
      } catch (err) {
        console.warn('RTDB getSiteYachts fallback:', err);
      }
    }
    const allYachts = this.getLocal<Record<string, YachtItem[]>>('siteYachts', {});
    return (allYachts[siteId] || []).filter((y) => !y.isDeleted);
  }

  public async saveYacht(siteId: string, yacht: Partial<YachtItem>): Promise<YachtItem> {
    const now = this.getUtcTimestamp();
    const id = yacht.id || this.generateId('yacht');
    const fullYacht: YachtItem = {
      id,
      siteId,
      name: yacht.name || 'Lüks Mega Yat',
      slug: yacht.slug || id,
      type: yacht.type || 'Motor Yacht',
      description: yacht.description || '',
      coverImage: yacht.coverImage || 'https://images.unsplash.com/photo-1569263979104-865ab7cd8d17?auto=format&fit=crop&w=1200&q=80',
      gallery: yacht.gallery || [],
      capacity: yacht.capacity || 12,
      cabins: yacht.cabins || 5,
      berths: yacht.berths || 10,
      bathrooms: yacht.bathrooms || 5,
      lengthMeters: yacht.lengthMeters || 36,
      buildYear: yacht.buildYear || 2023,
      location: yacht.location || 'Göcek / Bodrum',
      basePort: yacht.basePort || 'D-Marin Göcek',
      crewCount: yacht.crewCount || 6,
      cruisingSpeedKnots: yacht.cruisingSpeedKnots || 18,
      pricePerDay: yacht.pricePerDay || 4500,
      pricePerWeek: yacht.pricePerWeek || 28000,
      currency: yacht.currency || 'EUR',
      features: yacht.features || ['Flybridge', 'Jakuzi', 'Stabilizer', 'Su Sporları Ekipmanları'],
      specs: yacht.specs || {
        beam: '7.8m',
        draft: '2.1m',
        cruisingSpeed: '18 Knots',
        maxSpeed: '24 Knots',
      },
      amenities: yacht.amenities || ['Klima', 'Wi-Fi', 'Ses Sistemi', 'Jet Ski', 'Seabob'],
      status: yacht.status || 'AVAILABLE',
      isFeatured: Boolean(yacht.isFeatured),
      isStarter: yacht.isStarter !== undefined ? yacht.isStarter : false,
      isDeleted: false,
      createdAt: yacht.createdAt || now,
      updatedAt: now,
    };

    if (this.isRtdbAvailable() && database) {
      try {
        await set(ref(database, `siteYachts/${siteId}/${id}`), fullYacht);
      } catch (err) {
        console.warn('RTDB saveYacht fallback:', err);
      }
    }

    const allYachts = this.getLocal<Record<string, YachtItem[]>>('siteYachts', {});
    if (!allYachts[siteId]) allYachts[siteId] = [];
    const index = allYachts[siteId].findIndex((y) => y.id === id);
    if (index >= 0) allYachts[siteId][index] = fullYacht;
    else allYachts[siteId].push(fullYacht);
    this.setLocal('siteYachts', allYachts);
    return fullYacht;
  }

  public async deleteYacht(siteId: string, yachtId: string): Promise<void> {
    const now = this.getUtcTimestamp();
    if (this.isRtdbAvailable() && database) {
      try {
        await update(ref(database, `siteYachts/${siteId}/${yachtId}`), {
          isDeleted: true,
          updatedAt: now,
        });
      } catch (err) {
        console.warn('RTDB deleteYacht fallback:', err);
      }
    }
    const allYachts = this.getLocal<Record<string, YachtItem[]>>('siteYachts', {});
    if (allYachts[siteId]) {
      allYachts[siteId] = allYachts[siteId].filter((y) => y.id !== yachtId);
      this.setLocal('siteYachts', allYachts);
    }
  }

  // ==========================================
  // 8a. /siteYachtAvailability/{siteId}/{availabilityId}
  // ==========================================
  public async getYachtAvailability(siteId: string, yachtId?: string): Promise<YachtAvailability[]> {
    let list: YachtAvailability[] = [];
    if (this.isRtdbAvailable() && database) {
      try {
        const snap = await get(ref(database, `siteYachtAvailability/${siteId}`));
        if (snap.exists()) {
          const raw = snap.val() as Record<string, YachtAvailability>;
          list = Object.values(raw).filter((a) => !a.isDeleted);
        }
      } catch (err) {
        console.warn('RTDB getYachtAvailability fallback:', err);
      }
    }
    if (list.length === 0) {
      const all = this.getLocal<Record<string, YachtAvailability[]>>('siteYachtAvailability', {});
      list = (all[siteId] || []).filter((a) => !a.isDeleted);
    }
    if (yachtId) {
      return list.filter((a) => a.yachtId === yachtId);
    }
    return list;
  }

  public async saveYachtAvailability(
    siteId: string,
    availability: Partial<YachtAvailability>
  ): Promise<YachtAvailability> {
    const now = this.getUtcTimestamp();
    const id = availability.id || this.generateId('avail');
    const full: YachtAvailability = {
      id,
      siteId,
      yachtId: availability.yachtId || '',
      startDate: availability.startDate || new Date().toISOString().split('T')[0],
      endDate: availability.endDate || new Date().toISOString().split('T')[0],
      status: availability.status || 'BOOKED',
      type: availability.type || 'MANUAL_BLOCK',
      reservationId: availability.reservationId,
      notes: availability.notes || '',
      isDeleted: false,
      createdAt: availability.createdAt || now,
      updatedAt: now,
    };

    if (this.isRtdbAvailable() && database) {
      try {
        await set(ref(database, `siteYachtAvailability/${siteId}/${id}`), full);
      } catch (err) {
        console.warn('RTDB saveYachtAvailability fallback:', err);
      }
    }

    const all = this.getLocal<Record<string, YachtAvailability[]>>('siteYachtAvailability', {});
    if (!all[siteId]) all[siteId] = [];
    const idx = all[siteId].findIndex((a) => a.id === id);
    if (idx >= 0) all[siteId][idx] = full;
    else all[siteId].push(full);
    this.setLocal('siteYachtAvailability', all);
    return full;
  }

  public async deleteYachtAvailability(siteId: string, availabilityId: string): Promise<void> {
    const now = this.getUtcTimestamp();
    if (this.isRtdbAvailable() && database) {
      try {
        await update(ref(database, `siteYachtAvailability/${siteId}/${availabilityId}`), {
          isDeleted: true,
          updatedAt: now,
        });
      } catch (err) {
        console.warn('RTDB deleteYachtAvailability fallback:', err);
      }
    }
    const all = this.getLocal<Record<string, YachtAvailability[]>>('siteYachtAvailability', {});
    if (all[siteId]) {
      all[siteId] = all[siteId].filter((a) => a.id !== availabilityId);
      this.setLocal('siteYachtAvailability', all);
    }
  }

  public async checkYachtAvailability(
    siteId: string,
    yachtId: string,
    startDate: string,
    endDate: string,
    excludeReservationId?: string
  ): Promise<{ available: boolean; reason?: string }> {
    if (!startDate || !endDate) return { available: true };
    if (startDate > endDate) {
      return { available: false, reason: 'Bitiş tarihi başlangıç tarihinden önce olamaz.' };
    }

    // 1. Check Yacht general status
    const yachts = await this.getSiteYachts(siteId);
    const yacht = yachts.find((y) => y.id === yachtId);
    if (yacht && yacht.status === 'MAINTENANCE') {
      return { available: false, reason: 'Yat şu anda teknik bakımda.' };
    }

    // 2. Check stored availability records
    const availabilities = await this.getYachtAvailability(siteId, yachtId);
    for (const av of availabilities) {
      if (av.isDeleted) continue;
      if (excludeReservationId && av.reservationId === excludeReservationId) continue;
      if (av.status === 'AVAILABLE') continue;

      // Overlap check
      if (av.startDate <= endDate && av.endDate >= startDate) {
        const statusLabel = av.status === 'BLOCKED' ? 'Bloke edilmiş' : 'Rezerve';
        return {
          available: false,
          reason: `Seçtiğiniz tarihlerde bu yat ${statusLabel} durumdadır (${av.startDate} - ${av.endDate}).`,
        };
      }
    }

    // 3. Check confirmed reservations directly
    const reservations = await this.getSiteReservations(siteId);
    for (const res of reservations) {
      if (res.isDeleted) continue;
      if (excludeReservationId && res.id === excludeReservationId) continue;
      if (res.yachtId !== yachtId) continue;
      if (res.status === 'CANCELLED') continue;
      if (res.status === 'CONFIRMED' || res.status === 'COMPLETED') {
        if (res.startDate <= endDate && res.endDate >= startDate) {
          return {
            available: false,
            reason: `Bu yat ${res.startDate} - ${res.endDate} tarihleri arasında onaylanmış bir kiralama için rezerve edilmiştir.`,
          };
        }
      }
    }

    return { available: true };
  }

  // ==========================================
  // 8b. /siteRoutes/{siteId}/{routeId}
  // ==========================================
  public async getSiteRoutes(siteId: string): Promise<YachtRoute[]> {
    if (this.isRtdbAvailable() && database) {
      try {
        let snap;
        try {
          const routeQuery = query(ref(database, `siteRoutes/${siteId}`), orderByChild('createdAt'));
          snap = await get(routeQuery);
        } catch {
          snap = await get(ref(database, `siteRoutes/${siteId}`));
        }
        if (snap.exists()) {
          return Object.values(snap.val() as Record<string, YachtRoute>).filter((r) => !r.isDeleted);
        }
      } catch (err) {
        console.warn('RTDB getSiteRoutes fallback:', err);
      }
    }
    const allRoutes = this.getLocal<Record<string, YachtRoute[]>>('siteRoutes', {});
    return (allRoutes[siteId] || []).filter((r) => !r.isDeleted);
  }

  public async saveRoute(siteId: string, route: Partial<YachtRoute>): Promise<YachtRoute> {
    const now = this.getUtcTimestamp();
    const id = route.id || this.generateId('route');
    const fullRoute: YachtRoute = {
      id,
      siteId,
      name: route.name || 'Yeni Mavi Tur Rotası',
      slug: route.slug || id,
      description: route.description || '',
      coverImage: route.coverImage || 'https://images.unsplash.com/photo-1510312305653-8ed496efae75?auto=format&fit=crop&w=1200&q=80',
      durationDays: route.durationDays || 7,
      departurePort: route.departurePort || 'Göcek',
      arrivalPort: route.arrivalPort || 'Göcek',
      highlightBays: route.highlightBays || ['Bedri Rahmi Koyu', 'Kleopatra Hamamı', 'Tersane Adası'],
      itinerary: route.itinerary || [],
      isFeatured: Boolean(route.isFeatured),
      isStarter: route.isStarter !== undefined ? route.isStarter : false,
      isDeleted: false,
      createdAt: route.createdAt || now,
      updatedAt: now,
    };

    if (this.isRtdbAvailable() && database) {
      try {
        await set(ref(database, `siteRoutes/${siteId}/${id}`), fullRoute);
      } catch (err) {
        console.warn('RTDB saveRoute fallback:', err);
      }
    }

    const allRoutes = this.getLocal<Record<string, YachtRoute[]>>('siteRoutes', {});
    if (!allRoutes[siteId]) allRoutes[siteId] = [];
    const index = allRoutes[siteId].findIndex((r) => r.id === id);
    if (index >= 0) allRoutes[siteId][index] = fullRoute;
    else allRoutes[siteId].push(fullRoute);
    this.setLocal('siteRoutes', allRoutes);
    return fullRoute;
  }

  public async deleteRoute(siteId: string, routeId: string): Promise<void> {
    const now = this.getUtcTimestamp();
    if (this.isRtdbAvailable() && database) {
      try {
        await update(ref(database, `siteRoutes/${siteId}/${routeId}`), {
          isDeleted: true,
          updatedAt: now,
        });
      } catch (err) {
        console.warn('RTDB deleteRoute fallback:', err);
      }
    }
    const allRoutes = this.getLocal<Record<string, YachtRoute[]>>('siteRoutes', {});
    if (allRoutes[siteId]) {
      allRoutes[siteId] = allRoutes[siteId].filter((r) => r.id !== routeId);
      this.setLocal('siteRoutes', allRoutes);
    }
  }

  // ==========================================
  // 8c. /siteMarinas/{siteId}/{marinaId}
  // ==========================================
  public async getSiteMarinas(siteId: string): Promise<YachtMarina[]> {
    if (this.isRtdbAvailable() && database) {
      try {
        let snap;
        try {
          const marinaQuery = query(ref(database, `siteMarinas/${siteId}`), orderByChild('createdAt'));
          snap = await get(marinaQuery);
        } catch {
          snap = await get(ref(database, `siteMarinas/${siteId}`));
        }
        if (snap.exists()) {
          return Object.values(snap.val() as Record<string, YachtMarina>).filter((m) => !m.isDeleted);
        }
      } catch (err) {
        console.warn('RTDB getSiteMarinas fallback:', err);
      }
    }
    const allMarinas = this.getLocal<Record<string, YachtMarina[]>>('siteMarinas', {});
    return (allMarinas[siteId] || []).filter((m) => !m.isDeleted);
  }

  public async saveMarina(siteId: string, marina: Partial<YachtMarina>): Promise<YachtMarina> {
    const now = this.getUtcTimestamp();
    const id = marina.id || this.generateId('marina');
    const fullMarina: YachtMarina = {
      id,
      siteId,
      name: marina.name || 'Yeni Marina',
      slug: marina.slug || id,
      location: marina.location || 'Muğla / Türkiye',
      description: marina.description || '',
      coverImage: marina.coverImage || 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?auto=format&fit=crop&w=1200&q=80',
      coordinates: marina.coordinates || '36°45\'N 28°56\'E',
      berthCapacity: marina.berthCapacity || 400,
      maxDraft: marina.maxDraft || '6m',
      maxLoa: marina.maxLoa || '70m',
      services: marina.services || ['24/7 Güvenlik', 'Elektrik & Su', 'Yakıt İkmal İstasyonu', 'VIP Lounge', 'Teknik Servis'],
      isFeatured: Boolean(marina.isFeatured),
      isStarter: marina.isStarter !== undefined ? marina.isStarter : false,
      isDeleted: false,
      createdAt: marina.createdAt || now,
      updatedAt: now,
    };

    if (this.isRtdbAvailable() && database) {
      try {
        await set(ref(database, `siteMarinas/${siteId}/${id}`), fullMarina);
      } catch (err) {
        console.warn('RTDB saveMarina fallback:', err);
      }
    }

    const allMarinas = this.getLocal<Record<string, YachtMarina[]>>('siteMarinas', {});
    if (!allMarinas[siteId]) allMarinas[siteId] = [];
    const index = allMarinas[siteId].findIndex((m) => m.id === id);
    if (index >= 0) allMarinas[siteId][index] = fullMarina;
    else allMarinas[siteId].push(fullMarina);
    this.setLocal('siteMarinas', allMarinas);
    return fullMarina;
  }

  public async deleteMarina(siteId: string, marinaId: string): Promise<void> {
    const now = this.getUtcTimestamp();
    if (this.isRtdbAvailable() && database) {
      try {
        await update(ref(database, `siteMarinas/${siteId}/${marinaId}`), {
          isDeleted: true,
          updatedAt: now,
        });
      } catch (err) {
        console.warn('RTDB deleteMarina fallback:', err);
      }
    }
    const allMarinas = this.getLocal<Record<string, YachtMarina[]>>('siteMarinas', {});
    if (allMarinas[siteId]) {
      allMarinas[siteId] = allMarinas[siteId].filter((m) => m.id !== marinaId);
      this.setLocal('siteMarinas', allMarinas);
    }
  }

  // ==========================================
  // 9. /siteReservations/{siteId}/{reservationId}
  // ==========================================
  public async getSiteReservations(siteId: string): Promise<YachtReservation[]> {
    if (this.isRtdbAvailable() && database) {
      try {
        let snap;
        try {
          const resQuery = query(ref(database, `siteReservations/${siteId}`), orderByChild('createdAt'));
          snap = await get(resQuery);
        } catch {
          snap = await get(ref(database, `siteReservations/${siteId}`));
        }
        if (snap.exists()) {
          return Object.values(snap.val() as Record<string, YachtReservation>).filter((r) => !r.isDeleted);
        }
      } catch (err) {
        console.warn('RTDB getSiteReservations fallback:', err);
      }
    }
    const allRes = this.getLocal<Record<string, YachtReservation[]>>('siteReservations', {});
    return (allRes[siteId] || []).filter((r) => !r.isDeleted);
  }

  public async createReservationInquiry(
    siteId: string,
    data: Omit<YachtReservation, 'id' | 'createdAt' | 'status'>
  ): Promise<YachtReservation> {
    const now = this.getUtcTimestamp();
    const id = this.generateId('res');
    const newReservation: YachtReservation = {
      ...data,
      id,
      siteId,
      status: 'PENDING',
      isDeleted: false,
      createdAt: now,
      updatedAt: now,
    };

    if (this.isRtdbAvailable() && database) {
      try {
        await set(ref(database, `siteReservations/${siteId}/${id}`), newReservation);
      } catch (err) {
        console.warn('RTDB createReservationInquiry fallback:', err);
      }
    }

    const allRes = this.getLocal<Record<string, YachtReservation[]>>('siteReservations', {});
    if (!allRes[siteId]) allRes[siteId] = [];
    allRes[siteId].push(newReservation);
    this.setLocal('siteReservations', allRes);
    return newReservation;
  }

  public async updateReservationStatus(
    siteId: string,
    reservationId: string,
    status: YachtReservation['status'],
    adminNotes?: string
  ): Promise<void> {
    const now = this.getUtcTimestamp();
    if (this.isRtdbAvailable() && database) {
      try {
        await update(ref(database, `siteReservations/${siteId}/${reservationId}`), {
          status,
          adminNotes: adminNotes || null,
          updatedAt: now,
        });
      } catch (err) {
        console.warn('RTDB updateReservationStatus fallback:', err);
      }
    }
    const allRes = this.getLocal<Record<string, YachtReservation[]>>('siteReservations', {});
    let target: YachtReservation | undefined;
    if (allRes[siteId]) {
      target = allRes[siteId].find((r) => r.id === reservationId);
      if (target) {
        target.status = status;
        if (adminNotes) target.adminNotes = adminNotes;
        target.updatedAt = now;
        this.setLocal('siteReservations', allRes);
      }
    }

    // Availability Sync
    if (target && target.yachtId && target.yachtId !== 'fleet-general') {
      if (status === 'CONFIRMED') {
        await this.saveYachtAvailability(siteId, {
          id: `avail_res_${reservationId}`,
          siteId,
          yachtId: target.yachtId,
          startDate: target.startDate,
          endDate: target.endDate,
          status: 'BOOKED',
          type: 'RESERVATION',
          reservationId: target.id,
          notes: `${target.customerName} - Rezervasyon #${reservationId.slice(-6)}`,
        });
      } else if (status === 'CANCELLED') {
        const availabilities = await this.getYachtAvailability(siteId, target.yachtId);
        for (const av of availabilities) {
          if (av.reservationId === reservationId && av.type === 'RESERVATION') {
            await this.deleteYachtAvailability(siteId, av.id);
          }
        }
      }
    }
  }

  // ==========================================
  // 9b. /siteCommunications/{siteId}/{communicationId}
  // ==========================================
  public async getSiteCommunications(siteId: string): Promise<SiteCommunication[]> {
    if (this.isRtdbAvailable() && database) {
      try {
        let snap;
        try {
          const commQuery = query(ref(database, `siteCommunications/${siteId}`), orderByChild('createdAt'));
          snap = await get(commQuery);
        } catch {
          snap = await get(ref(database, `siteCommunications/${siteId}`));
        }
        if (snap.exists()) {
          const raw = snap.val() as Record<string, SiteCommunication>;
          const list = Object.entries(raw).map(([key, val]) => ({
            ...val,
            id: val?.id || key,
          })).filter((c) => !c.isDeleted);
          return list.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
        }
      } catch (err) {
        console.warn('RTDB getSiteCommunications fallback:', err);
      }
    }
    const allComm = this.getLocal<Record<string, SiteCommunication[]>>('siteCommunications', {});
    return (allComm[siteId] || []).filter((c) => !c.isDeleted).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  }

  public async createSiteCommunication(
    siteId: string,
    data: Omit<SiteCommunication, 'id' | 'createdAt' | 'status'> & { status?: CommunicationStatus }
  ): Promise<SiteCommunication> {
    const now = this.getUtcTimestamp();
    const id = this.generateId('msg');
    const newCommunication: SiteCommunication = {
      ...data,
      id,
      siteId,
      status: data.status || 'NEW',
      isDeleted: false,
      createdAt: now,
      updatedAt: now,
    };

    if (this.isRtdbAvailable() && database) {
      try {
        await set(ref(database, `siteCommunications/${siteId}/${id}`), newCommunication);
      } catch (err) {
        console.warn('RTDB createSiteCommunication fallback:', err);
      }
    }

    const allComm = this.getLocal<Record<string, SiteCommunication[]>>('siteCommunications', {});
    if (!allComm[siteId]) allComm[siteId] = [];
    allComm[siteId].unshift(newCommunication);
    this.setLocal('siteCommunications', allComm);
    return newCommunication;
  }

  public async updateCommunicationStatus(
    siteId: string,
    communicationId: string,
    status: CommunicationStatus,
    adminNotes?: string
  ): Promise<void> {
    const now = this.getUtcTimestamp();
    if (this.isRtdbAvailable() && database) {
      try {
        await update(ref(database, `siteCommunications/${siteId}/${communicationId}`), {
          status,
          adminNotes: adminNotes !== undefined ? adminNotes : null,
          updatedAt: now,
        });
      } catch (err) {
        console.warn('RTDB updateCommunicationStatus fallback:', err);
      }
    }
    const allComm = this.getLocal<Record<string, SiteCommunication[]>>('siteCommunications', {});
    if (allComm[siteId]) {
      const target = allComm[siteId].find((c) => c.id === communicationId);
      if (target) {
        target.status = status;
        if (adminNotes !== undefined) target.adminNotes = adminNotes;
        target.updatedAt = now;
        this.setLocal('siteCommunications', allComm);
      }
    }
  }

  public async deleteCommunication(siteId: string, communicationId: string): Promise<void> {
    const now = this.getUtcTimestamp();
    if (this.isRtdbAvailable() && database) {
      try {
        await update(ref(database, `siteCommunications/${siteId}/${communicationId}`), {
          isDeleted: true,
          updatedAt: now,
        });
      } catch (err) {
        console.warn('RTDB deleteCommunication fallback:', err);
      }
    }
    const allComm = this.getLocal<Record<string, SiteCommunication[]>>('siteCommunications', {});
    if (allComm[siteId]) {
      allComm[siteId] = allComm[siteId].filter((c) => c.id !== communicationId);
      this.setLocal('siteCommunications', allComm);
    }
  }

  // ==========================================
  // 10. /siteDomains/{siteId}
  // ==========================================
  public async getSiteDomains(siteId: string): Promise<SiteDomain[]> {
    if (this.isRtdbAvailable() && database) {
      try {
        const snap = await get(ref(database, `siteDomains/${siteId}`));
        if (snap.exists()) {
          const val = snap.val();
          return Array.isArray(val) ? val : [val];
        }
      } catch (err) {
        console.warn('RTDB getSiteDomains fallback:', err);
      }
    }
    const allDomains = this.getLocal<Record<string, SiteDomain[]>>('siteDomains', {});
    return allDomains[siteId] || [];
  }

  public async saveSiteDomains(siteId: string, domains: SiteDomain[]): Promise<void> {
    const now = this.getUtcTimestamp();
    const formatted = domains.map((d) => ({
      ...d,
      siteId,
      updatedAt: now,
      createdAt: d.createdAt || now,
    }));
    if (this.isRtdbAvailable() && database) {
      try {
        await set(ref(database, `siteDomains/${siteId}`), formatted);
      } catch (err) {
        console.warn('RTDB saveSiteDomains fallback:', err);
      }
    }
    const allDomains = this.getLocal<Record<string, SiteDomain[]>>('siteDomains', {});
    allDomains[siteId] = formatted;
    this.setLocal('siteDomains', allDomains);
  }

  // ==========================================
  // /domainMappings/{normalizedDomain}
  // ==========================================
  public async getDomainMapping(normalizedDomain: string): Promise<DomainMapping | null> {
    const clean = normalizeDomain(normalizedDomain);
    const key = clean.replace(/\./g, '_dot_');

    if (this.isRtdbAvailable() && database) {
      try {
        const snap = await get(ref(database, `domainMappings/${key}`));
        if (snap.exists()) {
          return snap.val() as DomainMapping;
        }
      } catch (err) {
        console.warn('RTDB getDomainMapping fallback:', err);
      }
    }
    const allMappings = this.getLocal<Record<string, DomainMapping>>('domainMappings', {});
    return allMappings[clean] || null;
  }

  public async saveDomainMapping(mapping: DomainMapping): Promise<DomainMapping> {
    const clean = normalizeDomain(mapping.normalizedDomain);
    const key = clean.replace(/\./g, '_dot_');
    const now = this.getUtcTimestamp();
    const payload: DomainMapping = {
      ...mapping,
      normalizedDomain: clean,
      updatedAt: now,
      createdAt: mapping.createdAt || now,
    };

    if (this.isRtdbAvailable() && database) {
      try {
        await set(ref(database, `domainMappings/${key}`), payload);
      } catch (err) {
        console.warn('RTDB saveDomainMapping fallback:', err);
      }
    }
    const allMappings = this.getLocal<Record<string, DomainMapping>>('domainMappings', {});
    allMappings[clean] = payload;
    this.setLocal('domainMappings', allMappings);
    return payload;
  }

  public async removeDomainMapping(normalizedDomain: string): Promise<void> {
    const clean = normalizeDomain(normalizedDomain);
    const key = clean.replace(/\./g, '_dot_');

    if (this.isRtdbAvailable() && database) {
      try {
        await remove(ref(database, `domainMappings/${key}`));
      } catch (err) {
        console.warn('RTDB removeDomainMapping fallback:', err);
      }
    }
    const allMappings = this.getLocal<Record<string, DomainMapping>>('domainMappings', {});
    delete allMappings[clean];
    this.setLocal('domainMappings', allMappings);
  }

  // ==========================================
  // /backgroundJobs/{jobId}
  // ==========================================
  public async saveBackgroundJob(job: BackgroundJob): Promise<BackgroundJob> {
    if (this.isRtdbAvailable() && database) {
      try {
        await set(ref(database, `backgroundJobs/${job.jobId}`), job);
      } catch (err) {
        console.warn('RTDB saveBackgroundJob fallback:', err);
      }
    }
    const allJobs = this.getLocal<Record<string, BackgroundJob>>('backgroundJobs', {});
    allJobs[job.jobId] = job;
    this.setLocal('backgroundJobs', allJobs);
    return job;
  }

  public async getPendingJobs(): Promise<BackgroundJob[]> {
    if (this.isRtdbAvailable() && database) {
      try {
        let snap;
        try {
          const jobsQuery = query(ref(database, 'backgroundJobs'), orderByChild('status'), equalTo('PENDING'));
          snap = await get(jobsQuery);
        } catch {
          snap = await get(ref(database, 'backgroundJobs'));
        }
        if (snap.exists()) {
          return Object.values(snap.val() as Record<string, BackgroundJob>).filter((j) => j?.status === 'PENDING');
        }
      } catch (err) {
        console.warn('RTDB getPendingJobs fallback:', err);
      }
    }
    const allJobs = this.getLocal<Record<string, BackgroundJob>>('backgroundJobs', {});
    return Object.values(allJobs).filter((j) => j.status === 'PENDING');
  }

  public async getAllBackgroundJobs(): Promise<BackgroundJob[]> {
    if (this.isRtdbAvailable() && database) {
      try {
        const snap = await get(ref(database, 'backgroundJobs'));
        if (snap.exists()) {
          return Object.values(snap.val() as Record<string, BackgroundJob>).sort((a, b) => b.createdAt - a.createdAt);
        }
      } catch (err) {
        console.warn('RTDB getAllBackgroundJobs fallback:', err);
      }
    }
    const allJobs = this.getLocal<Record<string, BackgroundJob>>('backgroundJobs', {});
    return Object.values(allJobs).sort((a, b) => b.createdAt - a.createdAt);
  }

  // ==========================================
  // 11. /siteSubscriptions/{siteId}
  // ==========================================
  public async getSiteSubscription(siteId: string): Promise<SiteSubscription | null> {
    if (this.isRtdbAvailable() && database) {
      try {
        const snap = await get(ref(database, `siteSubscriptions/${siteId}`));
        if (snap.exists()) return snap.val() as SiteSubscription;
      } catch (err) {
        console.warn('RTDB getSiteSubscription fallback:', err);
      }
    }
    const allSubs = this.getLocal<Record<string, SiteSubscription>>('siteSubscriptions', {});
    return allSubs[siteId] || null;
  }

  public async saveSiteSubscription(siteId: string, subscription: SiteSubscription): Promise<void> {
    const payload = {
      ...subscription,
      siteId,
      updatedAt: this.getUtcTimestamp(),
    };
    if (this.isRtdbAvailable() && database) {
      try {
        await set(ref(database, `siteSubscriptions/${siteId}`), payload);
      } catch (err) {
        console.warn('RTDB saveSiteSubscription fallback:', err);
      }
    }
    const allSubs = this.getLocal<Record<string, SiteSubscription>>('siteSubscriptions', {});
    allSubs[siteId] = payload;
    this.setLocal('siteSubscriptions', allSubs);
  }

  public async saveSite(site: SiteData): Promise<SiteData> {
    return this.updateSite(site);
  }

  public async getPaymentTransactions(): Promise<ShopierTransaction[]> {
    if (this.isRtdbAvailable() && database) {
      try {
        const snap = await get(ref(database, 'transactions'));
        if (snap.exists()) {
          return Object.values(snap.val() as Record<string, ShopierTransaction>);
        }
      } catch (err) {
        console.warn('RTDB getPaymentTransactions fallback:', err);
      }
    }
    return this.getLocal<ShopierTransaction[]>('transactions', []);
  }

  public async savePaymentTransaction(tx: ShopierTransaction): Promise<void> {
    if (this.isRtdbAvailable() && database) {
      try {
        await set(ref(database, `transactions/${tx.id}`), tx);
      } catch (err) {
        console.warn('RTDB savePaymentTransaction fallback:', err);
      }
    }
    const allTx = this.getLocal<ShopierTransaction[]>('transactions', []);
    allTx.unshift(tx);
    this.setLocal('transactions', allTx);
  }

  public async getAllPayments(): Promise<PaymentRecord[]> {
    if (this.isRtdbAvailable() && database) {
      try {
        const snap = await get(ref(database, 'payments'));
        if (snap.exists()) {
          return Object.values(snap.val() as Record<string, PaymentRecord>);
        }
      } catch (err) {
        console.warn('RTDB getAllPayments fallback:', err);
      }
    }
    const allPayments = this.getLocal<Record<string, PaymentRecord>>('paymentRecords', {});
    return Object.values(allPayments);
  }

  public async getAllSiteSubscriptions(): Promise<SiteSubscription[]> {
    if (this.isRtdbAvailable() && database) {
      try {
        const snap = await get(ref(database, 'siteSubscriptions'));
        if (snap.exists()) {
          return Object.values(snap.val() as Record<string, SiteSubscription>);
        }
      } catch (err) {
        console.warn('RTDB getAllSiteSubscriptions fallback:', err);
      }
    }
    const allSubs = this.getLocal<Record<string, SiteSubscription>>('siteSubscriptions', {});
    return Object.values(allSubs);
  }

  public async getAllContactMessages(): Promise<any[]> {
    if (this.isRtdbAvailable() && database) {
      try {
        const snap = await get(ref(database, 'contactMessages'));
        if (snap.exists()) {
          return Object.values(snap.val());
        }
      } catch (err) {
        console.warn('RTDB getAllContactMessages fallback:', err);
      }
    }
    return this.getLocal<any[]>('contactMessages', []);
  }

  // ==========================================
  // Marketing Site Admin Messages (adminMessages)
  // Dedicated to Adiosoft marketing contact inquiries - isolated from customer tenant messages
  // ==========================================
  public async saveAdminMessage(message: any): Promise<void> {
    const id = message.id || `msg_mkt_${Date.now()}`;
    if (this.isRtdbAvailable() && database) {
      try {
        await set(ref(database, `adminMessages/${id}`), message);
      } catch (err) {
        console.warn('RTDB saveAdminMessage fallback:', err);
      }
    }
    const all = this.getLocal<Record<string, any>>('adminMessages', {});
    all[id] = message;
    this.setLocal('adminMessages', all);
  }

  public async getAllAdminMessages(): Promise<any[]> {
    if (this.isRtdbAvailable() && database) {
      try {
        const snap = await get(ref(database, 'adminMessages'));
        if (snap.exists()) {
          return Object.values(snap.val());
        }
      } catch (err) {
        console.warn('RTDB getAllAdminMessages fallback:', err);
      }
    }
    const all = this.getLocal<Record<string, any>>('adminMessages', {});
    return Object.values(all);
  }

  public async updateAdminMessageStatus(
    messageId: string,
    status: 'NEW' | 'READ' | 'REPLIED' | 'ARCHIVED'
  ): Promise<void> {
    const now = this.getUtcTimestamp();
    const updateData: Record<string, any> = {
      status,
      updatedAt: now,
    };
    if (status === 'READ') {
      updateData.readAt = now;
    }

    if (this.isRtdbAvailable() && database) {
      try {
        await update(ref(database, `adminMessages/${messageId}`), updateData);
      } catch (err) {
        console.warn('RTDB updateAdminMessageStatus fallback:', err);
      }
    }

    const all = this.getLocal<Record<string, any>>('adminMessages', {});
    if (all[messageId]) {
      all[messageId] = { ...all[messageId], ...updateData };
      this.setLocal('adminMessages', all);
    }
  }

  public async deleteAdminMessage(messageId: string): Promise<void> {
    if (this.isRtdbAvailable() && database) {
      try {
        await set(ref(database, `adminMessages/${messageId}`), null);
      } catch (err) {
        console.warn('RTDB deleteAdminMessage fallback:', err);
      }
    }
    const all = this.getLocal<Record<string, any>>('adminMessages', {});
    delete all[messageId];
    this.setLocal('adminMessages', all);
  }

  public async getPlatformSettings(): Promise<{
    platformName: string;
    platformDomain: string;
    adminEmail: string;
    googleAdsManagerEmail: string;
    googleAdsDirectOAuthEnabled: boolean;
    googleAdsReportingEnabled: boolean;
  }> {
    const defaults = {
      platformName: 'Adiosoft',
      platformDomain: 'adiosoft.com',
      adminEmail: 'admin@adiosoft.com',
      googleAdsManagerEmail: 'adiosoftcom@gmail.com',
      googleAdsDirectOAuthEnabled: false,
      googleAdsReportingEnabled: false,
    };
    if (this.isRtdbAvailable() && database) {
      try {
        const snap = await get(ref(database, 'settings/platform'));
        if (snap.exists()) {
          return { ...defaults, ...snap.val() };
        }
      } catch (err) {
        console.warn('RTDB getPlatformSettings fallback:', err);
      }
    }
    const local = this.getLocal<any>('platformSettings', defaults);
    return { ...defaults, ...local };
  }

  public async savePlatformSettings(settings: Partial<{
    platformName: string;
    platformDomain: string;
    adminEmail: string;
    googleAdsManagerEmail: string;
    googleAdsDirectOAuthEnabled: boolean;
    googleAdsReportingEnabled: boolean;
  }>): Promise<any> {
    const current = await this.getPlatformSettings();
    const updated = { ...current, ...settings };
    if (this.isRtdbAvailable() && database) {
      try {
        await set(ref(database, 'settings/platform'), updated);
      } catch (err) {
        console.warn('RTDB savePlatformSettings fallback:', err);
      }
    }
    this.setLocal('platformSettings', updated);
    return updated;
  }

  public async saveAdminProfile(profile: any): Promise<void> {
    if (this.isRtdbAvailable() && database) {
      try {
        await set(ref(database, `admins/${profile.uid}`), profile);
      } catch (err) {
        console.warn('RTDB saveAdminProfile fallback:', err);
      }
    }
    const admins = this.getLocal<Record<string, any>>('admins', {});
    admins[profile.uid] = profile;
    this.setLocal('admins', admins);
  }

  public async getAdminProfile(uid: string): Promise<any | null> {
    if (this.isRtdbAvailable() && database) {
      try {
        const snap = await get(ref(database, `admins/${uid}`));
        if (snap.exists()) return snap.val();
      } catch (err) {
        console.warn('RTDB getAdminProfile fallback:', err);
      }
    }
    const admins = this.getLocal<Record<string, any>>('admins', {});
    return admins[uid] || null;
  }

  // ==========================================
  // Billing Orders Management
  // ==========================================
  public async saveOrder(order: BillingOrder): Promise<void> {
    const payload: BillingOrder = {
      ...order,
      updatedAt: this.getUtcTimestamp(),
    };

    if (this.isRtdbAvailable() && database) {
      try {
        const sanitized = this.cleanForRtdb(payload);
        await set(ref(database, `billingOrders/${order.id}`), sanitized);
      } catch (err) {
        console.warn('RTDB saveOrder warning:', err);
      }
    }

    const allOrders = this.getLocal<Record<string, BillingOrder>>('billingOrders', {});
    allOrders[order.id] = payload;
    this.setLocal('billingOrders', allOrders);
  }

  public async getOrderById(orderId: string): Promise<BillingOrder | null> {
    if (this.isRtdbAvailable() && database) {
      try {
        const snap = await get(ref(database, `billingOrders/${orderId}`));
        if (snap.exists()) {
          return snap.val() as BillingOrder;
        }
      } catch (err) {
        console.warn('RTDB getOrderById warning:', err);
      }
    }

    const allOrders = this.getLocal<Record<string, BillingOrder>>('billingOrders', {});
    return allOrders[orderId] || null;
  }

  public async getOrderByShopierOrderId(shopierOrderId: string): Promise<BillingOrder | null> {
    const allOrders = await this.getAllOrders();
    return allOrders.find((o) => o.shopierOrderId === shopierOrderId) || null;
  }

  public async getOrderByShopierProductId(shopierProductId: string): Promise<BillingOrder | null> {
    const allOrders = await this.getAllOrders();
    return allOrders.find((o) => o.shopierProductId === shopierProductId) || null;
  }

  public async getOrdersByUser(userId: string): Promise<BillingOrder[]> {
    const allOrders = await this.getAllOrders();
    return allOrders
      .filter((o) => o.userId === userId)
      .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  }

  public async getAllOrders(): Promise<BillingOrder[]> {
    if (this.isRtdbAvailable() && database) {
      try {
        const snap = await get(ref(database, 'billingOrders'));
        if (snap.exists()) {
          const val = snap.val();
          return Object.values(val) as BillingOrder[];
        }
      } catch (err) {
        console.warn('RTDB getAllOrders warning:', err);
      }
    }

    const allOrders = this.getLocal<Record<string, BillingOrder>>('billingOrders', {});
    return Object.values(allOrders).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  }

  public async updateOrderStatus(
    orderId: string,
    status: OrderStatus,
    details?: Partial<BillingOrder>
  ): Promise<BillingOrder | null> {
    const existing = await this.getOrderById(orderId);
    if (!existing) return null;

    const updated: BillingOrder = {
      ...existing,
      ...details,
      status,
      updatedAt: this.getUtcTimestamp(),
      paidAt: status === 'PAID' ? (details?.paidAt || this.getUtcTimestamp()) : existing.paidAt,
    };

    await this.saveOrder(updated);
    return updated;
  }

  public async saveReservation(siteId: string, reservation: Partial<YachtReservation>): Promise<YachtReservation> {
    const now = this.getUtcTimestamp();
    const id = reservation.id || this.generateId('res');
    const fullRes: YachtReservation = {
      id,
      siteId,
      customerName: reservation.customerName || 'Misafir',
      customerEmail: reservation.customerEmail || 'misafir@example.com',
      customerPhone: reservation.customerPhone || '',
      startDate: reservation.startDate || new Date().toISOString().split('T')[0],
      endDate: reservation.endDate || new Date().toISOString().split('T')[0],
      guestCount: reservation.guestCount || 2,
      yachtId: reservation.yachtId,
      yachtName: reservation.yachtName,
      totalEstimatedPrice: reservation.totalEstimatedPrice,
      currency: reservation.currency || 'EUR',
      budget: reservation.budget,
      notes: reservation.notes,
      message: reservation.message,
      status: reservation.status || 'PENDING',
      adminNotes: reservation.adminNotes,
      isDeleted: false,
      createdAt: reservation.createdAt || now,
      updatedAt: now,
    };

    if (this.isRtdbAvailable() && database) {
      try {
        await set(ref(database, `siteReservations/${siteId}/${id}`), fullRes);
      } catch (err) {
        console.warn('RTDB saveReservation fallback:', err);
      }
    }

    const allRes = this.getLocal<Record<string, YachtReservation[]>>('siteReservations', {});
    if (!allRes[siteId]) allRes[siteId] = [];
    const idx = allRes[siteId].findIndex((r) => r.id === id);
    if (idx >= 0) allRes[siteId][idx] = fullRes;
    else allRes[siteId].push(fullRes);
    this.setLocal('siteReservations', allRes);
    return fullRes;
  }

  public async deleteReservation(siteId: string, reservationId: string): Promise<void> {
    const now = this.getUtcTimestamp();
    if (this.isRtdbAvailable() && database) {
      try {
        await update(ref(database, `siteReservations/${siteId}/${reservationId}`), {
          isDeleted: true,
          updatedAt: now,
        });
      } catch (err) {
        console.warn('RTDB deleteReservation fallback:', err);
      }
    }
    const allRes = this.getLocal<Record<string, YachtReservation[]>>('siteReservations', {});
    if (allRes[siteId]) {
      const target = allRes[siteId].find((r) => r.id === reservationId);
      if (target?.yachtId) {
        const availabilities = await this.getYachtAvailability(siteId, target.yachtId);
        for (const av of availabilities) {
          if (av.reservationId === reservationId && av.type === 'RESERVATION') {
            await this.deleteYachtAvailability(siteId, av.id);
          }
        }
      }
      allRes[siteId] = allRes[siteId].filter((r) => r.id !== reservationId);
      this.setLocal('siteReservations', allRes);
    }
  }

  // ==========================================
  // 12. /siteAnalytics/{siteId}
  // ==========================================
  public async getSiteAnalytics(siteId: string): Promise<SiteAnalytics> {
    let current: SiteAnalytics | null = null;
    if (this.isRtdbAvailable() && database) {
      try {
        const snap = await get(ref(database, `siteAnalytics/${siteId}`));
        if (snap.exists()) {
          current = snap.val() as SiteAnalytics;
        }
      } catch (err) {
        console.warn('RTDB getSiteAnalytics fallback:', err);
      }
    }
    if (!current) {
      const allAnalytics = this.getLocal<Record<string, SiteAnalytics>>('siteAnalytics', {});
      current = allAnalytics[siteId] || null;
    }

    // Real business data: load actual inquiries from database
    const reservations = await this.getSiteReservations(siteId);
    const communications = await this.getSiteCommunications(siteId);
    const realInquiries = (reservations?.length || 0) + (communications?.length || 0);

    const totalViews = current?.totalViews || 0;
    const uniqueVisitors = current?.uniqueVisitors || (totalViews > 0 ? Math.max(1, Math.round(totalViews * 0.75)) : 0);

    return {
      siteId,
      totalViews,
      uniqueVisitors,
      totalInquiries: realInquiries,
      lastUpdated: current?.lastUpdated || this.getUtcTimestamp(),
    };
  }

  public async incrementSiteViewCount(siteId: string): Promise<number> {
    const allAnalytics = this.getLocal<Record<string, SiteAnalytics>>('siteAnalytics', {});
    const current = allAnalytics[siteId] || {
      siteId,
      totalViews: 0,
      uniqueVisitors: 0,
      totalInquiries: 0,
      lastUpdated: this.getUtcTimestamp(),
    };
    current.totalViews = (current.totalViews || 0) + 1;
    current.uniqueVisitors = Math.max(1, Math.round(current.totalViews * 0.75));
    current.lastUpdated = this.getUtcTimestamp();
    allAnalytics[siteId] = current;
    this.setLocal('siteAnalytics', allAnalytics);

    if (this.isRtdbAvailable() && database) {
      try {
        await update(ref(database, `siteAnalytics/${siteId}`), {
          totalViews: current.totalViews,
          uniqueVisitors: current.uniqueVisitors,
          lastUpdated: current.lastUpdated,
        });
      } catch (err) {
        console.warn('RTDB incrementSiteViewCount fallback:', err);
      }
    }
    return current.totalViews;
  }

  public async initializeAnalytics(siteId: string): Promise<void> {
    const payload: SiteAnalytics = {
      siteId,
      totalViews: 0,
      uniqueVisitors: 0,
      totalInquiries: 0,
      lastUpdated: this.getUtcTimestamp(),
    };
    if (this.isRtdbAvailable() && database) {
      try {
        await set(ref(database, `siteAnalytics/${siteId}`), payload);
      } catch (err) {
        console.warn('RTDB initializeAnalytics fallback:', err);
      }
    }
    const allAnalytics = this.getLocal<Record<string, SiteAnalytics>>('siteAnalytics', {});
    allAnalytics[siteId] = payload;
    this.setLocal('siteAnalytics', allAnalytics);
  }

  // ==========================================
  // 13. /templates/{templateId} & /industries/{industryId}
  // ==========================================
  public async getTemplates(): Promise<TemplateDefinition[]> {
    if (this.isRtdbAvailable() && database) {
      try {
        const snap = await get(ref(database, 'templates'));
        if (snap.exists()) return Object.values(snap.val());
      } catch (err) {
        console.warn('RTDB getTemplates fallback:', err);
      }
    }
    return TEMPLATES;
  }

  public async getIndustries(): Promise<IndustryDefinition[]> {
    return [
      {
        id: 'ind-corp',
        code: 'CORPORATE',
        name: 'Kurumsal & Holding',
        description: 'B2B, holding, müşavirlik ve kurumsal şirketler için tasarlanmış yüksek prestijli modüller.',
        icon: 'Building2',
        features: ['Hizmet & Çözüm Kataloğu', 'Ekip & Kariyer', 'Kurumsal Blog', 'Çoklu Dil & SEO'],
        defaultTemplates: ['corp-apex', 'corp-modern'],
        isAvailable: true,
      },
      {
        id: 'ind-yacht',
        code: 'YACHT_RENTAL',
        name: 'Lüks Yat Kiralama & Filo',
        description: 'Charter ve yat kiralama şirketleri için interaktif filo, takvim, teknik özellikler ve talep yönetimi.',
        icon: 'Anchor',
        features: ['Yat Filosu Kataloğu', 'Müsaitlik Takvimi', 'VIP Rezervasyon Formu', 'Teknik Şartname Kartları'],
        defaultTemplates: ['yacht-azure', 'yacht-monaco'],
        isAvailable: true,
      },
    ];
  }

  // Seed Helpers (Strict Zero-Fake Content Policy: No automated demo content created in production)
  public async seedYachtRentalStarterData(_siteId: string): Promise<void> {
    // New Yacht Rental websites start with 0 yachts (empty fleet).
  }

  public async seedBlogsForSite(_siteId: string, _companyName: string): Promise<void> {
    // Zero-fake policy: New websites start with empty blog posts array.
  }

  // ==========================================
  // REAL-TIME SUBSCRIPTION LISTENERS (ADMIN & APP)
  // ==========================================

  public subscribeToSites(callback: (sites: SiteData[]) => void): () => void {
    if (this.isRtdbAvailable() && database) {
      try {
        const sitesRef = ref(database, 'sites');
        const unsubscribe = onValue(sitesRef, (snapshot) => {
          if (snapshot.exists()) {
            const raw = snapshot.val();
            const list = (Object.values(raw) as SiteData[]).filter((s: any) => !s.isDeleted);
            callback(list);
          } else {
            callback([]);
          }
        }, (err) => {
          console.warn('Realtime subscribeToSites warning:', err);
        });
        return () => unsubscribe();
      } catch (err) {
        console.warn('subscribeToSites setup fallback:', err);
      }
    }
    const all = this.getLocal<Record<string, SiteData>>('sites', {});
    callback((Object.values(all) as SiteData[]).filter(s => !s.isDeleted));
    return () => {};
  }

  public subscribeToUsers(callback: (users: UserProfile[]) => void): () => void {
    if (this.isRtdbAvailable() && database) {
      try {
        const usersRef = ref(database, 'users');
        const unsubscribe = onValue(usersRef, (snapshot) => {
          if (snapshot.exists()) {
            callback(Object.values(snapshot.val()) as UserProfile[]);
          } else {
            callback([]);
          }
        }, (err) => {
          console.warn('Realtime subscribeToUsers warning:', err);
        });
        return () => unsubscribe();
      } catch (err) {
        console.warn('subscribeToUsers setup fallback:', err);
      }
    }
    const all = this.getLocal<Record<string, UserProfile>>('users', {});
    callback(Object.values(all) as UserProfile[]);
    return () => {};
  }

  public subscribeToOrders(callback: (orders: BillingOrder[]) => void): () => void {
    if (this.isRtdbAvailable() && database) {
      try {
        const ordersRef = ref(database, 'billingOrders');
        const unsubscribe = onValue(ordersRef, (snapshot) => {
          if (snapshot.exists()) {
            const raw = snapshot.val();
            const list = (Object.values(raw) as BillingOrder[]);
            callback(list.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)));
          } else {
            callback([]);
          }
        }, (err) => {
          console.warn('Realtime subscribeToOrders warning:', err);
        });
        return () => unsubscribe();
      } catch (err) {
        console.warn('subscribeToOrders setup fallback:', err);
      }
    }
    const all = this.getLocal<Record<string, BillingOrder>>('billingOrders', {});
    callback((Object.values(all) as BillingOrder[]).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)));
    return () => {};
  }

  public subscribeToTransactions(callback: (txs: ShopierTransaction[]) => void): () => void {
    if (this.isRtdbAvailable() && database) {
      try {
        const txRef = ref(database, 'transactions');
        const unsubscribe = onValue(txRef, (snapshot) => {
          if (snapshot.exists()) {
            const raw = snapshot.val();
            const list = (Object.values(raw) as ShopierTransaction[]);
            callback(list.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)));
          } else {
            callback([]);
          }
        }, (err) => {
          console.warn('Realtime subscribeToTransactions warning:', err);
        });
        return () => unsubscribe();
      } catch (err) {
        console.warn('subscribeToTransactions setup fallback:', err);
      }
    }
    const list = this.getLocal<ShopierTransaction[]>('transactions', []);
    callback(list.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)));
    return () => {};
  }

  public subscribeToGoogleAds(callback: (subs: GoogleAdsManagementSubscription[]) => void): () => void {
    if (this.isRtdbAvailable() && database) {
      try {
        const adsRef = ref(database, 'googleAdsManagement');
        const unsubscribe = onValue(adsRef, (snapshot) => {
          if (snapshot.exists()) {
            const raw = snapshot.val();
            callback(Object.values(raw) as GoogleAdsManagementSubscription[]);
          } else {
            callback([]);
          }
        }, (err) => {
          console.warn('Realtime subscribeToGoogleAds warning:', err);
        });
        return () => unsubscribe();
      } catch (err) {
        console.warn('subscribeToGoogleAds setup fallback:', err);
      }
    }
    const all = this.getLocal<Record<string, GoogleAdsManagementSubscription>>('googleAdsManagement', {});
    callback(Object.values(all) as GoogleAdsManagementSubscription[]);
    return () => {};
  }

  public subscribeToBackgroundJobs(callback: (jobs: BackgroundJob[]) => void): () => void {
    if (this.isRtdbAvailable() && database) {
      try {
        const jobsRef = ref(database, 'backgroundJobs');
        const unsubscribe = onValue(jobsRef, (snapshot) => {
          if (snapshot.exists()) {
            const raw = snapshot.val();
            callback(Object.values(raw) as BackgroundJob[]);
          } else {
            callback([]);
          }
        }, (err) => {
          console.warn('Realtime subscribeToBackgroundJobs warning:', err);
        });
        return () => unsubscribe();
      } catch (err) {
        console.warn('subscribeToBackgroundJobs setup fallback:', err);
      }
    }
    const all = this.getLocal<Record<string, BackgroundJob>>('backgroundJobs', {});
    callback(Object.values(all) as BackgroundJob[]);
    return () => {};
  }

  public subscribeToMessages(callback: (messages: any[]) => void): () => void {
    if (this.isRtdbAvailable() && database) {
      try {
        const msgRef = ref(database, 'contactMessages');
        const unsubscribe = onValue(msgRef, (snapshot) => {
          if (snapshot.exists()) {
            const raw = snapshot.val();
            callback(Object.values(raw));
          } else {
            callback([]);
          }
        }, (err) => {
          console.warn('Realtime subscribeToMessages warning:', err);
        });
        return () => unsubscribe();
      } catch (err) {
        console.warn('subscribeToMessages setup fallback:', err);
      }
    }
    const list = this.getLocal<any[]>('contactMessages', []);
    callback(list);
    return () => {};
  }

  // ==========================================
  // ADIOSOFT OFFICIAL PORTFOLIO / CASE STUDIES
  // ==========================================

  public async getOfficialPortfolioProjects(): Promise<OfficialPortfolioProject[]> {
    if (this.isRtdbAvailable()) {
      try {
        const snapshot = await get(ref(database!, 'officialPortfolio'));
        if (snapshot.exists()) {
          const data = snapshot.val();
          const list: OfficialPortfolioProject[] = Object.values(data);
          if (list.length > 0) {
            return list.sort((a, b) => (a.sortOrder || 99) - (b.sortOrder || 99));
          }
        }
      } catch (err) {
        console.warn('Firebase officialPortfolio fetch warning, falling back to local storage:', err);
      }
    }

    const localList = this.getLocal<OfficialPortfolioProject[]>('official_portfolio_projects', []);
    if (localList.length === 0) {
      // Seed with verified initial projects
      this.setLocal('official_portfolio_projects', INITIAL_OFFICIAL_PORTFOLIO);
      return INITIAL_OFFICIAL_PORTFOLIO;
    }
    return localList.sort((a, b) => (a.sortOrder || 99) - (b.sortOrder || 99));
  }

  public async saveOfficialPortfolioProject(project: OfficialPortfolioProject): Promise<void> {
    const updatedProject: OfficialPortfolioProject = {
      ...project,
      updatedAt: this.getUtcTimestamp(),
    };

    if (this.isRtdbAvailable()) {
      try {
        await set(ref(database!, `officialPortfolio/${project.id}`), updatedProject);
      } catch (err) {
        console.warn('Firebase officialPortfolio save warning:', err);
      }
    }

    const current = await this.getOfficialPortfolioProjects();
    const existingIndex = current.findIndex((p) => p.id === project.id);
    let nextList: OfficialPortfolioProject[];
    if (existingIndex >= 0) {
      nextList = current.map((p) => (p.id === project.id ? updatedProject : p));
    } else {
      nextList = [...current, updatedProject];
    }
    this.setLocal('official_portfolio_projects', nextList);
  }

  public async deleteOfficialPortfolioProject(projectId: string): Promise<void> {
    if (this.isRtdbAvailable()) {
      try {
        await remove(ref(database!, `officialPortfolio/${projectId}`));
      } catch (err) {
        console.warn('Firebase officialPortfolio remove warning:', err);
      }
    }

    const current = await this.getOfficialPortfolioProjects();
    const filtered = current.filter((p) => p.id !== projectId);
    this.setLocal('official_portfolio_projects', filtered);
  }

  public async toggleOfficialPortfolioFeatured(projectId: string): Promise<boolean> {
    const list = await this.getOfficialPortfolioProjects();
    const item = list.find((p) => p.id === projectId);
    if (!item) return false;

    const nextFeatured = !item.featured;
    await this.saveOfficialPortfolioProject({
      ...item,
      featured: nextFeatured,
    });
    return nextFeatured;
  }

  public async toggleOfficialPortfolioStatus(projectId: string): Promise<'ACTIVE' | 'PASSIVE'> {
    const list = await this.getOfficialPortfolioProjects();
    const item = list.find((p) => p.id === projectId);
    if (!item) return 'ACTIVE';

    const nextStatus = item.status === 'ACTIVE' ? 'PASSIVE' : 'ACTIVE';
    await this.saveOfficialPortfolioProject({
      ...item,
      status: nextStatus,
    });
    return nextStatus;
  }

  // ==========================================
  // Google Ads Management & Subscriptions
  // ==========================================
  public async getGoogleAdsConfig(): Promise<GoogleAdsConfig> {
    const isDirectOAuth = typeof process !== 'undefined' && process?.env?.GOOGLE_ADS_DIRECT_OAUTH_ENABLED === 'true';
    const managerEmail = (typeof process !== 'undefined' && process?.env?.GOOGLE_ADS_MANAGER_EMAIL) || 'adiosoftcom@gmail.com';
    const managerCustomerId = (typeof process !== 'undefined' && process?.env?.GOOGLE_ADS_LOGIN_CUSTOMER_ID) || '';

    const defaultConfig: GoogleAdsConfig = {
      monthlyManagementFeeTRY: 7000,
      currency: 'TRY',
      googleAdsDirectOAuthEnabled: isDirectOAuth,
      googleAdsManagerEmail: managerEmail,
      googleAdsManagerCustomerId: managerCustomerId,
      updatedAt: Date.now(),
    };

    if (this.isRtdbAvailable() && database) {
      try {
        const snap = await get(ref(database, 'systemConfig/googleAds'));
        if (snap.exists()) {
          const val = snap.val();
          return {
            ...defaultConfig,
            ...val,
            googleAdsDirectOAuthEnabled: typeof val.googleAdsDirectOAuthEnabled === 'boolean'
              ? val.googleAdsDirectOAuthEnabled
              : isDirectOAuth,
          };
        }
      } catch (err) {
        console.warn('RTDB getGoogleAdsConfig warning:', err);
      }
    }
    const local = this.getLocal<GoogleAdsConfig | null>('googleAdsConfig', null);
    if (local) {
      return {
        ...defaultConfig,
        ...local,
        googleAdsDirectOAuthEnabled: typeof local.googleAdsDirectOAuthEnabled === 'boolean'
          ? local.googleAdsDirectOAuthEnabled
          : isDirectOAuth,
      };
    }

    return defaultConfig;
  }

  public async saveGoogleAdsConfig(config: Partial<GoogleAdsConfig>): Promise<GoogleAdsConfig> {
    const current = await this.getGoogleAdsConfig();
    const updated: GoogleAdsConfig = {
      ...current,
      ...config,
      updatedAt: this.getUtcTimestamp(),
    };

    if (this.isRtdbAvailable() && database) {
      try {
        await set(ref(database, 'systemConfig/googleAds'), this.cleanForRtdb(updated));
      } catch (err) {
        console.warn('RTDB saveGoogleAdsConfig warning:', err);
      }
    }
    this.setLocal('googleAdsConfig', updated);
    return updated;
  }

  public async getGoogleAdsSubscription(tenantId: string): Promise<GoogleAdsManagementSubscription | null> {
    if (!tenantId) return null;

    let sub: GoogleAdsManagementSubscription | null = null;
    if (this.isRtdbAvailable() && database) {
      try {
        const snap = await get(ref(database, `googleAdsManagement/${tenantId}`));
        if (snap.exists()) {
          sub = snap.val() as GoogleAdsManagementSubscription;
        }
      } catch (err) {
        console.warn('RTDB getGoogleAdsSubscription warning:', err);
      }
    }

    if (!sub) {
      const allSubs = this.getLocal<Record<string, GoogleAdsManagementSubscription>>('googleAdsManagement', {});
      sub = allSubs[tenantId] || null;
    }

    if (sub) {
      const now = this.getUtcTimestamp();
      // Server-validated Request-Time Expiration Check (Requirements 23, 60, 61)
      if (sub.status === 'ACTIVE' && sub.renewalDate && sub.renewalDate < now) {
        sub.status = 'EXPIRED';
      }
    }

    return sub;
  }

  public async saveGoogleAdsSubscription(sub: GoogleAdsManagementSubscription): Promise<void> {
    const payload: GoogleAdsManagementSubscription = {
      ...sub,
      updatedAt: this.getUtcTimestamp(),
    };

    if (this.isRtdbAvailable() && database) {
      try {
        await set(ref(database, `googleAdsManagement/${sub.tenantId}`), this.cleanForRtdb(payload));
      } catch (err) {
        console.warn('RTDB saveGoogleAdsSubscription warning:', err);
      }
    }

    const allSubs = this.getLocal<Record<string, GoogleAdsManagementSubscription>>('googleAdsManagement', {});
    allSubs[sub.tenantId] = payload;
    this.setLocal('googleAdsManagement', allSubs);
  }

  public async getAllGoogleAdsSubscriptions(): Promise<GoogleAdsManagementSubscription[]> {
    const now = this.getUtcTimestamp();
    let subs: GoogleAdsManagementSubscription[] = [];

    if (this.isRtdbAvailable() && database) {
      try {
        const snap = await get(ref(database, 'googleAdsManagement'));
        if (snap.exists()) {
          const val = snap.val();
          subs = Object.values(val) as GoogleAdsManagementSubscription[];
        }
      } catch (err) {
        console.warn('RTDB getAllGoogleAdsSubscriptions warning:', err);
      }
    }

    if (subs.length === 0) {
      const allSubs = this.getLocal<Record<string, GoogleAdsManagementSubscription>>('googleAdsManagement', {});
      subs = Object.values(allSubs);
    }

    // Apply expiration evaluation
    return subs.map((s) => {
      if (s.status === 'ACTIVE' && s.renewalDate && s.renewalDate < now) {
        return { ...s, status: 'EXPIRED' };
      }
      return s;
    });
  }

  public async activateGoogleAdsSubscription(params: {
    tenantId: string;
    userId: string;
    price: number;
    currency?: 'TRY';
    paymentId: string;
    orderId?: string;
    siteId?: string;
    validityDays?: number;
  }): Promise<GoogleAdsManagementSubscription> {
    const now = this.getUtcTimestamp();
    const existing = await this.getGoogleAdsSubscription(params.tenantId);

    let startDate = now;
    let renewalDate: number;

    // Requirement 25 & 26: Safe Calendar Month Calculation & Renewal logic
    if (existing && existing.renewalDate && existing.renewalDate > now) {
      // Extend from current active renewalDate
      startDate = existing.startDate || now;
      renewalDate = params.validityDays && params.validityDays !== 30
        ? existing.renewalDate + params.validityDays * 86400000
        : addMonthsSafely(existing.renewalDate, 1);
    } else {
      // New period starts from current server time
      startDate = now;
      renewalDate = params.validityDays && params.validityDays !== 30
        ? now + params.validityDays * 86400000
        : addMonthsSafely(now, 1);
    }

    const updatedSub: GoogleAdsManagementSubscription = {
      id: existing?.id || `gads_${params.tenantId}`,
      tenantId: params.tenantId,
      userId: params.userId,
      status: 'ACTIVE',
      accessStatus: existing?.accessStatus || 'ACCESS_PENDING',
      price: params.price,
      currency: params.currency || 'TRY',
      startDate,
      renewalDate,
      paymentId: params.paymentId,
      orderId: params.orderId,
      siteId: params.siteId || existing?.siteId || '',
      companyName: existing?.companyName || '',
      contactEmail: existing?.contactEmail || '',
      contactPhone: existing?.contactPhone || '',
      monthlyAdBudget: existing?.monthlyAdBudget,
      connectionStatus: existing?.connectionStatus || 'NOT_CONNECTED',
      googleAdsCustomerId: existing?.googleAdsCustomerId || '',
      googleAdsAccountEmail: existing?.googleAdsAccountEmail || '',
      googleAdsAccountName: existing?.googleAdsAccountName || '',
      onboardingCompleted: existing?.onboardingCompleted || false,
      onboardingData: existing?.onboardingData,
      adminNotes: existing?.adminNotes || 'Ödeme başarıyla alındı. Google Ads yönetim hizmeti aktiftir.',
      createdAt: existing?.createdAt || now,
      updatedAt: now,
    };

    await this.saveGoogleAdsSubscription(updatedSub);

    // Update any linked request for this user / order
    const userRequests = await this.getGoogleAdsRequestsByUser(params.userId);
    for (const req of userRequests) {
      if (req.paymentOrderId === params.orderId || req.status === 'SUBMITTED') {
        await this.updateGoogleAdsRequestStatus(req.id, 'ACTIVE', 'Ödeme onaylandı. Hizmet aktif.');
      }
    }

    return updatedSub;
  }

  public async saveGoogleAdsOnboarding(params: {
    tenantId: string;
    companyName: string;
    googleAdsCustomerId: string;
    googleAdsAccountEmail: string;
    websiteUrl?: string;
    contactPhone: string;
    sector: string;
    targetLocation: string;
    monthlyAdBudget: number;
    adGoal: string;
    existingCampaignsNote?: string;
    additionalNotes?: string;
  }): Promise<GoogleAdsManagementSubscription | null> {
    const existing = await this.getGoogleAdsSubscription(params.tenantId);
    if (!existing) return null;
    const now = this.getUtcTimestamp();
    const updated: GoogleAdsManagementSubscription = {
      ...existing,
      companyName: params.companyName || existing.companyName,
      googleAdsCustomerId: params.googleAdsCustomerId,
      googleAdsAccountEmail: params.googleAdsAccountEmail,
      contactPhone: params.contactPhone,
      monthlyAdBudget: params.monthlyAdBudget,
      accessStatus: existing.accessStatus === 'ACCESS_VERIFIED' ? 'ACCESS_VERIFIED' : 'ACCESS_PENDING',
      onboardingCompleted: true,
      onboardingData: {
        companyName: params.companyName,
        googleAdsCustomerId: params.googleAdsCustomerId,
        googleAdsAccountEmail: params.googleAdsAccountEmail,
        websiteUrl: params.websiteUrl,
        contactPhone: params.contactPhone,
        sector: params.sector,
        targetLocation: params.targetLocation,
        monthlyAdBudget: params.monthlyAdBudget,
        adGoal: params.adGoal,
        existingCampaignsNote: params.existingCampaignsNote,
        additionalNotes: params.additionalNotes,
        submittedAt: now,
      },
      updatedAt: now,
    };
    await this.saveGoogleAdsSubscription(updated);
    return updated;
  }

  public async getGoogleAdsPayments(tenantId: string): Promise<PaymentRecord[]> {
    const all = this.getLocal<Record<string, PaymentRecord>>('paymentRecords', {});
    return Object.values(all)
      .filter((p) => p.planId === 'PLAN_GOOGLE_ADS_MONTHLY' && (p.userId === tenantId || p.siteId === tenantId))
      .sort((a, b) => b.createdAt - a.createdAt);
  }

  public async getAllGoogleAdsPayments(): Promise<PaymentRecord[]> {
    const all = this.getLocal<Record<string, PaymentRecord>>('paymentRecords', {});
    return Object.values(all)
      .filter((p) => p.planId === 'PLAN_GOOGLE_ADS_MONTHLY')
      .sort((a, b) => b.createdAt - a.createdAt);
  }

  public async saveGoogleAdsRequest(request: GoogleAdsManagementRequest): Promise<void> {
    const payload: GoogleAdsManagementRequest = {
      ...request,
      updatedAt: this.getUtcTimestamp(),
    };

    if (this.isRtdbAvailable() && database) {
      try {
        await set(ref(database, `googleAdsManagementRequests/${request.id}`), this.cleanForRtdb(payload));
      } catch (err) {
        console.warn('RTDB saveGoogleAdsRequest warning:', err);
      }
    }

    const allReqs = this.getLocal<Record<string, GoogleAdsManagementRequest>>('googleAdsManagementRequests', {});
    allReqs[request.id] = payload;
    this.setLocal('googleAdsManagementRequests', allReqs);
  }

  public async getGoogleAdsRequest(id: string): Promise<GoogleAdsManagementRequest | null> {
    if (!id) return null;

    if (this.isRtdbAvailable() && database) {
      try {
        const snap = await get(ref(database, `googleAdsManagementRequests/${id}`));
        if (snap.exists()) {
          return snap.val() as GoogleAdsManagementRequest;
        }
      } catch (err) {
        console.warn('RTDB getGoogleAdsRequest warning:', err);
      }
    }

    const allReqs = this.getLocal<Record<string, GoogleAdsManagementRequest>>('googleAdsManagementRequests', {});
    return allReqs[id] || null;
  }

  public async getGoogleAdsRequestsByUser(userId: string): Promise<GoogleAdsManagementRequest[]> {
    if (!userId) return [];

    if (this.isRtdbAvailable() && database) {
      try {
        const snap = await get(ref(database, 'googleAdsManagementRequests'));
        if (snap.exists()) {
          const val = snap.val();
          return Object.values(val).filter(
            (r: any) => r.userId === userId || r.tenantId === userId
          ) as GoogleAdsManagementRequest[];
        }
      } catch (err) {
        console.warn('RTDB getGoogleAdsRequestsByUser warning:', err);
      }
    }

    const allReqs = this.getLocal<Record<string, GoogleAdsManagementRequest>>('googleAdsManagementRequests', {});
    return Object.values(allReqs).filter(
      (r) => r.userId === userId || r.tenantId === userId
    );
  }

  public async getAllGoogleAdsRequests(): Promise<GoogleAdsManagementRequest[]> {
    if (this.isRtdbAvailable() && database) {
      try {
        const snap = await get(ref(database, 'googleAdsManagementRequests'));
        if (snap.exists()) {
          const val = snap.val();
          return Object.values(val) as GoogleAdsManagementRequest[];
        }
      } catch (err) {
        console.warn('RTDB getAllGoogleAdsRequests warning:', err);
      }
    }

    const allReqs = this.getLocal<Record<string, GoogleAdsManagementRequest>>('googleAdsManagementRequests', {});
    return Object.values(allReqs);
  }

  public async updateGoogleAdsRequestStatus(
    id: string,
    status: GoogleAdsManagementRequest['status'],
    adminNotes?: string
  ): Promise<void> {
    const existing = await this.getGoogleAdsRequest(id);
    if (!existing) return;

    const updated: GoogleAdsManagementRequest = {
      ...existing,
      status,
      adminNotes: adminNotes !== undefined ? adminNotes : existing.adminNotes,
      updatedAt: this.getUtcTimestamp(),
    };

    await this.saveGoogleAdsRequest(updated);
  }

  public async getGoogleAdsAccount(tenantId: string): Promise<GoogleAdsAccount | null> {
    if (!tenantId) return null;

    if (this.isRtdbAvailable() && database) {
      try {
        const snap = await get(ref(database, `googleAdsAccounts/${tenantId}`));
        if (snap.exists()) {
          return snap.val() as GoogleAdsAccount;
        }
      } catch (err) {
        console.warn('RTDB getGoogleAdsAccount warning:', err);
      }
    }

    const allAccounts = this.getLocal<Record<string, GoogleAdsAccount>>('googleAdsAccounts', {});
    return allAccounts[tenantId] || null;
  }

  public async saveGoogleAdsAccount(account: GoogleAdsAccount): Promise<void> {
    const payload: GoogleAdsAccount = {
      ...account,
      lastSyncedAt: this.getUtcTimestamp(),
    };

    if (this.isRtdbAvailable() && database) {
      try {
        await set(ref(database, `googleAdsAccounts/${account.userId || account.id}`), this.cleanForRtdb(payload));
      } catch (err) {
        console.warn('RTDB saveGoogleAdsAccount warning:', err);
      }
    }

    const allAccounts = this.getLocal<Record<string, GoogleAdsAccount>>('googleAdsAccounts', {});
    allAccounts[account.userId || account.id] = payload;
    this.setLocal('googleAdsAccounts', allAccounts);
  }

  public async saveGoogleAdsConnection(conn: GoogleAdsConnection): Promise<void> {
    const payload: GoogleAdsConnection = {
      ...conn,
      updatedAt: this.getUtcTimestamp(),
    };

    if (this.isRtdbAvailable() && database) {
      try {
        await set(ref(database, `googleAdsConnections/${conn.tenantId}`), this.cleanForRtdb(payload));
      } catch (err) {
        console.warn('RTDB saveGoogleAdsConnection warning:', err);
      }
    }

    const allConns = this.getLocal<Record<string, GoogleAdsConnection>>('googleAdsConnections', {});
    allConns[conn.tenantId] = payload;
    this.setLocal('googleAdsConnections', allConns);
  }

  public async getGoogleAdsConnection(tenantId: string): Promise<GoogleAdsConnection | null> {
    if (!tenantId) return null;

    if (this.isRtdbAvailable() && database) {
      try {
        const snap = await get(ref(database, `googleAdsConnections/${tenantId}`));
        if (snap.exists()) {
          return snap.val() as GoogleAdsConnection;
        }
      } catch (err) {
        console.warn('RTDB getGoogleAdsConnection warning:', err);
      }
    }

    const allConns = this.getLocal<Record<string, GoogleAdsConnection>>('googleAdsConnections', {});
    return allConns[tenantId] || null;
  }

  public async getAllGoogleAdsConnections(): Promise<GoogleAdsConnection[]> {
    if (this.isRtdbAvailable() && database) {
      try {
        const snap = await get(ref(database, 'googleAdsConnections'));
        if (snap.exists()) {
          const val = snap.val();
          return Object.values(val) as GoogleAdsConnection[];
        }
      } catch (err) {
        console.warn('RTDB getAllGoogleAdsConnections warning:', err);
      }
    }

    const allConns = this.getLocal<Record<string, GoogleAdsConnection>>('googleAdsConnections', {});
    return Object.values(allConns);
  }

  public async getGoogleAdsConnectionByCustomerId(customerId: string): Promise<GoogleAdsConnection | null> {
    if (!customerId) return null;
    const cleanId = customerId.replace(/[^0-9]/g, '');

    const all = await this.getAllGoogleAdsConnections();
    return all.find((c) => c.customerId === cleanId && c.status === 'CONNECTED') || null;
  }

  public async deleteGoogleAdsConnection(tenantId: string): Promise<void> {
    if (!tenantId) return;

    if (this.isRtdbAvailable() && database) {
      try {
        await remove(ref(database, `googleAdsConnections/${tenantId}`));
      } catch (err) {
        console.warn('RTDB deleteGoogleAdsConnection warning:', err);
      }
    }

    const allConns = this.getLocal<Record<string, GoogleAdsConnection>>('googleAdsConnections', {});
    delete allConns[tenantId];
    this.setLocal('googleAdsConnections', allConns);
  }

  public async saveGoogleAdsTokens(
    tenantId: string,
    tokens: {
      accessToken: string;
      refreshToken?: string;
      expiryDate?: number;
      scope?: string;
      googleAccountEmail?: string;
      googleUserId?: string;
    }
  ): Promise<void> {
    if (!tenantId) return;
    const payload = {
      ...tokens,
      updatedAt: this.getUtcTimestamp(),
    };

    if (this.isRtdbAvailable() && database) {
      try {
        await set(ref(database, `googleAdsTokens/${tenantId}`), this.cleanForRtdb(payload));
      } catch (err) {
        console.warn('RTDB saveGoogleAdsTokens warning:', err);
      }
    }

    const allTokens = this.getLocal<Record<string, any>>('googleAdsTokens', {});
    allTokens[tenantId] = payload;
    this.setLocal('googleAdsTokens', allTokens);
  }

  public async getGoogleAdsTokens(
    tenantId: string
  ): Promise<{
    accessToken: string;
    refreshToken?: string;
    expiryDate?: number;
    scope?: string;
    googleAccountEmail?: string;
    googleUserId?: string;
  } | null> {
    if (!tenantId) return null;

    if (this.isRtdbAvailable() && database) {
      try {
        const snap = await get(ref(database, `googleAdsTokens/${tenantId}`));
        if (snap.exists()) {
          return snap.val();
        }
      } catch (err) {
        console.warn('RTDB getGoogleAdsTokens warning:', err);
      }
    }

    const allTokens = this.getLocal<Record<string, any>>('googleAdsTokens', {});
    return allTokens[tenantId] || null;
  }

  public async updateUserStatus(userId: string, status: 'ACTIVE' | 'SUSPENDED' | 'active' | 'suspended'): Promise<void> {
    if (this.isRtdbAvailable() && database) {
      try {
        await update(ref(database, `users/${userId}`), {
          status,
          updatedAt: this.getUtcTimestamp(),
        });
      } catch (err) {
        console.warn('RTDB updateUserStatus warning:', err);
      }
    }
    const allUsers = this.getLocal<Record<string, UserProfile>>('users', {});
    if (allUsers[userId]) {
      allUsers[userId].status = status as any;
      allUsers[userId].updatedAt = this.getUtcTimestamp();
      this.setLocal('users', allUsers);
    }
  }

  public async updateGoogleAdsSubscriptionAccessStatus(tenantId: string, accessStatus: GoogleAdsAccessStatus, notes?: string): Promise<void> {
    const sub = await this.getGoogleAdsSubscription(tenantId);
    if (sub) {
      sub.accessStatus = accessStatus;
      if (notes) {
        sub.adminNotes = notes;
      }
      sub.updatedAt = this.getUtcTimestamp();
      await this.saveGoogleAdsSubscription(sub);
    }
  }

  public async requestPasswordResetEmail(email: string): Promise<void> {
    try {
      const { auth } = await import('../config/firebase');
      const { sendPasswordResetEmail } = await import('firebase/auth');
      if (auth) {
        await sendPasswordResetEmail(auth, email);
      }
    } catch (err) {
      console.warn('Firebase sendPasswordResetEmail warning:', err);
    }
  }

  // ==========================================================================
  // AI ARAMA GÖRÜNÜRLÜĞÜ (AI SEARCH VISIBILITY / AI PRESENCE)
  // ==========================================================================

  public async getAIVisibilitySubscription(tenantId: string): Promise<AIVisibilitySubscription | null> {
    if (!tenantId) return null;
    const now = this.getUtcTimestamp();

    if (this.isRtdbAvailable() && database) {
      try {
        const snapshot = await get(ref(database, `aiVisibilitySubscriptions/${tenantId}`));
        if (snapshot.exists()) {
          const sub = snapshot.val() as AIVisibilitySubscription;
          if (sub.status === 'active' && sub.endDate < now) {
            sub.status = 'expired';
          }
          return sub;
        }
      } catch (err) {
        console.warn('RTDB getAIVisibilitySubscription warning:', err);
      }
    }

    const allSubs = this.getLocal<Record<string, AIVisibilitySubscription>>('aiVisibilitySubscriptions', {});
    const sub = allSubs[tenantId] || null;
    if (sub && sub.status === 'active' && sub.endDate < now) {
      return { ...sub, status: 'expired' };
    }
    return sub;
  }

  public async saveAIVisibilitySubscription(sub: AIVisibilitySubscription): Promise<void> {
    if (this.isRtdbAvailable() && database) {
      try {
        await set(ref(database, `aiVisibilitySubscriptions/${sub.tenantId}`), this.cleanForRtdb(sub));
      } catch (err) {
        console.warn('RTDB saveAIVisibilitySubscription warning:', err);
      }
    }

    const allSubs = this.getLocal<Record<string, AIVisibilitySubscription>>('aiVisibilitySubscriptions', {});
    allSubs[sub.tenantId] = sub;
    this.setLocal('aiVisibilitySubscriptions', allSubs);
  }

  public async activateAIVisibilitySubscription(params: {
    tenantId: string;
    userId: string;
    price: number;
    currency?: 'TRY';
    paymentId: string;
    orderId?: string;
    siteId?: string;
    validityDays?: number;
    paymentProvider?: 'Shopier' | 'ADMIN';
  }): Promise<AIVisibilitySubscription> {
    const now = this.getUtcTimestamp();
    const existing = await this.getAIVisibilitySubscription(params.tenantId);

    let startDate = now;
    let endDate = addMonthsSafely(now, 12);

    // Idempotent renewal: extend from current active expiration date if still valid
    if (existing && existing.status === 'active' && existing.endDate > now) {
      startDate = existing.startDate || now;
      endDate = addMonthsSafely(existing.endDate, 12);
    } else if (params.validityDays && (params.validityDays < 360 || params.validityDays > 366)) {
      endDate = now + params.validityDays * 86400000;
    } else {
      // 1 Full Year = 12 calendar months
      endDate = addMonthsSafely(startDate, 12);
    }

    const updatedSub: AIVisibilitySubscription = {
      id: existing?.id || `ai_vis_${params.tenantId}`,
      userId: params.userId,
      tenantId: params.tenantId,
      websiteId: params.siteId || existing?.websiteId,
      status: 'active',
      productName: 'AI Yerel İşletme Görünürlüğü',
      planId: 'AI_LOCAL_VISIBILITY',
      priceTRY: params.price || 5000,
      currency: params.currency || 'TRY',
      billingPeriod: 'YEAR',
      startDate,
      endDate,
      renewalStatus: 'MANUAL',
      orderId: params.orderId,
      paymentProvider: params.paymentProvider || 'Shopier',
      paymentId: params.paymentId,
      createdAt: existing?.createdAt || now,
      updatedAt: now,
    };

    await this.saveAIVisibilitySubscription(updatedSub);

    // Initialize default profile and tasks if needed
    await this.ensureAIVisibilitySetup(params.tenantId, params.siteId);

    return updatedSub;
  }

  // =========================================================================
  // AI LOCAL VISIBILITY ENTITY & ANALYSIS PERSISTENCE (1 ACTIVE ENTITY PER TENANT)
  // =========================================================================

  /**
   * Retrieves the active business entity for AI_LOCAL_VISIBILITY. Maximum 1 active entity per tenant.
   */
  public async getActiveAILocalEntity(tenantId: string): Promise<AILocalVisibilityEntity | null> {
    if (!tenantId) return null;

    if (this.isRtdbAvailable() && database) {
      try {
        const snapshot = await get(ref(database, `aiLocalEntities/${tenantId}`));
        if (snapshot.exists()) {
          const val = snapshot.val();
          const entities: AILocalVisibilityEntity[] = Array.isArray(val) ? val.filter(Boolean) : Object.values(val);
          const active = entities.find((e) => e.status === 'ACTIVE' && e.active !== false);
          if (active) return active;
        }
      } catch (err) {
        console.warn('RTDB getActiveAILocalEntity warning:', err);
      }
    }

    const all = this.getLocal<Record<string, Record<string, AILocalVisibilityEntity>>>('aiLocalEntities', {});
    const tenantEntities = all[tenantId] ? Object.values(all[tenantId]) : [];
    return tenantEntities.find((e) => e.status === 'ACTIVE' && e.active !== false) || null;
  }

  /**
   * Retrieves all entities (including archived) for a tenant
   */
  public async getAllAILocalEntities(tenantId: string): Promise<AILocalVisibilityEntity[]> {
    if (!tenantId) return [];

    let list: AILocalVisibilityEntity[] = [];
    if (this.isRtdbAvailable() && database) {
      try {
        const snapshot = await get(ref(database, `aiLocalEntities/${tenantId}`));
        if (snapshot.exists()) {
          const val = snapshot.val();
          list = Array.isArray(val) ? val.filter(Boolean) : Object.values(val);
        }
      } catch (err) {
        console.warn('RTDB getAllAILocalEntities warning:', err);
      }
    }

    if (list.length === 0) {
      const all = this.getLocal<Record<string, Record<string, AILocalVisibilityEntity>>>('aiLocalEntities', {});
      list = all[tenantId] ? Object.values(all[tenantId]) : [];
    }

    // Filter out deleted entities so active count is accurate (0 after delete)
    return list.filter((e) => e.status !== 'DELETED' && e.active !== false);
  }

  /**
   * Creates a new business entity for AI_LOCAL_VISIBILITY.
   * Strictly enforces: MAXIMUM 1 ACTIVE ENTITY PER SUBSCRIPTION / TENANT.
   */
  public async createAILocalEntity(
    tenantId: string,
    entityData: Omit<AILocalVisibilityEntity, 'id' | 'tenantId' | 'status' | 'createdAt' | 'updatedAt'>
  ): Promise<{ success: boolean; entity?: AILocalVisibilityEntity; error?: string }> {
    const active = await this.getActiveAILocalEntity(tenantId);
    if (active) {
      return {
        success: false,
        error: 'Bu abonelikte zaten aktif bir işletme bulunuyor. Yeni bir işletme eklemek için mevcut işletmeyi arşivleyin, silin veya ikinci bir AI Görünürlük paketi satın alın.',
      };
    }

    const now = this.getUtcTimestamp();
    const entityId = `entity_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`;
    const newEntity: AILocalVisibilityEntity = {
      id: entityId,
      tenantId,
      status: 'ACTIVE',
      active: true,
      ...entityData,
      createdAt: now,
      updatedAt: now,
    };

    if (this.isRtdbAvailable() && database) {
      try {
        await set(ref(database, `aiLocalEntities/${tenantId}/${entityId}`), this.cleanForRtdb(newEntity));
      } catch (err) {
        console.warn('RTDB createAILocalEntity warning:', err);
      }
    }

    const all = this.getLocal<Record<string, Record<string, AILocalVisibilityEntity>>>('aiLocalEntities', {});
    if (!all[tenantId]) all[tenantId] = {};
    all[tenantId][entityId] = newEntity;
    this.setLocal('aiLocalEntities', all);

    return { success: true, entity: newEntity };
  }

  /**
   * Updates an existing business entity
   */
  public async updateAILocalEntity(
    tenantId: string,
    entityId: string,
    updates: Partial<AILocalVisibilityEntity>
  ): Promise<AILocalVisibilityEntity | null> {
    const now = this.getUtcTimestamp();
    const all = await this.getAllAILocalEntities(tenantId);
    const existing = all.find((e) => e.id === entityId);
    if (!existing) return null;

    const merged: AILocalVisibilityEntity = {
      ...existing,
      ...updates,
      id: entityId,
      tenantId,
      updatedAt: now,
    };

    if (this.isRtdbAvailable() && database) {
      try {
        await set(ref(database, `aiLocalEntities/${tenantId}/${entityId}`), this.cleanForRtdb(merged));
      } catch (err) {
        console.warn('RTDB updateAILocalEntity warning:', err);
      }
    }

    const localAll = this.getLocal<Record<string, Record<string, AILocalVisibilityEntity>>>('aiLocalEntities', {});
    if (!localAll[tenantId]) localAll[tenantId] = {};
    localAll[tenantId][entityId] = merged;
    this.setLocal('aiLocalEntities', localAll);

    return merged;
  }

  /**
   * Archives an entity to allow creating a replacement if needed
   */
  public async archiveAILocalEntity(tenantId: string, entityId: string): Promise<boolean> {
    const updated = await this.updateAILocalEntity(tenantId, entityId, { status: 'ARCHIVED', active: false });
    return Boolean(updated);
  }

  /**
   * Deletes an entity: marks status as DELETED, active = false, deletedAt, deletedBy,
   * completely frees up activeEntity quota (activeCount = 0).
   */
  public async deleteAILocalEntity(tenantId: string, entityId: string, deletedBy?: string): Promise<boolean> {
    const now = this.getUtcTimestamp();
    const deleteUpdates: Partial<AILocalVisibilityEntity> = {
      status: 'DELETED',
      active: false,
      deletedAt: now,
      deletedBy: deletedBy || tenantId,
      updatedAt: now,
    };

    if (this.isRtdbAvailable() && database) {
      try {
        // Also remove or update to DELETED
        await update(ref(database, `aiLocalEntities/${tenantId}/${entityId}`), deleteUpdates);
      } catch (err) {
        console.warn('RTDB deleteAILocalEntity warning:', err);
      }
    }

    const all = this.getLocal<Record<string, Record<string, AILocalVisibilityEntity>>>('aiLocalEntities', {});
    if (all[tenantId] && all[tenantId][entityId]) {
      all[tenantId][entityId] = {
        ...all[tenantId][entityId],
        ...deleteUpdates,
      };
      this.setLocal('aiLocalEntities', all);
    }
    return true;
  }

  /**
   * Saves an AI Visibility Analysis Run
   */
  public async saveAIVisibilityRun(tenantId: string, run: AIVisibilityAnalysisRun): Promise<void> {
    run.updatedAt = this.getUtcTimestamp();

    if (this.isRtdbAvailable() && database) {
      try {
        await set(ref(database, `aiVisibilityRuns/${tenantId}/${run.runId}`), this.cleanForRtdb(run));
      } catch (err) {
        console.warn('RTDB saveAIVisibilityRun warning:', err);
      }
    }

    const allRuns = this.getLocal<Record<string, Record<string, AIVisibilityAnalysisRun>>>('aiVisibilityRuns', {});
    if (!allRuns[tenantId]) allRuns[tenantId] = {};
    allRuns[tenantId][run.runId] = run;
    this.setLocal('aiVisibilityRuns', allRuns);
  }

  /**
   * Retrieves all analysis runs for a tenant (sorted by newest)
   */
  public async getAIVisibilityRuns(tenantId: string): Promise<AIVisibilityAnalysisRun[]> {
    if (!tenantId) return [];

    if (this.isRtdbAvailable() && database) {
      try {
        const snapshot = await get(ref(database, `aiVisibilityRuns/${tenantId}`));
        if (snapshot.exists()) {
          const val = snapshot.val();
          const items: AIVisibilityAnalysisRun[] = Array.isArray(val) ? val.filter(Boolean) : Object.values(val);
          return items.sort((a, b) => (b.startedAt || 0) - (a.startedAt || 0));
        }
      } catch (err) {
        console.warn('RTDB getAIVisibilityRuns warning:', err);
      }
    }

    const allRuns = this.getLocal<Record<string, Record<string, AIVisibilityAnalysisRun>>>('aiVisibilityRuns', {});
    const tenantRuns = allRuns[tenantId] ? Object.values(allRuns[tenantId]) : [];
    return tenantRuns.sort((a, b) => (b.startedAt || 0) - (a.startedAt || 0));
  }

  /**
   * Marks a running analysis as aborted
   */
  public async abortAIVisibilityRun(tenantId: string, runId: string): Promise<boolean> {
    const runs = await this.getAIVisibilityRuns(tenantId);
    const run = runs.find((r) => r.runId === runId);
    if (!run) return false;

    run.status = 'ABORTED';
    run.abortedAt = this.getUtcTimestamp();
    run.updatedAt = this.getUtcTimestamp();
    await this.saveAIVisibilityRun(tenantId, run);
    return true;
  }

  public async hasAIVisibilityEntitlement(tenantId: string): Promise<boolean> {
    const sub = await this.getAIVisibilitySubscription(tenantId);
    if (!sub) return false;
    const now = this.getUtcTimestamp();
    return sub.status === 'active' && sub.endDate > now;
  }

  public async getAIVisibilityProfile(tenantId: string): Promise<AIVisibilityProfile | null> {
    if (!tenantId) return null;

    if (this.isRtdbAvailable() && database) {
      try {
        const snapshot = await get(ref(database, `aiVisibilityProfiles/${tenantId}`));
        if (snapshot.exists()) {
          return snapshot.val() as AIVisibilityProfile;
        }
      } catch (err) {
        console.warn('RTDB getAIVisibilityProfile warning:', err);
      }
    }

    const allProfiles = this.getLocal<Record<string, AIVisibilityProfile>>('aiVisibilityProfiles', {});
    return allProfiles[tenantId] || null;
  }

  public async saveAIVisibilityProfile(tenantId: string, profile: AIVisibilityProfile): Promise<void> {
    profile.updatedAt = this.getUtcTimestamp();

    if (this.isRtdbAvailable() && database) {
      try {
        await set(ref(database, `aiVisibilityProfiles/${tenantId}`), this.cleanForRtdb(profile));
      } catch (err) {
        console.warn('RTDB saveAIVisibilityProfile warning:', err);
      }
    }

    const allProfiles = this.getLocal<Record<string, AIVisibilityProfile>>('aiVisibilityProfiles', {});
    allProfiles[tenantId] = profile;
    this.setLocal('aiVisibilityProfiles', allProfiles);
  }

  public async getAIVisibilityTasks(tenantId: string): Promise<AIVisibilityTask[]> {
    if (!tenantId) return [];

    if (this.isRtdbAvailable() && database) {
      try {
        const snapshot = await get(ref(database, `aiVisibilityTasks/${tenantId}`));
        if (snapshot.exists()) {
          const val = snapshot.val();
          return Array.isArray(val) ? val.filter(Boolean) : Object.values(val);
        }
      } catch (err) {
        console.warn('RTDB getAIVisibilityTasks warning:', err);
      }
    }

    const allTasks = this.getLocal<Record<string, Record<string, AIVisibilityTask>>>('aiVisibilityTasks', {});
    const tenantTasks = allTasks[tenantId];
    return tenantTasks ? Object.values(tenantTasks) : [];
  }

  public async saveAIVisibilityTask(tenantId: string, task: AIVisibilityTask): Promise<void> {
    task.updatedAt = this.getUtcTimestamp();

    if (this.isRtdbAvailable() && database) {
      try {
        await set(ref(database, `aiVisibilityTasks/${tenantId}/${task.taskId}`), this.cleanForRtdb(task));
      } catch (err) {
        console.warn('RTDB saveAIVisibilityTask warning:', err);
      }
    }

    const allTasks = this.getLocal<Record<string, Record<string, AIVisibilityTask>>>('aiVisibilityTasks', {});
    if (!allTasks[tenantId]) allTasks[tenantId] = {};
    allTasks[tenantId][task.taskId] = task;
    this.setLocal('aiVisibilityTasks', allTasks);
  }

  public async deleteAIVisibilityTask(tenantId: string, taskId: string): Promise<void> {
    if (this.isRtdbAvailable() && database) {
      try {
        await remove(ref(database, `aiVisibilityTasks/${tenantId}/${taskId}`));
      } catch (err) {
        console.warn('RTDB deleteAIVisibilityTask warning:', err);
      }
    }

    const allTasks = this.getLocal<Record<string, Record<string, AIVisibilityTask>>>('aiVisibilityTasks', {});
    if (allTasks[tenantId] && allTasks[tenantId][taskId]) {
      delete allTasks[tenantId][taskId];
      this.setLocal('aiVisibilityTasks', allTasks);
    }
  }

  public async archiveAIVisibilityTask(tenantId: string, taskId: string): Promise<void> {
    const tasks = await this.getAIVisibilityTasks(tenantId);
    const target = tasks.find((t) => t.taskId === taskId);
    if (!target) return;
    target.isArchived = true;
    target.updatedAt = this.getUtcTimestamp();
    await this.saveAIVisibilityTask(tenantId, target);
  }

  public async stopAIVisibilityTask(tenantId: string, taskId: string): Promise<void> {
    const tasks = await this.getAIVisibilityTasks(tenantId);
    const target = tasks.find((t) => t.taskId === taskId);
    if (!target) return;
    target.status = 'stopped';
    target.stoppedAt = this.getUtcTimestamp();
    target.updatedAt = this.getUtcTimestamp();
    await this.saveAIVisibilityTask(tenantId, target);
  }

  public async getAllAIVisibilitySubscriptions(): Promise<AIVisibilitySubscription[]> {
    const now = this.getUtcTimestamp();

    if (this.isRtdbAvailable() && database) {
      try {
        const snapshot = await get(ref(database, 'aiVisibilitySubscriptions'));
        if (snapshot.exists()) {
          const val = snapshot.val();
          const items: AIVisibilitySubscription[] = Array.isArray(val) ? val.filter(Boolean) : Object.values(val);
          return items.map((sub) => {
            if (sub.status === 'active' && sub.endDate < now) {
              return { ...sub, status: 'expired' };
            }
            return sub;
          });
        }
      } catch (err) {
        console.warn('RTDB getAllAIVisibilitySubscriptions warning:', err);
      }
    }

    const allSubs = this.getLocal<Record<string, AIVisibilitySubscription>>('aiVisibilitySubscriptions', {});
    return Object.values(allSubs).map((sub) => {
      if (sub.status === 'active' && sub.endDate < now) {
        return { ...sub, status: 'expired' };
      }
      return sub;
    });
  }

  public async ensureAIVisibilitySetup(tenantId: string, siteId?: string): Promise<void> {
    const now = this.getUtcTimestamp();

    // 1. Ensure Profile exists
    const existingProfile = await this.getAIVisibilityProfile(tenantId);
    if (!existingProfile) {
      // Pre-fill from site if available
      let siteName = 'İşletme';
      let siteDomain = '';
      let siteDesc = '';
      if (siteId) {
        const site = await this.getSiteById(siteId);
        if (site) {
          siteName = site.name || siteName;
          siteDomain = (typeof site.customDomain === 'string' ? site.customDomain : '') || (site.slug ? `adiosoft.com/${site.slug}` : (site.subdomain ? `adiosoft.com/${site.subdomain}` : site.domain || ''));
          siteDesc = site.settings?.metaDescription || '';
        }
      }

      const initialProfile: AIVisibilityProfile = {
        businessName: siteName,
        legalName: siteName,
        website: siteDomain ? `https://${siteDomain}` : '',
        primaryDomain: siteDomain,
        description: siteDesc || `${siteName} kurumsal hizmetleri ve sektörel çözümleri.`,
        industry: 'Kurumsal Hizmetler',
        services: ['Kurumsal Danışmanlık', 'Müşteri Hizmetleri'],
        serviceAreas: ['İstanbul', 'Türkiye'],
        city: 'İstanbul',
        district: 'Kadıköy',
        address: '',
        phone: '',
        email: '',
        openingHours: 'Pazartesi - Cuma: 09:00 - 18:00',
        socialProfiles: {},
        faq: [
          {
            question: `${siteName} hangi hizmetleri sunmaktadır?`,
            answer: `${siteName} alanında uzman kadrosuyla kurumsal çözümler ve profesyonel hizmetler sunmaktadır.`,
          },
        ],
        importantFacts: [
          'Resmi tescilli işletme',
          'Doğrulanmış web adresi ve iletişim kanalları',
        ],
        primaryKeywords: [siteName, 'kurumsal hizmetler'],
        brandVariants: [siteName],
        language: 'tr',
        updatedAt: now,
      };

      await this.saveAIVisibilityProfile(tenantId, initialProfile);
    }

    // 2. Ensure standard operational tasks exist
    const existingTasks = await this.getAIVisibilityTasks(tenantId);
    if (existingTasks.length === 0) {
      const defaultTasks: AIVisibilityTask[] = [
        {
          taskId: 'task_tech_seo',
          tenantId,
          websiteId: siteId,
          subscriptionId: `ai_vis_${tenantId}`,
          taskType: 'technical_seo',
          title: 'Teknik SEO & Canonical Yapılandırması',
          description: 'Web sitesi meta başlıkları, açıklamaları, canonical linkleri ve dil etiketlerinin doğrulanması.',
          status: 'completed',
          notes: 'Teknik SEO mimarisi sistem tarafından otomatik senkronize edildi.',
          createdAt: now,
          updatedAt: now,
          completedAt: now,
        },
        {
          taskId: 'task_structured_data',
          tenantId,
          websiteId: siteId,
          subscriptionId: `ai_vis_${tenantId}`,
          taskType: 'structured_data',
          title: 'JSON-LD Yapılandırılmış Veri Şemaları',
          description: 'Organization, LocalBusiness, WebSite ve Service şemalarının doğrulanması.',
          status: 'completed',
          notes: 'JSON-LD şemaları robots ve arama motorları için hazırlandı.',
          createdAt: now,
          updatedAt: now,
          completedAt: now,
        },
        {
          taskId: 'task_entity_consistency',
          tenantId,
          websiteId: siteId,
          subscriptionId: `ai_vis_${tenantId}`,
          taskType: 'entity_consistency',
          title: 'Varlık & Marka Bütünlüğü Denetimi',
          description: 'Firma adı, adres, telefon ve marka varyantlarının web üzerindeki tutarlılık kontrolü.',
          status: 'in_progress',
          notes: 'İşletme profili bilgileri güncellenmeye devam ediyor.',
          createdAt: now,
          updatedAt: now,
        },
        {
          taskId: 'task_sitemap_robots',
          tenantId,
          websiteId: siteId,
          subscriptionId: `ai_vis_${tenantId}`,
          taskType: 'sitemap_robots',
          title: 'Sitemap & Robots.txt Senkronizasyonu',
          description: 'ChatGPT Search ve Gemini botları için sitemap.xml ve robots.txt kurallarının yapılandırılması.',
          status: 'completed',
          notes: 'Sitemap.xml ve robots.txt arama motorlarına açık olarak yapılandırıldı.',
          createdAt: now,
          updatedAt: now,
          completedAt: now,
        },
        {
          taskId: 'task_content_coverage',
          tenantId,
          websiteId: siteId,
          subscriptionId: `ai_vis_${tenantId}`,
          taskType: 'content_coverage',
          title: 'AI Arama İçerik Kapsamı & Sıkça Sorulan Sorular',
          description: 'İşletme hakkında grounding sağlayan FAQ ve temel olgular içeriğinin tamamlanması.',
          status: 'in_progress',
          notes: 'Sıkça sorulan sorular ve işletme olguları profilden güncellenebilir.',
          createdAt: now,
          updatedAt: now,
        },
      ];

      for (const t of defaultTasks) {
        await this.saveAIVisibilityTask(tenantId, t);
      }
    }
  }

  // =========================================================================
  // ADMIN AUDIT LOGS & MANUAL PACKAGE ASSIGNMENTS
  // =========================================================================

  public async recordAdminAuditLog(log: {
    adminUid: string;
    adminEmail?: string;
    tenantId: string;
    customer?: string;
    product: string;
    action: string;
    oldStatus?: string;
    newStatus: string;
    startDate: number;
    endDate: number;
    reason?: string;
  }): Promise<string> {
    const logId = `audit_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
    const logEntry = {
      id: logId,
      ...log,
      createdAt: Date.now(),
    };
    if (this.isRtdbAvailable() && database) {
      try {
        await set(ref(database, `auditLogs/${logId}`), this.cleanForRtdb(logEntry));
      } catch (err) {
        console.warn('RTDB recordAdminAuditLog warning:', err);
      }
    }
    return logId;
  }

  public async getAdminAuditLogs(tenantId?: string): Promise<any[]> {
    try {
      const snap = await get(ref(database, 'auditLogs'));
      if (!snap.exists()) return [];
      const data = snap.val();
      const logs = Object.values(data) as any[];
      logs.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      if (tenantId) {
        return logs.filter((l) => l.tenantId === tenantId);
      }
      return logs;
    } catch (err) {
      console.error('Error fetching admin audit logs:', err);
      return [];
    }
  }

  public async assignManualSubscription(params: {
    adminUid: string;
    adminEmail?: string;
    tenantId: string;
    customerName?: string;
    product: 'PLAN_CORP_ANNUAL' | 'PLAN_YACHT_ANNUAL' | 'AI_SEARCH_VISIBILITY' | 'PLAN_GOOGLE_ADS_MONTHLY' | string;
    siteId?: string;
    startDate: number;
    endDate: number;
    status: 'ACTIVE';
    price?: number;
    reason?: string;
  }): Promise<{ success: boolean; message: string }> {
    const now = Date.now();
    const orderId = `ADMIN_ORD_${Date.now()}`;
    const paymentId = `ADMIN_PAY_${Date.now()}`;

    // 1. Process based on product type
    if (params.product === 'AI_SEARCH_VISIBILITY' || params.product === 'AI_LOCAL_VISIBILITY') {
      const validityDays = Math.max(1, Math.round((params.endDate - params.startDate) / 86400000));
      await this.activateAIVisibilitySubscription({
        tenantId: params.tenantId,
        userId: params.tenantId,
        price: params.price || 5000,
        currency: 'TRY',
        paymentId,
        orderId,
        siteId: params.siteId,
        validityDays,
        paymentProvider: 'ADMIN',
      });
    } else if (params.product === 'PLAN_GOOGLE_ADS_MONTHLY') {
      const existingGoogleAds = await this.getGoogleAdsSubscription(params.tenantId);
      const isExistingActive = existingGoogleAds && existingGoogleAds.status === 'ACTIVE';
      const durationMs = params.endDate - params.startDate;
      const baseRenewal = isExistingActive && existingGoogleAds.renewalDate > now
        ? existingGoogleAds.renewalDate
        : now;
      const effectiveRenewalDate = isExistingActive ? baseRenewal + durationMs : params.endDate;

      await set(
        ref(database, `googleAdsSubscriptions/${params.tenantId}`),
        this.cleanForRtdb({
          tenantId: params.tenantId,
          userId: params.tenantId,
          status: 'ACTIVE',
          accessStatus: existingGoogleAds?.accessStatus || 'ACCESS_GRANTED',
          startDate: isExistingActive ? (existingGoogleAds.startDate || params.startDate) : params.startDate,
          renewalDate: effectiveRenewalDate,
          price: params.price || 7000,
          currency: 'TRY',
          paymentProvider: 'ADMIN',
          paymentId,
          orderId,
          updatedAt: now,
        })
      );
    } else {
      // Website plan (Corporate or Yacht)
      const isYacht = params.product === 'PLAN_YACHT_ANNUAL' || params.product === 'PLAN_YACHT' || params.product === 'YACHT_RENTAL_WEBSITE';
      const planName = isYacht ? 'Lüks Yat Kiralama Paketi' : 'Kurumsal Web Sitesi Paketi';
      const planPrice = isYacht ? 36000 : 15000;
      const sector = isYacht ? 'YACHT_RENTAL' : 'CORPORATE';
      const targetPlanId = isYacht ? 'PLAN_YACHT_ANNUAL' : 'PLAN_CORP_ANNUAL';

      // Enforce Requirement 26: Super Admin Single License Architecture Rule
      // An account can only have 1 active website subscription at any time.
      const activeWebsiteSub = await this.findActiveWebsiteSubscription(params.tenantId, now);
      if (activeWebsiteSub) {
        const isSameSector = activeWebsiteSub.sector === sector || activeWebsiteSub.planId === targetPlanId;
        if (!isSameSector) {
          throw new Error(
            `Bu kullanıcı hesabında zaten aktif bir website aboneliği bulunmaktadır (${activeWebsiteSub.planName || activeWebsiteSub.planId}). Aynı anda yalnızca bir website paketi kullanılabilir.`
          );
        }
      }

      // Update /siteSubscriptions/{siteId} if siteId exists
      if (params.siteId) {
        await set(
          ref(database, `siteSubscriptions/${params.siteId}`),
          this.cleanForRtdb({
            siteId: params.siteId,
            planId: targetPlanId,
            planName,
            priceTRY: params.price || planPrice,
            currency: 'TRY',
            status: 'ACTIVE',
            startDate: params.startDate,
            expireDate: params.endDate,
            paymentProvider: 'ADMIN',
            paymentId,
            updatedAt: now,
          })
        );

        // Also update site document directly
        await update(
          ref(database, `sites/${params.siteId}`),
          this.cleanForRtdb({
            subscription: {
              planId: targetPlanId,
              planName,
              status: 'ACTIVE',
              startDate: params.startDate,
              expireDate: params.endDate,
            },
            updatedAt: now,
          })
        );
      }

      // Check existing user subscriptions to prevent duplicate active records
      const existingUserSubs = await this.getUserSubscriptions(params.tenantId);
      const activeExistingSub = existingUserSubs.find(
        (s) => s.status === 'ACTIVE' && (s.planId === targetPlanId || s.sector === sector)
      );

      if (activeExistingSub) {
        // Extend existing subscription without creating duplicates
        const baseExpire = activeExistingSub.expireDate && activeExistingSub.expireDate > now
          ? activeExistingSub.expireDate
          : now;
        const extendedExpire = baseExpire + (params.endDate - params.startDate);

        await update(
          ref(database, `userSubscriptions/${params.tenantId}/${activeExistingSub.id}`),
          this.cleanForRtdb({
            expireDate: extendedExpire,
            planId: targetPlanId,
            planName,
            priceTRY: params.price || planPrice,
            paymentProvider: 'ADMIN',
            paymentId,
            updatedAt: now,
          })
        );
      } else {
        // Create new UserSubscriptionRecord only if no active record exists
        const subRecordId = `sub_${params.tenantId}_${Date.now()}`;
        await set(
          ref(database, `userSubscriptions/${params.tenantId}/${subRecordId}`),
          this.cleanForRtdb({
            id: subRecordId,
            userId: params.tenantId,
            planId: targetPlanId,
            planName,
            sector,
            websiteLimit: 1,
            status: 'ACTIVE',
            startDate: params.startDate,
            expireDate: params.endDate,
            priceTRY: params.price || planPrice,
            paymentProvider: 'ADMIN',
            paymentId,
            createdAt: now,
            updatedAt: now,
          })
        );
      }
    }

    // 2. Record Billing Order
    await set(
      ref(database, `billingOrders/${orderId}`),
      this.cleanForRtdb({
        id: orderId,
        userId: params.tenantId,
        tenantId: params.tenantId,
        siteId: params.siteId || '',
        planId: params.product as any,
        planName:
          params.product === 'AI_SEARCH_VISIBILITY'
            ? 'AI Arama Görünürlüğü (Yıllık)'
            : params.product === 'PLAN_GOOGLE_ADS_MONTHLY'
            ? 'Google Ads Profesyonel Yönetim'
            : params.product === 'PLAN_YACHT_ANNUAL'
            ? 'Lüks Yat Kiralama Paketi'
            : 'Kurumsal Web Sitesi Paketi',
        sector: (params.product === 'PLAN_YACHT_ANNUAL' ? 'YACHT_RENTAL' : 'CORPORATE') as any,
        amount: params.price || 0,
        currency: 'TRY',
        status: 'PAID',
        provider: 'ADMIN',
        customerName: params.customerName || 'Müşteri',
        customerEmail: '',
        paidAt: now,
        createdAt: now,
        updatedAt: now,
        notes: `Super Admin manuel paket ataması (${params.reason || 'Yetkili ataması'})`,
      })
    );

    // 3. Record Audit Log
    await this.recordAdminAuditLog({
      adminUid: params.adminUid,
      adminEmail: params.adminEmail,
      tenantId: params.tenantId,
      customer: params.customerName,
      product: params.product,
      action: 'MANUAL_PACKAGE_ASSIGNMENT',
      oldStatus: 'NONE_OR_EXPIRED',
      newStatus: 'ACTIVE',
      startDate: params.startDate,
      endDate: params.endDate,
      reason: params.reason || 'Super Admin yetkili paket ataması',
    });

    return {
      success: true,
      message: `${params.product} paketi başarıyla tanımlandı ve aktifleştirildi.`,
    };
  }

  // ==========================================
  // /external_licenses (External Hosting Licenses)
  // ==========================================
  public generateLicenseKey(): string {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    const segment = (len = 4) => {
      let res = '';
      for (let i = 0; i < len; i++) {
        res += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      return res;
    };
    return `ADIO-${segment(4)}-${segment(4)}-${segment(4)}-${segment(4)}`;
  }

  public async createExternalLicenseRequest(params: {
    tenantId: string;
    websiteId: string;
    domain: string;
    siteName?: string;
    orderId?: string;
    paymentStatus?: 'PAID' | 'UNPAID';
    priceTRY?: number;
    customerName?: string;
    customerEmail?: string;
    customerPhone?: string;
  }): Promise<ExternalLicense> {
    const cleanDomain = params.domain.toLowerCase().trim().replace(/^https?:\/\//, '').split('/')[0].split(':')[0];
    const licenseId = `lic_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
    const now = new Date().toISOString();

    const license: ExternalLicense = {
      id: licenseId,
      tenantId: params.tenantId,
      websiteId: params.websiteId,
      domain: cleanDomain,
      status: 'PENDING_APPROVAL',
      requestedAt: now,
      siteName: params.siteName || '',
      orderId: params.orderId,
      paymentStatus: params.paymentStatus || 'UNPAID',
      priceTRY: params.priceTRY,
      customerName: params.customerName,
      customerEmail: params.customerEmail,
      customerPhone: params.customerPhone,
    };

    if (this.isRtdbAvailable() && database) {
      try {
        await set(ref(database, `external_licenses/${licenseId}`), this.cleanForRtdb(license));
      } catch (err) {
        console.warn('RTDB createExternalLicenseRequest fallback:', err);
      }
    }

    const all = this.getLocal<Record<string, ExternalLicense>>('external_licenses', {});
    all[licenseId] = license;
    this.setLocal('external_licenses', all);

    return license;
  }

  public async recordExternalLicensePayment(params: {
    tenantId: string;
    websiteId?: string;
    domain: string;
    siteName?: string;
    orderId: string;
    priceTRY: number;
    currency?: string;
    customerName?: string;
    customerEmail?: string;
    customerPhone?: string;
  }): Promise<ExternalLicense> {
    const all = await this.getAllExternalLicenses();
    const cleanDomain = params.domain.toLowerCase().trim().replace(/^https?:\/\//, '').split('/')[0].split(':')[0];
    const existing = all.find(
      (l) =>
        (l.orderId && l.orderId === params.orderId) ||
        (cleanDomain && l.domain && l.domain.toLowerCase().trim() === cleanDomain && l.tenantId === params.tenantId)
    );

    if (existing) {
      existing.paymentStatus = 'PAID';
      existing.priceTRY = params.priceTRY;
      existing.orderId = params.orderId;
      if (params.customerName) existing.customerName = params.customerName;
      if (params.customerEmail) existing.customerEmail = params.customerEmail;
      if (params.customerPhone) existing.customerPhone = params.customerPhone;
      await this.saveExternalLicense(existing);
      return existing;
    }

    return this.createExternalLicenseRequest({
      tenantId: params.tenantId,
      websiteId: params.websiteId || 'external',
      domain: cleanDomain,
      siteName: params.siteName || cleanDomain,
      orderId: params.orderId,
      paymentStatus: 'PAID',
      priceTRY: params.priceTRY,
      customerName: params.customerName,
      customerEmail: params.customerEmail,
      customerPhone: params.customerPhone,
    });
  }

  public async saveExternalLicense(license: ExternalLicense): Promise<void> {
    if (this.isRtdbAvailable() && database) {
      try {
        await set(ref(database, `external_licenses/${license.id}`), this.cleanForRtdb(license));
      } catch (err) {
        console.warn('RTDB saveExternalLicense fallback:', err);
      }
    }

    const all = this.getLocal<Record<string, ExternalLicense>>('external_licenses', {});
    all[license.id] = license;
    this.setLocal('external_licenses', all);
  }

  public async getExternalLicensesByTenant(tenantId: string): Promise<ExternalLicense[]> {
    if (this.isRtdbAvailable() && database) {
      try {
        const snap = await get(ref(database, 'external_licenses'));
        if (snap.exists()) {
          const val = snap.val() as Record<string, ExternalLicense>;
          return Object.values(val)
            .filter((l) => l && l.tenantId === tenantId)
            .sort((a, b) => new Date(b.requestedAt).getTime() - new Date(a.requestedAt).getTime());
        }
      } catch (err) {
        console.warn('RTDB getExternalLicensesByTenant fallback:', err);
      }
    }

    const all = this.getLocal<Record<string, ExternalLicense>>('external_licenses', {});
    return Object.values(all)
      .filter((l) => l && l.tenantId === tenantId)
      .sort((a, b) => new Date(b.requestedAt).getTime() - new Date(a.requestedAt).getTime());
  }

  public async getAllExternalLicenses(): Promise<ExternalLicense[]> {
    if (this.isRtdbAvailable() && database) {
      try {
        const snap = await get(ref(database, 'external_licenses'));
        if (snap.exists()) {
          const val = snap.val() as Record<string, ExternalLicense>;
          return Object.values(val)
            .filter(Boolean)
            .sort((a, b) => new Date(b.requestedAt).getTime() - new Date(a.requestedAt).getTime());
        }
      } catch (err) {
        console.warn('RTDB getAllExternalLicenses fallback:', err);
      }
    }

    const all = this.getLocal<Record<string, ExternalLicense>>('external_licenses', {});
    return Object.values(all)
      .filter(Boolean)
      .sort((a, b) => new Date(b.requestedAt).getTime() - new Date(a.requestedAt).getTime());
  }

  public async getExternalLicenseById(id: string): Promise<ExternalLicense | null> {
    if (this.isRtdbAvailable() && database) {
      try {
        const snap = await get(ref(database, `external_licenses/${id}`));
        if (snap.exists()) {
          return snap.val() as ExternalLicense;
        }
      } catch (err) {
        console.warn('RTDB getExternalLicenseById fallback:', err);
      }
    }

    const all = this.getLocal<Record<string, ExternalLicense>>('external_licenses', {});
    return all[id] || null;
  }

  public async approveExternalLicense(id: string, adminNotes?: string): Promise<ExternalLicense> {
    const existing = await this.getExternalLicenseById(id);
    if (!existing) {
      throw new Error('Lisans talebi bulunamadı.');
    }

    const licenseKey = existing.licenseKey || this.generateLicenseKey();
    const approvedAt = new Date().toISOString();
    const startDate = Date.now();
    const endDate = startDate + 365 * 24 * 60 * 60 * 1000; // 365 gün (1 Yıl)

    const updated: ExternalLicense = {
      ...existing,
      licenseKey,
      status: 'ACTIVE',
      approvedAt,
      startDate,
      endDate,
      adminNotes: adminNotes !== undefined ? adminNotes : existing.adminNotes,
    };

    if (this.isRtdbAvailable() && database) {
      try {
        await update(ref(database, `external_licenses/${id}`), this.cleanForRtdb({
          licenseKey,
          status: 'ACTIVE',
          approvedAt,
          startDate,
          endDate,
          adminNotes: updated.adminNotes || null,
        }));
      } catch (err) {
        console.warn('RTDB approveExternalLicense fallback:', err);
      }
    }

    const all = this.getLocal<Record<string, ExternalLicense>>('external_licenses', {});
    all[id] = updated;
    this.setLocal('external_licenses', all);

    return updated;
  }

  public async updateExternalLicenseStatus(
    id: string,
    status: ExternalLicenseStatus,
    adminNotes?: string
  ): Promise<ExternalLicense> {
    const existing = await this.getExternalLicenseById(id);
    if (!existing) {
      throw new Error('Lisans kaydı bulunamadı.');
    }

    const now = new Date().toISOString();
    const nowMs = Date.now();
    const updates: Partial<ExternalLicense> = {
      status,
      adminNotes: adminNotes !== undefined ? adminNotes : existing.adminNotes,
    };

    if (status === 'SUSPENDED') updates.suspendedAt = now;
    if (status === 'REVOKED') updates.revokedAt = now;
    if (status === 'EXPIRED') updates.expiredAt = now;
    if (status === 'ACTIVE') {
      if (!existing.licenseKey) {
        updates.licenseKey = this.generateLicenseKey();
        updates.approvedAt = now;
      }
      if (!existing.startDate) {
        updates.startDate = nowMs;
        updates.endDate = nowMs + 365 * 24 * 60 * 60 * 1000;
      }
    }

    const updated: ExternalLicense = {
      ...existing,
      ...updates,
    };

    if (this.isRtdbAvailable() && database) {
      try {
        await update(ref(database, `external_licenses/${id}`), this.cleanForRtdb(updates as any));
      } catch (err) {
        console.warn('RTDB updateExternalLicenseStatus fallback:', err);
      }
    }

    const all = this.getLocal<Record<string, ExternalLicense>>('external_licenses', {});
    all[id] = updated;
    this.setLocal('external_licenses', all);

    return updated;
  }

  public async validateExternalLicense(
    domain: string,
    licenseKey: string,
    clientIp?: string
  ): Promise<{ valid: boolean; status: ExternalLicenseStatus | 'INVALID'; license?: ExternalLicense; message: string }> {
    const cleanDomain = domain.toLowerCase().trim().replace(/^https?:\/\//, '').split('/')[0].split(':')[0];
    const rootDomain = cleanDomain.replace(/^www\./, '');
    const cleanKey = (licenseKey || '').trim().toUpperCase();

    const allLicenses = await this.getAllExternalLicenses();
    const found = allLicenses.find((l) => {
      if (!l || !l.licenseKey) return false;
      const lKey = l.licenseKey.trim().toUpperCase();
      if (lKey !== cleanKey) return false;
      const lDomain = l.domain.toLowerCase().trim().replace(/^https?:\/\//, '').split('/')[0].split(':')[0];
      const lRoot = lDomain.replace(/^www\./, '');
      return lDomain === cleanDomain || lRoot === rootDomain;
    });

    if (!found) {
      return {
        valid: false,
        status: 'INVALID',
        message: 'Lisans anahtarı veya alan adı eşleşmedi.',
      };
    }

    const nowMs = Date.now();
    const nowIso = new Date().toISOString();

    // Check expiration if endDate is defined and passed
    if (found.endDate && nowMs > found.endDate) {
      if (found.status !== 'EXPIRED') {
        found.status = 'EXPIRED';
        found.expiredAt = nowIso;
        if (this.isRtdbAvailable() && database) {
          try {
            await update(ref(database, `external_licenses/${found.id}`), this.cleanForRtdb({
              status: 'EXPIRED',
              expiredAt: nowIso,
            }));
          } catch (e) {
            console.warn('RTDB expired update warning:', e);
          }
        }
        const all = this.getLocal<Record<string, ExternalLicense>>('external_licenses', {});
        if (all[found.id]) {
          all[found.id].status = 'EXPIRED';
          all[found.id].expiredAt = nowIso;
          this.setLocal('external_licenses', all);
        }
      }

      return {
        valid: false,
        status: 'EXPIRED',
        license: found,
        message: 'Lisans süreniz dolmuştur.',
      };
    }

    if (found.status === 'EXPIRED') {
      return {
        valid: false,
        status: 'EXPIRED',
        license: found,
        message: 'Lisans süreniz dolmuştur.',
      };
    }

    // Ping update
    if (found.status === 'ACTIVE') {
      try {
        if (this.isRtdbAvailable() && database) {
          await update(ref(database, `external_licenses/${found.id}`), this.cleanForRtdb({
            lastPingAt: nowIso,
            clientIp: clientIp || null,
          }));
        }
        found.lastPingAt = nowIso;
        found.clientIp = clientIp || found.clientIp;
        const all = this.getLocal<Record<string, ExternalLicense>>('external_licenses', {});
        if (all[found.id]) {
          all[found.id].lastPingAt = nowIso;
          if (clientIp) all[found.id].clientIp = clientIp;
          this.setLocal('external_licenses', all);
        }
      } catch (pingErr) {
        console.warn('Error updating license ping:', pingErr);
      }

      return {
        valid: true,
        status: 'ACTIVE',
        license: found,
        message: 'Lisans geçerli ve aktif.',
      };
    }

    return {
      valid: false,
      status: found.status,
      license: found,
      message:
        found.status === 'SUSPENDED'
          ? 'Lisans askıya alınmıştır.'
          : found.status === 'REVOKED'
          ? 'Lisans iptal edilmiştir.'
          : 'Lisans onay beklemektedir.',
    };
  }

  public subscribeToExternalLicenses(callback: (licenses: ExternalLicense[]) => void): () => void {
    if (!this.isRtdbAvailable() || !database) {
      const all = this.getLocal<Record<string, ExternalLicense>>('external_licenses', {});
      callback(Object.values(all));
      return () => {};
    }

    const licensesRef = ref(database, 'external_licenses');
    const handleSnapshot = (snap: any) => {
      if (snap.exists()) {
        const val = snap.val() as Record<string, ExternalLicense>;
        callback(
          Object.values(val)
            .filter(Boolean)
            .sort((a, b) => new Date(b.requestedAt).getTime() - new Date(a.requestedAt).getTime())
        );
      } else {
        callback([]);
      }
    };

    onValue(licensesRef, handleSnapshot);
    return () => off(licensesRef, 'value', handleSnapshot);
  }
}

export const realtimeDbService = new RealtimeDatabaseService();
export default realtimeDbService;
